import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useParams } from "react-router-dom";
import { Form, Modal, Button, Tooltip, OverlayTrigger, Tabs, Tab, Alert } from "react-bootstrap";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle, faArrowLeft, faArrowDown, faArrowUp, faTrash, faComments, faPlus, faEdit, faUpload, faWandMagicSparkles, faUtensils, faListCheck, faDollarSign, faChartPie, faClock, faTag, faImage, faNoteSticky, faLock, faHeading, faChevronDown, faGripVertical, faFileText } from "@fortawesome/free-solid-svg-icons";
import QuickUnitCreator from "./QuickUnitCreator";
import { useNavigate } from "react-router-dom";
import "../styles/style.scss";
import styles from "../styles/EditRecipe.module.css";
import importStyles from "../styles/ImportIngredients.module.css";
import Fraction from "fraction.js";
import FullChatBot from "./FullChatBot";
import Tiptap from "./Tiptap";
import "../styles/Tiptap.css";
import TagInput from "./TagInput";

// Portal component to render dropdown menu at document root
const DropdownPortal = ({ children, targetRef, isOpen }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const portalRef = useRef(null);

  useEffect(() => {
    if (isOpen && targetRef.current) {
      const rect = targetRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      });

      // Store portal ref in target for click-outside detection
      if (targetRef.current) {
        targetRef.current._portalRef = portalRef.current;
      }
    }
  }, [isOpen, targetRef]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      ref={portalRef}
      style={{
        position: 'absolute',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 9999
      }}
    >
      {children}
    </div>,
    document.body
  );
};

function EditRecipe() {
  //Chatbot
  const [closeChat, setCloseChat] = useState(false);
  const [inverseConversion, setInverseConversion] = useState(false);

  const handleToggleConversion = () => {
    setInverseConversion(!inverseConversion);
  };

  function toggleCloseChat() {
    if (!hasSilver) {
      alert("A Silver subscription is required for this feature");
      return;
    }
    if (closeChat) {
      const confirmed = window.confirm(
        `Are you sure you want to close you chat with Remy? Your conversation will be erased!`
      );
      if (!confirmed) return;
    }
    setCloseChat(!closeChat);
  }

  const toggleClearChat = async () => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the chat history? You cannot undo?`
    );

    if (!confirmed) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/delete-chat-history`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id }), // Send recipeId in the request body
        }
      );

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      setCloseChat(!closeChat);
    } catch (error) {
      console.error(error);
    }
  };
  //
  const navigate = useNavigate();
  const { id } = useParams();
  const [isMetric, setIsMetric] = useState(false);
  const [hasGold, setHasGold] = useState(false);
  const [hasSilver, setHasSilver] = useState(false);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/gold`, {
      method: "GET",
      credentials: "include",
    })
      .then((response) => {
        if (response.ok) {
          setHasGold(true);
          setHasSilver(true);
          console.log("Gold");
        } else if (response.status === 403) {
          console.log("Not authorized");
          setHasGold(false);
        } else {
          console.log("Error");
          setHasGold(false);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        setHasGold(false);
      });
  }, [id]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/silver`, {
      method: "GET",
      credentials: "include",
    })
      .then((response) => {
        if (response.ok) {
          setHasSilver(true);
          console.log("Silver");
        } else if (response.status === 403) {
          console.log("Not authorized");
          setHasSilver(false);
        } else {
          console.log("Error");
          setHasSilver(false);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
        setHasSilver(false);
      });
  }, [id]);

  const [isLoggedIn, setIsLoggedIn] = useState(true);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/isLoggedIn`, {
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.isLoggedIn) {
          setIsLoggedIn(true);
        } else {
          throw new Error("User is not logged in");
        }
      })
      .catch((error) => {
        console.error("Error checking if user is logged in:", error);
        setIsLoggedIn(false);
      });
  }, [id]);

  //Macro
  let [totalProtein, setTotalProtein] = useState(0);
  let [totalCarbs, setTotalCarbs] = useState(0);
  let [totalFat, setTotalFat] = useState(0);
  let [totalFiber, setTotalFiber] = useState(0);
  let [totalSugar, setTotalSugar] = useState(0);
  let [totalCalories, setTotalCalories] = useState(0);
  let [totalPrice, setTotalPrice] = useState(0);

  //Editing Ingredient
  const [selectedRecipeIngredient, setSelectedRecipeIngredient] =
    useState(null);
  const [editRecipeIngredientName, setEditRecipeIngredientName] = useState("");
  const [editIngredientQty, setEditIngredientQty] = useState("");
  const [editIngredientCalorie, setEditIngredientCalorie] = useState("");
  const [editIngredientUnit, setEditIngredientUnit] = useState("");
  const [editIngredientSuffix, setEditIngredientSuffix] = useState("");
  const [selectedRecipeIngredientId, setSelectedRecipeIngredientId] =
    useState("");

  // Editing Instruction
  const [selectedInstruction, setSelectedInstruction] = useState(null);
  const [editInstructionStep, setEditInstructionStep] = useState("");
  const [currentUser, setCurrentUser] = useState("");
  const [userIsOwner, setUserIsOwner] = useState(false);
  // Insturctions
  const [addInstruction, setAddInstruction] = useState("");

  const [imageUrl, setImageUrl] = useState("");

  const fetchUserMetricSetting = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/getUser`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const { user } = await response.json();

      if (!user) {
        // Handle error
      }
      setCurrentUser(user.id);
      setIsMetric(user.default_unit_of_measurement === "metric");
    } catch (error) {
      // Handle error
    }
  };

  const handleToggle = () => {
    setIsMetric(!isMetric);

    // Make a POST request to update user's default_unit_of_measurement value
    fetch(`${process.env.REACT_APP_API_URL}/api/updateUser`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        default_unit_of_measurement: isMetric ? "traditional" : "metric",
      }),
    }).then((response) => {
      fetchRecipeIngredients(id);
      if (!response.ok) {
        // Handle error
      }
    });
  };

  const [recipeData, setRecipeData] = useState(null);
  const [recipeName, setRecipeName] = useState("");
  const [ingredients, setIngredients] = useState([]);
  const [newSelectedIngredient, setNewSelectedIngredient] = useState("");

  const [
    editIngredientConversionsOptions,
    setEditIngredientConversionsOptions,
  ] = useState([]);

  const handleEditIngredient = (ingredient) => {
    setSelectedRecipeIngredient(ingredient);
    if (ingredient.is_section_header) {
      // For section headers, we only need to set the name
      setEditRecipeIngredientName(ingredient.section_name);
    } else if (ingredient.is_text_row) {
      // For text rows, we only need to set the text content
      setEditRecipeIngredientName(ingredient.section_name);
    } else {
      setSelectedRecipeIngredientId(ingredient.id);
      setEditRecipeIngredientName(ingredient.name);
      setEditIngredientQty(ingredient.qty);
      setEditIngredientCalorie(ingredient.ingredient_calorie_percent);
      setEditIngredientUnit(ingredient.unit_name);
      setEditIngredientSuffix(ingredient.suffix);
      const option = {
        value: ingredient.old_id,
        label: ingredient.name,
      };
      handleEditChange(option);
    }
  };

  const handleEditInstruction = (instruction) => {
    setSelectedInstruction(instruction);
    if (instruction.is_section_header) {
      setEditInstructionStep(instruction.section_name);
    } else {
      setEditInstructionStep(instruction.instruction);
    }
  };

  // NEW
  const [newIngredientConversionsOptions, setNewIngredientConversionsOptions] =
    useState([]);
  const [
    newIngredientConversionsOptionsId,
    setNewIngredientConversionsOptionsId,
  ] = useState("");
  const [
    newIngredientConversionsOptionsName,
    setNewIngredientConversionsOptionsName,
  ] = useState("");
  const [newIngredientConversionFactor, setNewIngredientConversionFactor] =
    useState("");

  //Edit

  const [
    editIngredientConversionsOptionsId,
    setEditIngredientConversionsOptionsId,
  ] = useState("");
  const [
    editIngredientConversionsOptionsName,
    setEditIngredientConversionsOptionsName,
  ] = useState("");
  const [editIngredientConversionFactor, setEditIngredientConversionFactor] =
    useState("");

  const [newQty, setNewQty] = useState("");
  const [newSuffix, setNewSuffix] = useState("");
  const [newCalorie, setNewCalorie] = useState(100);

  const courseOptions = [
    { value: "Breakfast", label: "Breakfast" },
    { value: "Lunch", label: "Lunch" },
    { value: "Entree", label: "Entree" },
    { value: "Appetizer", label: "Appetizer" },
    { value: "Snack", label: "Snack" },
    { value: "Dessert", label: "Dessert" },
    { value: "Side", label: "Side" },
    { value: "Dips and Sauce", label: "Dips and Sauce" },
    { value: "Marinade", label: "Marinade" },
    { value: "Beverage", label: "Beverage" },
  ].map((option) => ({ value: option.value, label: option.value }));

  const preloadedCourseOption = recipeData?.course
    ? { value: recipeData.course, label: recipeData.course }
    : null;

  const [recipeCourse, setRecipeCourse] = useState(
    preloadedCourseOption
      ? courseOptions.find(
          (option) => option.value === preloadedCourseOption.value
        )
      : null
  );

  const [recipeServings, setRecipeServings] = useState("");
  const [recipeServingSize, setRecipeServingSize] = useState("");
  const [recipeTags, setRecipeTags] = useState("");
  const [recipeDescription, setRecipeDescription] = useState("");
  const [recipeIngredients, setRecipeIngredients] = useState([]);
  const [recipeInstructions, setRecipeInstructions] = useState([]);
  const [originalIngredientOrder, setOriginalIngredientOrder] = useState([]);
  const [originalInstructionOrder, setOriginalInstructionOrder] = useState([]);
  const [hasUnsavedIngredientOrder, setHasUnsavedIngredientOrder] = useState(false);
  const [hasUnsavedInstructionOrder, setHasUnsavedInstructionOrder] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [openPrepDropdown, setOpenPrepDropdown] = useState(null);
  const dropdownRefs = useRef({});
  const prepDropdownRefs = useRef({});
  
  // Drag and drop state
  const [draggedIngredient, setDraggedIngredient] = useState(null);
  const [draggedIngredientIndex, setDraggedIngredientIndex] = useState(null);
  const [dragOverIngredientIndex, setDragOverIngredientIndex] = useState(null);
  const [draggedInstruction, setDraggedInstruction] = useState(null);
  const [draggedInstructionIndex, setDraggedInstructionIndex] = useState(null);
  const [dragOverInstructionIndex, setDragOverInstructionIndex] = useState(null);

  const fetchRecipeData = (id) => {
    fetch(`${process.env.REACT_APP_API_URL}/recipes/user-check/${id}`, {
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        setRecipeData(data);
        setRecipeName(data.name ? data.name : "");
        setRecipeCourse(
          data.course
            ? courseOptions.find((option) => option.value === data.course)
            : null
        );
        setRecipeServings(data.servings ? data.servings : 0);
        setRecipeServingSize(data.serving_size ? data.serving_size : 0);
        setRecipeTags(data.tags ? data.tags : "");
        setRecipeDescription(data.description ? data.description : "");
        setImageUrl(`${data.image_url}?updated=${Date.now()}`);
        setPublicNotesContent(data.public_notes);
      })
      .catch((error) => console.error("Error retrieving recipe data:", error));
  };

  // Call fetchRecipeData with the recipe ID to fetch the data
  useEffect(() => {
    fetchRecipeData(id);
  }, [id]);

  // Handle beforeunload event for unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedIngredientOrder || hasUnsavedInstructionOrder) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedIngredientOrder, hasUnsavedInstructionOrder]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on a dropdown menu item
      if (event.target.closest('.dropdown-menu')) {
        return;
      }

      // Check if click is outside unit dropdowns
      const isClickOutsideDropdowns = !Object.values(dropdownRefs.current).some(
        ref => ref && ref.contains(event.target)
      );
      if (isClickOutsideDropdowns && openDropdown !== null) {
        setOpenDropdown(null);
      }

      // Check if click is outside prep dropdowns (including portal)
      const isClickOutsidePrepDropdowns = !Object.values(prepDropdownRefs.current).some(
        ref => {
          if (!ref) return false;
          // Check if click is inside the button
          if (ref.contains(event.target)) return true;
          // Check if click is inside the portal dropdown
          if (ref._portalRef && ref._portalRef.contains(event.target)) return true;
          return false;
        }
      );
      if (isClickOutsidePrepDropdowns && openPrepDropdown !== null) {
        console.log('Closing prep dropdown due to outside click');
        setOpenPrepDropdown(null);
      }
    };

    // Use 'click' instead of 'mousedown' to avoid conflicts with button clicks
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openDropdown, openPrepDropdown]);

  const fetchPrivateNotes = (id) => {
    fetch(
      `${process.env.REACT_APP_API_URL}/api/get_private_recipe_notes/${id}`,
      {
        credentials: "include",
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data && data[0] && data[0].private_note !== undefined) {
          setNotesContent(data[0].private_note);
        } else {
          // Set notesContent to an empty string if private_note is undefined
          setNotesContent(null);
        }
      })
      .catch((error) => console.error("Error retrieving recipe data:", error));
  };

  // Call fetchRecipeData with the recipe ID to fetch the data
  useEffect(() => {
    fetchPrivateNotes(id);
  }, [recipeData]);

  const handleSetUnitOptionDefault = (ingredientRowId) => {
    console.log(ingredientRowId);

    fetch(`${process.env.REACT_APP_API_URL}/api/setunit`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ingredientRowId: ingredientRowId,
        conversionId: 0,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log("Success");
        fetchRecipeIngredients(id);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleSetUnitOption = (ingredientRowId, conversionId) => {
    console.log(ingredientRowId);
    console.log(conversionId);

    fetch(`${process.env.REACT_APP_API_URL}/api/setunit`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ingredientRowId: ingredientRowId,
        conversionId: conversionId,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        console.log("Success");
        fetchRecipeIngredients(id);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  //Handle Form Changes
  const handleRecipeNameChange = (event) => {
    setRecipeName(event.target.value);
  };

  //Handle Form Changes
  const handleRecipeCourseChange = (selectedOption) => {
    setRecipeCourse(selectedOption);
  };

  //Handle Form Changes
  const handleRecipeServingsChange = (event) => {
    if (!isNaN(event)) {
      setRecipeServings(event);
    } else {
      setRecipeServings("");
    }
  };

  //Handle Form Changes
  const handleRecipeServingSizeChange = (event) => {
    setRecipeServingSize(event.target.value);
  };


  //Handle Form Changes
  const handleRecipeDescriptionChange = (event) => {
    setRecipeDescription(event.target.value);
  };

  // Update Recipe
  const handleUpdateRecipe = () => {
    const updatedRecipe = {
      name: recipeName,
      course: recipeCourse.value,
      servings: recipeServings,
      serving_size: recipeServingSize,
      tags: recipeTags,
      description: recipeDescription,
    };

    fetch(`${process.env.REACT_APP_API_URL}/api/recipes/${id}`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updatedRecipe),
    })
      .then((response) => {
        if (response.ok) {
          console.log("Recipe updated successfully.");
          fetchRecipeData(id); // Call the function here
        } else {
          throw new Error("Failed to update recipe");
        }
      })
      .catch((error) => console.error(error));
  };

  // New Qty:
  const handleNewQtyChange = (newQtyValue) => {
    if (isNaN(newQtyValue)) {
      setNewQty("");
    } else {
      setNewQty(newQtyValue);
    }
  };

  // Edit Qty:
  const handleEditQtyChange = (editIngredientQty) => {
    if (isNaN(editIngredientQty)) {
      setEditIngredientQty("");
    } else {
      setEditIngredientQty(editIngredientQty);
    }
  };

  // New Calorie:
  const handleNewCalorieChange = (newCalorieValue) => {
    if (isNaN(newCalorieValue)) {
      setNewCalorie("");
    } else {
      setNewCalorie(newCalorieValue);
    }
  };

  // Edit Calorie:
  const handleEditCalorieChange = (editIngredientCalorie) => {
    if (isNaN(editIngredientCalorie)) {
      setEditIngredientCalorie("");
    } else {
      setEditIngredientCalorie(editIngredientCalorie);
    }
  };

  // New Suffix:
  const handleNewSuffixChange = (event) => {
    setNewSuffix(event.target.value);
  };

  // Edit Suffix
  const handleEditSuffixChange = (event) => {
    setEditIngredientSuffix(event.target.value);
  };

  // Fetch Ingredients for Chart
  const fetchIngredients = () => {
    fetch(`${process.env.REACT_APP_API_URL}/ingredients`, {
      credentials: "include",
    })
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401) {
            console.log("User not authenticated");
            setIngredients([]);
            return;
          }
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (data) {
          // Map data to an array of options
          const options = data.map((ingredient) => ({
            label: ingredient.ingredient_name,
            value: ingredient.id,
          }));
          setIngredients(options);
        }
      })
      .catch((err) => {
        console.error("Error retrieving user ingredients:", err);
        setIngredients([]);
      });
  };

  const handleEditChange = (selectedOption) => {
    // Determine custom_ingredient flag
    console.log(selectedOption);
    setSelectedRecipeIngredientId(selectedOption.value);
    setEditRecipeIngredientName(selectedOption.label);
    setNewSelectedIngredient(selectedOption.value);
    const customIngredientFlag = selectedOption.value.includes("user") ? 1 : 0;
    const ingredientId = selectedOption.value.split(",")[0];

    // Make API call to retrieve unit conversions
    fetch(
      `${process.env.REACT_APP_API_URL}/api/ingredient-all-units/${ingredientId}?custom_ingredient=${customIngredientFlag}`,
      {
        credentials: "include",
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.statusText);
        }
        return response.json();
      })
      .then((data) => {
        setEditIngredientConversionsOptions(data);
      })
      .catch((error) => {
        console.error("Error retrieving ingredient conversions:", error);
        setEditIngredientConversionsOptions([]);
      });

    console.log(`Option selected:`, selectedOption);
  };

  //Handle Form Changes
  const handleInstructionChange = (event) => {
    setAddInstruction(event.target.value);
  };

  //Handle Form Changes
  const handleEditInstructionChange = (event) => {
    setEditInstructionStep(event.target.value);
  };

  // Add this useEffect block after the `handleEditChange` function
  useEffect(() => {
    const selectedUnit = editIngredientConversionsOptions.find(
      (option) => option.unit_name === editIngredientUnit
    );
    if (selectedUnit) {
      setEditIngredientConversionsOptionsId(selectedUnit.unit_id);
      setEditIngredientConversionsOptionsName(selectedUnit.unit_name);
      setEditIngredientConversionFactor(selectedUnit.conversion_factor);
    }
  }, [editIngredientConversionsOptions, editIngredientUnit]);

  const handleChange = (selectedOption) => {
    // Determine custom_ingredient flag
    console.log(selectedOption.value);
    setNewSelectedIngredient(selectedOption.value);
    const customIngredientFlag = selectedOption.value.includes("user") ? 1 : 0;
    const ingredientId = selectedOption.value.split(",")[0];

    // Make API call to retrieve unit conversions
    fetch(
      `${process.env.REACT_APP_API_URL}/api/ingredient-all-units/${ingredientId}?custom_ingredient=${customIngredientFlag}`,
      {
        credentials: "include",
      }
    )
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error(response.statusText);
      })
      .then((data) => {
        setNewIngredientConversionsOptions(data);
        setNewIngredientConversionsOptionsId(data[0].unit_id);
        setNewIngredientConversionsOptionsName(data[0].unit_name);
        setNewIngredientConversionFactor(data[0].conversion_factor);
      })
      .catch((error) => {
        console.error("Error retrieving ingredient conversions:", error);
        setNewIngredientConversionsOptions([]);
      });

    console.log(`Option selected:`, selectedOption);
  };

  const fetchRecipeIngredients = (id) => {
    fetch(`${process.env.REACT_APP_API_URL}/api/recipe/ingredients/${id}`, {
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        console.log(data);
        setRecipeIngredients(data);
        setOriginalIngredientOrder(data.map(ing => ({ id: ing.id, sort_order: ing.sort_order })));
        setHasUnsavedIngredientOrder(false);
        const totalProtein = data.reduce(
          (accumulator, currentValue) =>
            accumulator + parseFloat(currentValue.protein),
          0
        );
        const totalCarbs = data.reduce(
          (accumulator, currentValue) =>
            accumulator + parseFloat(currentValue.carbs),
          0
        );
        const totalFat = data.reduce(
          (accumulator, currentValue) =>
            accumulator + parseFloat(currentValue.fat),
          0
        );
        const totalFiber = data.reduce(
          (accumulator, currentValue) =>
            accumulator + parseFloat(currentValue.fiber),
          0
        );

        const totalSugar = data.reduce(
          (accumulator, currentValue) =>
            accumulator + parseFloat(currentValue.sugar),
          0
        );
        const totalCalories = data.reduce(
          (accumulator, currentValue) =>
            accumulator + parseFloat(currentValue.calories),
          0
        );
        const totalPrice = data.reduce(
          (accumulator, currentValue) =>
            accumulator + parseFloat(currentValue.price),
          0
        );

        // send PUT request to update recipe with total values
        fetch(`${process.env.REACT_APP_API_URL}/api/recipe/totals/${id}`, {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            total_calories: totalCalories,
            total_price: totalPrice,
          }),
        })
          .then((response) => response.json())
          .then((data) => {
            console.log("Success");
          })
          .catch((error) => {
            console.error(error);
          });

        setTotalProtein(totalProtein);
        setTotalCarbs(totalCarbs);
        setTotalFat(totalFat);
        setTotalFiber(totalFiber);
        setTotalSugar(totalSugar);
        setTotalCalories(totalCalories);
        setTotalPrice(totalPrice);
      });
  };

  // Add Ingredient Section Header
  const handleAddIngredientSectionHeader = async () => {
    if (!newSectionName.trim()) {
      alert("Please enter a section name");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/add-ingredient-to-recipe`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          recipe_id: recipeData.id,
          ingredient_id: null,
          custom_ingredient: 0,
          qty: 0,
          suffix: "",
          is_section_header: 1,
          section_name: newSectionName.trim(),
        }),
      });

      if (response.ok) {
        fetchRecipeIngredients(id);
        setShowIngredientSectionModal(false);
        setNewSectionName("");
      } else {
        throw new Error("Error adding section header");
      }
    } catch (err) {
      console.error("Error adding section header:", err);
      alert("Error adding section header");
    }
  };

  // Add Instruction Section Header
  const handleAddInstructionSectionHeader = async () => {
    if (!newSectionName.trim()) {
      alert("Please enter a section name");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/add-instruction-to-recipe`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          recipe_id: recipeData.id,
          instructionStep: "",
          is_section_header: 1,
          section_name: newSectionName.trim(),
        }),
      });

      if (response.ok) {
        fetchRecipeInstructions(id);
        setShowInstructionSectionModal(false);
        setNewSectionName("");
      } else {
        throw new Error("Error adding section header");
      }
    } catch (err) {
      console.error("Error adding section header:", err);
      alert("Error adding section header");
    }
  };

  // Add Text Row to Ingredients
  const handleAddTextRow = async () => {
    if (!newTextRowContent.trim()) {
      alert("Please enter text content");
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/add-ingredient-to-recipe`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          recipe_id: recipeData.id,
          ingredient_id: null,
          custom_ingredient: 0,
          qty: null,
          suffix: "",
          is_section_header: 0,
          is_text_row: 1,
          section_name: newTextRowContent.trim(),
        }),
      });

      if (response.ok) {
        fetchRecipeIngredients(id);
        setShowTextRowModal(false);
        setNewTextRowContent("");
      } else {
        throw new Error("Error adding text row");
      }
    } catch (err) {
      console.error("Error adding text row:", err);
      alert("Error adding text row");
    }
  };

  //Add New Ingredient to Recipe
  const handleAddIngredientToRecipe = () => {
    // Split ingredientId to get the ID
    const ingredientId = newSelectedIngredient.split(",")[0];

    // Determine if ingredient is custom
    const customIngredientFlag = newSelectedIngredient.includes("user") ? 1 : 0;

    // Send POST request to add new ingredient to recipe
    fetch(`${process.env.REACT_APP_API_URL}/api/add-ingredient-to-recipe`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        recipe_id: recipeData.id,
        ingredient_id: ingredientId,
        custom_ingredient: customIngredientFlag,
        qty: newQty * newIngredientConversionFactor,
        suffix: newSuffix,
        old_id: newSelectedIngredient,
        calorie_percent: newCalorie != null ? newCalorie : 100,
        needs_prep: null, // Use ingredient's default prep flag
      }),
    })
      .then((res) => {
        if (res.ok) {
          // Reload recipe data
          fetchRecipeData(id);
          // setNewSelectedIngredient("");
          setNewQty(0);
          setNewSuffix("");
          setNewCalorie(100);
          setNewIngredientConversionFactor(1);
          fetchRecipeIngredients(id);
        } else {
          throw new Error("Error adding new ingredient to recipe");
        }
      })
      .catch((err) => {
        console.error("Error adding new ingredient to recipe:", err);
      });
  };

  function updateIngredientColor(ingredientId, color) {
    const url = `${process.env.REACT_APP_API_URL}/api/v1/ingredients/${ingredientId}`;
    const options = {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ color }),
    };

    fetch(url, options)
      .then((response) => {
        fetchRecipeIngredients(id);
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        console.log("Success ");

        // handle response as needed
      })
      .catch((error) => {
        console.error("API error: ", error);
        // handle error as needed
      });
  }
  ///
  ///
  function updateInstructionColor(instructionId, color) {
    const url = `${process.env.REACT_APP_API_URL}/api/v1/instruction/${instructionId}`;
    const options = {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ color }),
    };

    fetch(url, options)
      .then((response) => {
        fetchRecipeInstructions(id);
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        console.log("Success");

        // handle response as needed
      })
      .catch((error) => {
        console.error("API error: ", error);
        // handle error as needed
      });
  }
  ///
  ///
  ///
  // Update Section Header
  const handleUpdateSectionHeader = () => {
    const ingredient = selectedRecipeIngredient;
    
    fetch(`${process.env.REACT_APP_API_URL}/api/update-ingredient-to-recipe`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        row_id: ingredient.id,
        ingredient_id: null,
        custom_ingredient: 0,
        qty: 0,
        suffix: "",
        calorie_percent: 100,
        is_section_header: 1,
        section_name: editRecipeIngredientName,
      }),
    })
      .then((res) => {
        if (res.ok) {
          setSelectedRecipeIngredient(null);
          fetchRecipeIngredients(id);
        } else {
          throw new Error("Error updating section header");
        }
      })
      .catch((err) => {
        console.error("Error updating section header:", err);
      });
  };

  // Update Text Row
  const handleUpdateTextRow = () => {
    const ingredient = selectedRecipeIngredient;
    
    fetch(`${process.env.REACT_APP_API_URL}/api/update-ingredient-to-recipe`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        row_id: ingredient.id,
        ingredient_id: null,
        custom_ingredient: 0,
        qty: null,
        suffix: "",
        calorie_percent: 100,
        is_section_header: 0,
        is_text_row: 1,
        section_name: editRecipeIngredientName,
      }),
    })
      .then((res) => {
        fetchRecipeIngredients(id);
        setSelectedRecipeIngredient(null);
        if (!res.ok) {
          throw new Error("Network response was not ok");
        }
        return res.json();
      })
      .then((data) => {
        console.log("Success");
      })
      .catch((error) => {
        console.error("API error: ", error);
      });
  };

  const handleUpdateRecipeIngredient = () => {
    const ingredientId = selectedRecipeIngredientId.split(",")[0];

    // Determine if ingredient is custom
    const customIngredientFlag = selectedRecipeIngredientId.includes("user")
      ? 1
      : 0;

    // Send POST request to add new ingredient to recipe
    fetch(`${process.env.REACT_APP_API_URL}/api/update-ingredient-to-recipe`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        row_id: selectedRecipeIngredient.id,
        ingredient_id: ingredientId,
        custom_ingredient: customIngredientFlag,
        qty: editIngredientQty * editIngredientConversionFactor,
        suffix: editIngredientSuffix,
        old_id: newSelectedIngredient,
        calorie_percent:
          editIngredientCalorie != null ? editIngredientCalorie : 100,
        needs_prep: selectedRecipeIngredient.needs_prep, // Preserve the existing needs_prep value
        prep_type: selectedRecipeIngredient.prep_type, // Preserve the existing prep_type value
      }),
    })
      .then((res) => {
        if (res.ok) {
          // Reload recipe data
          fetchRecipeData(id);
          setSelectedRecipeIngredient(null);
          setEditIngredientQty(0);
          setEditIngredientCalorie(100);
          setEditIngredientSuffix("");
          setEditIngredientConversionFactor(1);
          fetchRecipeIngredients(id);
        } else {
          throw new Error("Error adding new ingredient to recipe");
        }
      })
      .catch((err) => {
        console.error("Error adding new ingredient to recipe:", err);
      });
  };

  // Handle updating text row content inline
  const handleUpdateTextRowInline = (ingredientId, newText) => {
    setRecipeIngredients(prevIngredients =>
      prevIngredients.map(ing =>
        ing.id === ingredientId ? { ...ing, section_name: newText } : ing
      )
    );
  };

  // Save text row changes to backend
  const saveTextRowChanges = async (ingredient) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/update-ingredient-to-recipe`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          row_id: ingredient.id,
          ingredient_id: null,
          custom_ingredient: 0,
          qty: null,
          suffix: "",
          is_section_header: 0,
          is_text_row: 1,
          section_name: ingredient.section_name,
        }),
      });

      if (!response.ok) {
        throw new Error("Error updating text row");
      }
    } catch (err) {
      console.error("Error updating text row:", err);
      alert("Error updating text row");
      // Refresh to revert changes
      fetchRecipeIngredients(id);
    }
  };

  const handleDeleteIngredient = async (ingredient) => {
    const confirmed = window.confirm(
      ingredient.is_text_row 
        ? `Are you sure you want to delete this text row?`
        : `Are you sure you want to delete the ingredient "${ingredient.name}"?`
    );

    if (!confirmed) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/ingredient/delete/${ingredient.id}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      fetchRecipeIngredients(id);
    } catch (error) {
      console.error(error);
    }
  };

  function updateIngredientPrepFlag(ingredientRowId, needsPrep, prepType, ingredient) {
    const url = `${process.env.REACT_APP_API_URL}/api/v1/ingredients/${ingredientRowId}/prep`;
    console.log('updateIngredientPrepFlag called:', { ingredientRowId, needsPrep, prepType });
    
    const options = {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ 
        needs_prep: needsPrep,
        prep_type: needsPrep ? prepType : null
      }),
    };

    console.log('Sending request to:', url, 'with body:', JSON.stringify({ needs_prep: needsPrep, prep_type: needsPrep ? prepType : null }));

    fetch(url, options)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        console.log("Success updating prep flag");
        fetchRecipeIngredients(id);
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }

  const handlePrepFlagToggle = async (ingredient) => {
    // Determine the new prep value
    let newNeedsPrep;
    if (ingredient.needs_prep === null) {
      // Currently using default, toggle opposite of default
      newNeedsPrep = ingredient.default_needs_prep === 1 ? 0 : 1;
    } else {
      // Has explicit value, toggle it
      newNeedsPrep = ingredient.needs_prep === 1 ? 0 : 1;
    }

    // Use existing prep type or default
    const prepType = ingredient.prep_type || ingredient.default_prep_type || 'chop';
    updateIngredientPrepFlag(ingredient.id, newNeedsPrep, prepType, ingredient);
  };

  const handlePrepTypeChange = (ingredient, newPrepType) => {
    console.log('handlePrepTypeChange called:', ingredient.id, newPrepType);
    // Keep prep enabled when changing type
    const needsPrep = 1;
    updateIngredientPrepFlag(ingredient.id, needsPrep, newPrepType, ingredient);
  };

  const handleDeleteRecipe = async (id) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the recipe - "${recipeData.name}"? You CANNOT undo this.`
    );

    if (!confirmed) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/recipe/delete/${id}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        throw new Error(response.statusText);
      }
      window.location.href = `${process.env.REACT_APP_CLIENT_URL}/recipes`;
    } catch (error) {
      console.error(error);
    }
  };

  // handleUpArrowClick function
  function handleUpArrowClick(currentIndex, currentId, currentSortOrder) {
    const previousIndex = currentIndex - 1;
    if (previousIndex < 0) {
      return;
    }

    // Create a copy of the ingredients array
    const newIngredients = [...recipeIngredients];
    
    // Swap the items
    [newIngredients[previousIndex], newIngredients[currentIndex]] = 
    [newIngredients[currentIndex], newIngredients[previousIndex]];
    
    // Update the state
    setRecipeIngredients(newIngredients);
    setHasUnsavedIngredientOrder(true);
  }

  function handleDownArrowClick(currentIndex, currentId, currentSortOrder) {
    const nextIndex = currentIndex + 1;
    if (nextIndex >= recipeIngredients.length) {
      return;
    }

    // Create a copy of the ingredients array
    const newIngredients = [...recipeIngredients];
    
    // Swap the items
    [newIngredients[currentIndex], newIngredients[nextIndex]] = 
    [newIngredients[nextIndex], newIngredients[currentIndex]];
    
    // Update the state
    setRecipeIngredients(newIngredients);
    setHasUnsavedIngredientOrder(true);
  }

  // Ingredient drag handlers
  const handleIngredientDragStart = (e, ingredient, index) => {
    setDraggedIngredient(ingredient);
    setDraggedIngredientIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleIngredientDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIngredientIndex(index);
  };

  const handleIngredientDragEnd = () => {
    setDraggedIngredient(null);
    setDraggedIngredientIndex(null);
    setDragOverIngredientIndex(null);
  };

  const handleIngredientDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIngredientIndex === null || draggedIngredientIndex === dropIndex) {
      return;
    }

    const newIngredients = [...recipeIngredients];
    const [movedIngredient] = newIngredients.splice(draggedIngredientIndex, 1);
    newIngredients.splice(dropIndex, 0, movedIngredient);
    
    setRecipeIngredients(newIngredients);
    setHasUnsavedIngredientOrder(true);
    setDraggedIngredient(null);
    setDraggedIngredientIndex(null);
    setDragOverIngredientIndex(null);
  };

  // Add Instruction to Recipe
  const handleAddInstructionToRecipe = () => {
    // Send POST request to add new ingredient to recipe
    fetch(`${process.env.REACT_APP_API_URL}/api/add-instruction-to-recipe`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        recipe_id: recipeData.id,
        instructionStep: addInstruction,
      }),
    })
      .then((res) => {
        if (res.ok) {
          // Reload recipe data
          fetchRecipeInstructions(id);
          fetchRecipeData(id);
          fetchRecipeIngredients(id);
          setAddInstruction("");
        } else {
          throw new Error("Error adding new ingredient to recipe");
        }
      })
      .catch((err) => {
        console.error("Error adding new ingredient to recipe:", err);
      });
  };

  // handleUpArrowClick function
  function handleInstructionUpArrowClick(
    currentIndex,
    currentId,
    currentSortOrder
  ) {
    const previousIndex = currentIndex - 1;
    if (previousIndex < 0) {
      return;
    }

    // Create a copy of the instructions array
    const newInstructions = [...recipeInstructions];
    
    // Swap the items
    [newInstructions[previousIndex], newInstructions[currentIndex]] = 
    [newInstructions[currentIndex], newInstructions[previousIndex]];
    
    // Update the state
    setRecipeInstructions(newInstructions);
    setHasUnsavedInstructionOrder(true);
  }

  function handleInstructionDownArrowClick(
    currentIndex,
    currentId,
    currentSortOrder
  ) {
    const nextIndex = currentIndex + 1;
    if (nextIndex >= recipeInstructions.length) {
      return;
    }

    // Create a copy of the instructions array
    const newInstructions = [...recipeInstructions];
    
    // Swap the items
    [newInstructions[currentIndex], newInstructions[nextIndex]] = 
    [newInstructions[nextIndex], newInstructions[currentIndex]];
    
    // Update the state
    setRecipeInstructions(newInstructions);
    setHasUnsavedInstructionOrder(true);
  }

  // Instruction drag handlers
  const handleInstructionDragStart = (e, instruction, index) => {
    setDraggedInstruction(instruction);
    setDraggedInstructionIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleInstructionDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverInstructionIndex(index);
  };

  const handleInstructionDragEnd = () => {
    setDraggedInstruction(null);
    setDraggedInstructionIndex(null);
    setDragOverInstructionIndex(null);
  };

  const handleInstructionDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedInstructionIndex === null || draggedInstructionIndex === dropIndex) {
      return;
    }

    const newInstructions = [...recipeInstructions];
    const [movedInstruction] = newInstructions.splice(draggedInstructionIndex, 1);
    newInstructions.splice(dropIndex, 0, movedInstruction);
    
    setRecipeInstructions(newInstructions);
    setHasUnsavedInstructionOrder(true);
    setDraggedInstruction(null);
    setDraggedInstructionIndex(null);
    setDragOverInstructionIndex(null);
  };

  const handleDeleteInstruction = async (instruction) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete this instruction step? You cannot undo.`
    );

    if (!confirmed) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/instruction/delete/${instruction.id}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      fetchRecipeInstructions(id);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchRecipeInstructions = (id) => {
    fetch(`${process.env.REACT_APP_API_URL}/api/recipe/instructions/${id}`, {
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => {
        setRecipeInstructions(data);
        setOriginalInstructionOrder(data.map(inst => ({ id: inst.id, sort_order: inst.sort_order })));
        setHasUnsavedInstructionOrder(false);
      });
  };

  const handleUpdateInstructionSectionHeader = () => {
    const instruction = selectedInstruction;
    
    fetch(`${process.env.REACT_APP_API_URL}/api/update-instruction-to-recipe`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        row_id: instruction.id,
        instruction: "",
        is_section_header: 1,
        section_name: editInstructionStep,
      }),
    })
      .then((res) => {
        if (res.ok) {
          setSelectedInstruction(null);
          fetchRecipeInstructions(id);
        } else {
          throw new Error("Error updating section header");
        }
      })
      .catch((err) => {
        console.error("Error updating section header:", err);
      });
  };

  const handleUpdateRecipeInstruction = () => {
    fetch(`${process.env.REACT_APP_API_URL}/api/update-instruction-to-recipe`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        row_id: selectedInstruction.id,
        instruction: editInstructionStep,
      }),
    })
      .then((res) => {
        if (res.ok) {
          // Reload recipe data
          fetchRecipeData(id);
          setSelectedInstruction(null);
          setEditInstructionStep("");
          fetchRecipeInstructions(id);
        } else {
          throw new Error("Error editing new instruction to recipe");
        }
      })
      .catch((err) => {
        console.error("Error adding new instruction to recipe:", err);
      });
  };

  const handleSaveIngredientOrder = async () => {
    try {
      // Send updates for each pair that needs swapping
      const updates = [];
      
      // Assign new sort orders based on current positions
      recipeIngredients.forEach((ingredient, index) => {
        if (ingredient.sort_order !== index) {
          updates.push({
            id: ingredient.id,
            newSortOrder: index
          });
        }
      });

      // Apply all updates
      for (const update of updates) {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/ingredients/sort`, {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id1: update.id,
            sortOrder1: update.newSortOrder,
            id2: update.id, // Same ID to just update sort order
            sortOrder2: update.newSortOrder
          }),
        });

        if (!response.ok) {
          throw new Error("Error saving ingredient order");
        }
      }

      // Refresh the ingredients list
      fetchRecipeIngredients(id);
      setHasUnsavedIngredientOrder(false);
    } catch (err) {
      console.error("Error saving ingredient order:", err);
      alert("Failed to save ingredient order. Please try again.");
    }
  };

  const handleSaveInstructionOrder = async () => {
    try {
      // Send updates for each pair that needs swapping
      const updates = [];
      
      // Assign new sort orders based on current positions
      recipeInstructions.forEach((instruction, index) => {
        if (instruction.sort_order !== index) {
          updates.push({
            id: instruction.id,
            newSortOrder: index
          });
        }
      });

      // Apply all updates
      for (const update of updates) {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/instruction/sort`, {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id1: update.id,
            sortOrder1: update.newSortOrder,
            id2: update.id, // Same ID to just update sort order
            sortOrder2: update.newSortOrder
          }),
        });

        if (!response.ok) {
          throw new Error("Error saving instruction order");
        }
      }

      // Refresh the instructions list
      fetchRecipeInstructions(id);
      setHasUnsavedInstructionOrder(false);
    } catch (err) {
      console.error("Error saving instruction order:", err);
      alert("Failed to save instruction order. Please try again.");
    }
  };

  const handleCancelIngredientOrder = () => {
    // Restore original order
    const originalOrder = [...recipeIngredients].sort((a, b) => {
      const aOrder = originalIngredientOrder.find(o => o.id === a.id)?.sort_order || 0;
      const bOrder = originalIngredientOrder.find(o => o.id === b.id)?.sort_order || 0;
      return aOrder - bOrder;
    });
    setRecipeIngredients(originalOrder);
    setHasUnsavedIngredientOrder(false);
  };

  const handleCancelInstructionOrder = () => {
    // Restore original order
    const originalOrder = [...recipeInstructions].sort((a, b) => {
      const aOrder = originalInstructionOrder.find(o => o.id === a.id)?.sort_order || 0;
      const bOrder = originalInstructionOrder.find(o => o.id === b.id)?.sort_order || 0;
      return aOrder - bOrder;
    });
    setRecipeInstructions(originalOrder);
    setHasUnsavedInstructionOrder(false);
  };

  const handleBackClick = () => {
    if (hasUnsavedIngredientOrder || hasUnsavedInstructionOrder) {
      const confirmLeave = window.confirm(
        "You have unsaved changes to the order of items. Are you sure you want to leave?"
      );
      if (!confirmLeave) return;
    }

    if (
      document.referrer &&
      document.referrer.startsWith(window.location.origin)
    ) {
      navigate(-1);
    } else {
      navigate("/recipes", { replace: true });
    }
  };

  async function updateImageRecord(imageUrl) {
    const updateData = {
      image_url: imageUrl.imageUrl,
      // any other fields you want to update
    };
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/recipe-image/${id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData),
      }
    );
    if (!response.ok) {
      // handle error
    }
    fetchRecipeData(id);
  }

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];

    const formData = new FormData();
    formData.append("id", id); // <--- add this line
    formData.append("image", file);

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/upload`,
      {
        method: "POST",
        body: formData,
      }
    );

    if (!response.ok) {
      // handle error
    }

    const imageUrl = await response.json();

    // Update the record in your database with the new image URL
    updateImageRecord(imageUrl);
  };

  const [isLoading, setIsLoading] = useState(false);

  const handleImageUpGenerate = async (event) => {
    handleUpdateRecipe();

    if (!hasGold) {
      alert("A Gold subscription is required for this feature");
      return;
    }

    setIsLoading(true);
    // Display confirmation message to user
    const confirmed = window.confirm(
      "Please make sure your recipe is complete before proceeding. The image generation is based off your RECIPE DESCRIPTION. The RECIPE TITLE, INGREDIENTS, and INSTRUCTIONS also impact the image that is generated. These should be complete for a more accurate image."
    );

    if (!confirmed) {
      setIsLoading(false);
      return;
    }

    const ingredientList = recipeIngredients.map(
      (ingredient) =>
        `${ingredient.qty}x ${ingredient.unit_name} ${ingredient.name} ${ingredient.suffix}`
    );
    const instructionList = recipeInstructions.map(
      (instruction, index) => `${index + 1} - ${instruction.instruction}`
    );

    const message = [
      {
        role: "user",
        content: `Respond with nothing but a Dall-e prompt I can use to generate a realistic image for my recipe. The style should be for a website. The dish should be plated. My recipe called "${recipeData.name}". Here are the ingredients: "${ingredientList}". Here are the instructions "${instructionList}". Here is the description: "${recipeData.description}"`,
      },
    ];

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/generate_image`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: id,
          message: message,
        }),
      }
    );

    if (!response.ok) {
      // handle error
    }

    const imageUrl = await response.json();

    // Update the record in your database with the new image URL
    setIsLoading(false);
    updateImageRecord(imageUrl);
  };

  // ADD NEW INGREDIENT OPTIONS
  // variables for adding new ingredient:
  const [showNewModal, setShowNewModal] = useState(false);
  const [newIngredientName, setNewIngredientName] = useState("");
  const [newUnitOfMeasurement, setNewUnitOfMeasurement] = useState("");
  const [newUnitOfMeasurementLabel, SetNewUnitOfMeasurementLabel] =
    useState("");
  const [newShoppingAisle, setNewShoppingAisle] = useState("");
  const [newProtein, setNewProtein] = useState("");
  const [newCarbs, setNewCarbs] = useState("");
  const [newFat, setNewFat] = useState("");
  const [newFiber, setNewFiber] = useState("");
  const [newSugar, setNewSugar] = useState("");
  const [newStoreAmount, setNewStoreAmount] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [newCalculatedPrice, setNewCalculatedPrice] = useState(0);
  const [newCustomUnitFlag, setNewCustomUnitFlag] = useState(0);
  const [newDefaultNeedsPrep, setNewDefaultNeedsPrep] = useState(false);
  const [showQuickUnitModal, setShowQuickUnitModal] = useState(false);
  const [newPricingUnit, setNewPricingUnit] = useState(null);
  const [newPricingUnitLabel, setNewPricingUnitLabel] = useState("");

  // Section Header Variables
  const [showIngredientSectionModal, setShowIngredientSectionModal] = useState(false);
  const [showInstructionSectionModal, setShowInstructionSectionModal] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  
  // Text Row Variables
  const [showTextRowModal, setShowTextRowModal] = useState(false);
  const [newTextRowContent, setNewTextRowContent] = useState("");

  // Import Ingredients Variables
  const [showImportIngredientsModal, setShowImportIngredientsModal] = useState(false);
  const [importIngredientsText, setImportIngredientsText] = useState("");
  const [parsedIngredients, setParsedIngredients] = useState([]);
  const [importIngredientsError, setImportIngredientsError] = useState("");
  const [parseErrors, setParseErrors] = useState({});
  const [skippedLines, setSkippedLines] = useState([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  
  // Import Instructions Variables
  const [showImportInstructionsModal, setShowImportInstructionsModal] = useState(false);
  const [importInstructionsText, setImportInstructionsText] = useState("");
  const [parsedInstructions, setParsedInstructions] = useState([]);
  const [originalParsedInstructions, setOriginalParsedInstructions] = useState([]);
  const [isParsingInstructions, setIsParsingInstructions] = useState(false);
  const [isImportingInstructions, setIsImportingInstructions] = useState(false);

  // Private Notes Variables
  const [showPrivateNotesModal, setShowPrivateNotesModal] = useState(false);

  const handlePrivateNotesOpenModal = () => {
    setShowPrivateNotesModal(true);
  };

  const handlePrivateNotesCloseModal = () => {
    setShowPrivateNotesModal(false);
  };

  const updateNotesContent = (updatedContent) => {
    setNotesContent(updatedContent);
  };

  // Holding the notes content state
  const [notesContent, setNotesContent] = useState("");
  const handlePrivatelSaveNotes = () => {
    fetch(`${process.env.REACT_APP_API_URL}/api/private_recipe_notes_save`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        recipe_id: recipeData.id,
        updatedNotes: notesContent,
      }),
    }).then((response) => {
      handlePrivateNotesCloseModal();
      if (!response.ok) {
        // Handle error
      }
    });
  };

  // Public Notes Variables
  const [showPublicNotesModal, setShowPublicNotesModal] = useState(false);

  const handlePublicNotesOpenModal = () => {
    setShowPublicNotesModal(true);
  };

  const handlePublicNotesCloseModal = () => {
    setShowPublicNotesModal(false);
  };

  const updatePublicNotesContent = (updatedContent) => {
    setPublicNotesContent(updatedContent);
  };

  // Holding the notes content state
  const [publicNotesContent, setPublicNotesContent] = useState(null);

  const handlePublicSaveNotes = () => {
    fetch(`${process.env.REACT_APP_API_URL}/api/public_recipe_notes`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        recipe_id: recipeData.id,
        updatedNotes: publicNotesContent,
      }),
    }).then((response) => {
      handlePublicNotesCloseModal();
      if (!response.ok) {
        // Handle error
      }
    });
  };

  // Add New Unit Modal Show/Hide
  const handleAddClickModal = () => {
    setShowNewModal(true);
  };

  const handleAddCloseModal = () => {
    setShowNewModal(false);
  };

  // Add New Unit Handle Form Changes
  const handleNewIngredientNameChange = (event) => {
    setNewIngredientName(event.target.value);
  };

  const handleNewStoreBaseAmountChange = (event) => {
    const value = event.target.value;
    setNewStoreAmount(value);
    setNewCalculatedPrice(newPrice / value);
  };

  const handleNewStorePriceAmountChange = (event) => {
    const value = event.target.value;
    setNewPrice(value);
    setNewCalculatedPrice(value / newStoreAmount);
  };

  // New Unit Macros:
  const handleNewProteinChange = (newProteinValue) => {
    if (!isNaN(newProteinValue)) {
      setNewProtein(newProteinValue);
    } else {
      setNewProtein(""); // Set a default value
    }
  };

  const handleNewCarbsChange = (newCarbsValue) => {
    if (!isNaN(newCarbsValue)) {
      setNewCarbs(newCarbsValue);
    } else {
      setNewCarbs(""); // Set a default value
    }
  };

  const handleNewFatChange = (newFatValue) => {
    if (!isNaN(newFatValue)) {
      setNewFat(newFatValue);
    } else {
      setNewFat(""); // Set a default value
    }
  };

  const handleNewFiberChange = (newFiberValue) => {
    if (!isNaN(newFiberValue)) {
      setNewFiber(newFiberValue);
    } else {
      setNewFiber(""); // Set a default value
    }
  };

  const handleNewSugarChange = (newSugarValue) => {
    if (!isNaN(newSugarValue)) {
      setNewSugar(newSugarValue);
    } else {
      setNewSugar(""); // Set a default value
    }
  };

  // Helper function to calculate calories
  const calculateCalories = (protein, carbs, fat) => {
    const proteinCal = (protein || 0) * 4;
    const carbsCal = (carbs || 0) * 4;
    const fatCal = (fat || 0) * 9;
    return proteinCal + carbsCal + fatCal;
  };

  const handleAddIngredient = () => {
    // Check that required fields have a value
    if (!newIngredientName || newIngredientName.trim() === "") {
      alert("Please enter an ingredient name.");
      return;
    }
    if (newIngredientName.trim().length > 100) {
      alert("Ingredient name must be 100 characters or less.");
      return;
    }
    if (!newUnitOfMeasurement) {
      alert("Please select a base unit of measurement for the ingredient.");
      return;
    }
    // Send POST request to add new unit
    fetch(`${process.env.REACT_APP_API_URL}/add-ingredient`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ingredientName: newIngredientName.trim(),
        unitOfMeasurement: newUnitOfMeasurement,
        customUnitFlag: newCustomUnitFlag,
        shoppingAisleId: newShoppingAisle,
        protein: newProtein,
        carbs: newCarbs,
        fat: newFat,
        fiber: newFiber,
        sugar: newSugar,
        price: newCalculatedPrice,
        storePrice: newPrice,
        storeAmount: newStoreAmount,
        defaultNeedsPrep: newDefaultNeedsPrep,
      }),
    })
      .then((res) => {
        if (res.ok) {
          // Parse the JSON response
          return res.json();
        } else {
          return res.text().then(text => {
            throw new Error(text || "Error adding new ingredient");
          });
        }
      })
      .then((resultIngredient) => {
        // Set variables based on the ingredient data
        // Assuming ingredient contains properties such as id, name, unitOfMeasurement, etc.

        // Set your variables with the retrieved values
        // Example:
        console.log(resultIngredient[0]);
        setSelectedIngredient(resultIngredient[0]);
        setEditIngredientName(resultIngredient[0].ingredient_name);
        setEditUnitOfMeasurement(resultIngredient[0].unit_id);
        setEditUnitOfMeasurementId(resultIngredient[0].default_unit_id);
        setEditShoppingAisle(resultIngredient[0].shopping_aisle);
        setEditShoppingAisleId(resultIngredient[0].custom_shopping_aisle_id);
        setEditProtein(resultIngredient[0].protein);
        setEditCarbs(resultIngredient[0].carbs);
        setEditFat(resultIngredient[0].fat);
        setEditFiber(resultIngredient[0].fiber);
        setEditSugar(resultIngredient[0].sugar);
        setEditStoreAmount(resultIngredient[0].store_amount);
        setEditPrice(resultIngredient[0].store_price);
        setEditCalculatedPrice(resultIngredient[0].price);
        setEditCustomUnitFlag(resultIngredient[0].custom_unit);
        setEditDefaultNeedsPrep(resultIngredient[0].default_needs_prep === 1);

        // Reload units data
        fetchIngredients();
        setNewIngredientName("");
        setNewUnitOfMeasurement("");
        SetNewUnitOfMeasurementLabel("");
        setNewShoppingAisle("");
        setNewProtein("");
        setNewCarbs("");
        setNewFat("");
        setNewFiber("");
        setNewSugar("");
        setNewStoreAmount("");
        setNewPrice("");
        setNewCalculatedPrice(0);
        setNewCustomUnitFlag(0);
        setNewDefaultNeedsPrep(false);
        handleAddCloseModal();
      })
      .catch((err) => {
        console.error("Error adding new ingredient:", err);
        alert(err.message || "Failed to add ingredient. Please try again.");
      });
  };

  // variables for editing ingredient
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [editIngredientName, setEditIngredientName] = useState("");
  const [editUnitOfMeasurement, setEditUnitOfMeasurement] = useState("");
  const [editUnitOfMeasurementId, setEditUnitOfMeasurementId] = useState("");
  const [editShoppingAisle, setEditShoppingAisle] = useState("");
  const [editShoppingAisleId, setEditShoppingAisleId] = useState("");
  const [editProtein, setEditProtein] = useState("");
  const [editCarbs, setEditCarbs] = useState("");
  const [editFat, setEditFat] = useState("");
  const [editFiber, setEditFiber] = useState("");
  const [editSugar, setEditSugar] = useState("");
  const [editStoreAmount, setEditStoreAmount] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [editCalculatedPrice, setEditCalculatedPrice] = useState(0);
  const [editCustomUnitFlag, setEditCustomUnitFlag] = useState(0);
  const [editDefaultNeedsPrep, setEditDefaultNeedsPrep] = useState(false);
  const [ingredientConversionList, setIngredientConversionList] = useState([]);
  const [addConversionInput, setAddConversionInput] = useState("");

  // Variables for Editing Conversions
  const [selectedConversionToEdit, setSelectedConversionToEdit] =
    useState(null);
  const [selectedConversionToEditId, setSelectedConversionToEditId] =
    useState(null);
  const [editConversionUnitOfMeasurement, setEditConversionUnitOfMeasurement] =
    useState("");
  const [
    editConversionUnitOfMeasurementId,
    setEditConversionUnitOfMeasurementId,
  ] = useState("");
  const [editConversionCustomUnitFlag, setEditConversionCustomUnitFlag] =
    useState(0);
  const [editConversionFactor, setEditConversionFactor] = useState("");

  // variable for unit options
  const [unitOptions, setUnitOptions] = useState([]);
  const [selectedUnitOption, setSelectedUnitOption] = useState(null);

  // var for shopping options:
  const [shoppingOptions, setShoppingOptions] = useState([]);

  const handleEditConversion = (conversion) => {
    setSelectedConversionToEdit(conversion);
    setSelectedConversionToEditId(conversion.id);
    setEditConversionUnitOfMeasurement(conversion.unit_name);
    setEditConversionUnitOfMeasurementId(conversion.unit_id);
    setEditConversionCustomUnitFlag(conversion.custom_unit);
    setEditConversionFactor(conversion.conversion_factor);
    console.log(conversion);
  };

  const handleAddConversionInput = (newConverionValue) => {
    if (!isNaN(newConverionValue)) {
      setAddConversionInput(newConverionValue);
    } else {
      setAddConversionInput("");
    }
  };

  const handleEditConversionInput = (newConverionValue) => {
    if (!isNaN(newConverionValue)) {
      setEditConversionFactor(newConverionValue);
    } else {
      setEditConversionFactor("");
    }
  };

  // fetch available unit options:
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/units`, {
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => setUnitOptions(data))
      .catch((error) => console.error("Error retrieving units:", error));
  }, []);

  // fetch shopping list unit options:
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/shopping_aisle`, {
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => setShoppingOptions(data))
      .catch((error) => console.error("Error retrieving units:", error));
  }, []);

  // Funciton to Fetch Unit Converions of the Selected Ingredient
  const fetchIngredientConversions = async (id) => {
    // Determine custom_ingredient flag
    const customIngredientFlag = id.includes("user") ? 1 : 0;

    const ingredientId = id.split(",")[0];

    // Make API call to retrieve unit conversions
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/ingredient-conversions/${ingredientId}?custom_ingredient=${customIngredientFlag}`,
      {
        credentials: "include",
      }
    );

    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      console.error(response.statusText);
      return [];
    }
  };

  const handleSelectIngredient = async (ingredient) => {
    const old_ingredient_id = ingredient.old_id;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/getIngredientToEdit/${old_ingredient_id}`,
        {
          credentials: "include",
        }
      );

      if (response.ok) {
        response.json().then((result) => {
          const editingIngredient = result[0];
          console.log(editingIngredient);

          setSelectedIngredient(editingIngredient);
          setEditIngredientName(editingIngredient.ingredient_name);
          setEditUnitOfMeasurement(editingIngredient.unit_id);
          setEditUnitOfMeasurementId(editingIngredient.default_unit_id);
          setEditShoppingAisle(editingIngredient.shopping_aisle);
          setEditShoppingAisleId(editingIngredient.custom_shopping_aisle_id);
          setEditProtein(editingIngredient.protein);
          setEditCarbs(editingIngredient.carbs);
          setEditFat(editingIngredient.fat);
          setEditFiber(editingIngredient.fiber);
          setEditSugar(editingIngredient.sugar);
          setEditStoreAmount(editingIngredient.store_amount);
          setEditPrice(editingIngredient.store_price);
          setEditCalculatedPrice(editingIngredient.price);
          setEditCustomUnitFlag(editingIngredient.custom_unit);
          setEditDefaultNeedsPrep(editingIngredient.default_needs_prep === 1);

          fetchIngredientConversions(ingredient.old_id).then((data) => {
            setIngredientConversionList(data);
          });
        });
      } else {
        console.error("Error retrieving ingredient:", response.status);
        // Handle error case
      }
    } catch (error) {
      console.error("Error retrieving ingredient:", error);
      // Handle error case
    }
  };

  // Add Edit Unit Handle Form Changes
  const handleEditIngredientNameChange = (event) => {
    setEditIngredientName(event.target.value);
  };

  const handleEditStoreBaseAmountChange = (event) => {
    const value = event.target.value;
    setEditStoreAmount(value);
    setEditCalculatedPrice(editPrice / value);
  };

  const handleEditStorePriceAmountChange = (event) => {
    const value = event.target.value;
    setEditPrice(value);
    setEditCalculatedPrice(value / editStoreAmount);
  };

  // Edit Unit Macros:
  const handleEditProteinChange = (newProteinValue) => {
    if (!isNaN(newProteinValue)) {
      setEditProtein(newProteinValue);
    } else {
      setEditProtein(""); // Set a default value
    }
  };

  const handleEditCarbsChange = (newCarbsValue) => {
    if (!isNaN(newCarbsValue)) {
      setEditCarbs(newCarbsValue);
    } else {
      setEditCarbs(""); // Set a default value
    }
  };

  const handleEditFatChange = (newFatValue) => {
    if (!isNaN(newFatValue)) {
      setEditFat(newFatValue);
    } else {
      setEditFat(""); // Set a default value
    }
  };

  const handleEditFiberChange = (newFiberValue) => {
    if (!isNaN(newFiberValue)) {
      setEditFiber(newFiberValue);
    } else {
      setEditFiber(""); // Set a default value
    }
  };

  const handleEditSugarChange = (newSugarValue) => {
    if (!isNaN(newSugarValue)) {
      setEditSugar(newSugarValue);
    } else {
      setEditSugar(""); // Set a default value
    }
  };

  const handleSaveIngredient = () => {
    if (selectedIngredient.id.includes("user")) {
      const ingredient_id = selectedIngredient.id.split(",")[0];
      // This is a user ingredient, send PUT request to backend
      fetch(
        `${process.env.REACT_APP_API_URL}/edit-ingredient?id=${ingredient_id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: ingredient_id,
            ingredientName: editIngredientName.trim(),
            unitOfMeasurement: editUnitOfMeasurementId,
            customUnitFlag: editCustomUnitFlag,
            shoppingAisleId: editShoppingAisleId,
            protein: editProtein,
            carbs: editCarbs,
            fat: editFat,
            fiber: editFiber,
            sugar: editSugar,
            price: editCalculatedPrice,
            storePrice: editPrice,
            storeAmount: editStoreAmount,
            defaultNeedsPrep: editDefaultNeedsPrep,
          }),
        }
      )
        .then((res) => {
          if (res.ok) {
            // Reload ingredients data
            fetchRecipeIngredients(id);
            fetchIngredients();
            setSelectedIngredient(null);
            setEditIngredientName("");
            setEditUnitOfMeasurement("");
            setEditUnitOfMeasurementId("");
            setEditShoppingAisle("");
            setEditShoppingAisleId("");
            setEditProtein("");
            setEditCarbs("");
            setEditFat("");
            setEditFiber("");
            setEditSugar("");
            setEditStoreAmount("");
            setEditPrice("");
            setEditCalculatedPrice(0);
            setEditCustomUnitFlag(0);
            setEditDefaultNeedsPrep(false);
          } else {
            throw new Error("Error updating ingredient");
          }
        })
        .catch((err) => {
          console.error("Error updating ingredient:", err);
        });
    } else {
      const ingredient_id = selectedIngredient.id.split(",")[0];
      // This is not a user ingredient, query user_ingredient_customizations table
      const url = `${process.env.REACT_APP_API_URL}/user-ingredient-customization?ingredient_id=${ingredient_id}`;
      fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((res) => {
          if (res.ok) {
            return res.json();
          } else {
            throw new Error("Error querying user ingredient customization");
          }
        })
        .then((data) => {
          console.log(data);
          try {
            if (data.length === 0) {
              // No match, create new entry in user_ingredient_customizations table
              fetch(
                `${process.env.REACT_APP_API_URL}/user-ingredient-customization`,
                {
                  method: "POST",
                  credentials: "include",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({
                    id: ingredient_id,
                    shoppingAisleId: editShoppingAisleId,
                    price: editCalculatedPrice,
                    storePrice: editPrice,
                    storeAmount: editStoreAmount,
                  }),
                }
              )
                .then((res) => {
                  if (res.ok) {
                    // Reload ingredients data
                    fetchRecipeIngredients(id);
                    fetchIngredients();
                    setSelectedIngredient(null);
                    setEditIngredientName("");
                    setEditUnitOfMeasurement("");
                    setEditUnitOfMeasurementId("");
                    setEditShoppingAisle("");
                    setEditShoppingAisleId("");
                    setEditProtein("");
                    setEditCarbs("");
                    setEditFat("");
                    setEditFiber("");
                    setEditSugar("");
                    setEditStoreAmount("");
                    setEditPrice("");
                    setEditCalculatedPrice(0);
                    setEditCustomUnitFlag(0);
                    setEditDefaultNeedsPrep(false);
                  } else {
                    throw new Error(
                      "Error creating user ingredient customization"
                    );
                  }
                })
                .catch((err) => {
                  console.error(
                    "Error creating user ingredient customization:",
                    err
                  );
                });
            } else {
              // Match found, update existing entry in user_ingredient_customizations table
              fetch(
                `${process.env.REACT_APP_API_URL}/user-ingredient-customization/${data[0].ingredient_id}`,
                {
                  method: "PUT",
                  credentials: "include",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({
                    id: ingredient_id,
                    shoppingAisleId: editShoppingAisleId,
                    price: editCalculatedPrice,
                    storePrice: editPrice,
                    storeAmount: editStoreAmount,
                  }),
                }
              )
                .then((res) => {
                  if (res.ok) {
                    // Reload ingredients data
                    fetchRecipeIngredients(id);
                    fetchIngredients();
                    setSelectedIngredient(null);
                    setEditIngredientName("");
                    setEditUnitOfMeasurement("");
                    setEditUnitOfMeasurementId("");
                    setEditShoppingAisle("");
                    setEditShoppingAisleId("");
                    setEditProtein("");
                    setEditCarbs("");
                    setEditFat("");
                    setEditFiber("");
                    setEditSugar("");
                    setEditStoreAmount("");
                    setEditPrice("");
                    setEditCalculatedPrice(0);
                    setEditCustomUnitFlag(0);
                    setEditDefaultNeedsPrep(false);
                  } else {
                    throw new Error(
                      "Error updating user ingredient customization"
                    );
                  }
                })
                .catch((err) => {
                  console.error(
                    "Error updating user ingredient customization:",
                    err
                  );
                });
            }
          } catch (err) {
            console.error("Error parsing JSON response:", err);
          }
        })
        .catch((err) => {
          console.error("Error querying user ingredient customization:", err);
        });
    }
  };

  const handleAddUnitConversion = () => {
    if (!addConversionInput) {
      alert("Please add a conversion qty.");
      return;
    }
    const conversionFactor = inverseConversion
      ? 1 / addConversionInput
      : addConversionInput;

    const customIngredientFlag = selectedIngredient.id.includes("user") ? 1 : 0;
    const ingredientId = selectedIngredient.id.split(",")[0];

    // Check for existing unit conversion
    fetch(
      `${process.env.REACT_APP_API_URL}/api/check-unit-conversion?ingredientId=${ingredientId}&newUnitOfMeasurement=${newUnitOfMeasurement}&newCustomUnitFlag=${newCustomUnitFlag}&customIngredientFlag=${customIngredientFlag}`,
      {
        credentials: "include",
      }
    )
      .then((response) => {
        if (response.ok) {
          // No existing conversion found, continue with adding a new conversion
          return fetch(
            `${process.env.REACT_APP_API_URL}/api/add-unit-conversion`,
            {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                customIngredientFlag: customIngredientFlag,
                ingredientId: ingredientId,
                newUnitOfMeasurement: newUnitOfMeasurement,
                newCustomUnitFlag: newCustomUnitFlag,
                addConversionInput: conversionFactor,
              }),
            }
          );
        } else {
          // Existing conversion found, display error message
          const error =
            "Duplicate Entry - You already have an existing conversion for this unit";
          throw new Error(error);
        }
      })
      .then((response) => response.json())
      .then((data) => {
        //Fetch the updated ingredient conversions after successful addition
        fetchIngredientConversions(selectedIngredient.id)
          .then((data) => setIngredientConversionList(data))
          .catch((error) => console.error(error));
        //Clear the addConversionInput field
        setAddConversionInput("");
        fetchRecipeIngredients(id);
      })
      .catch((error) => {
        console.error(error.message);
        alert(error.message);
      });
  };

  // Update Unit Conversion
  const handleUpdateUnitConversion = async () => {
    try {
      const conversionFactor = inverseConversion
        ? 1 / editConversionFactor
        : editConversionFactor;
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/conversions/${selectedConversionToEditId}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            unit_id: editConversionUnitOfMeasurementId,
            custom_unit: editConversionCustomUnitFlag,
            conversion_factor: conversionFactor,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      // Update the state with the new values
      setSelectedConversionToEdit({
        id: selectedConversionToEditId,
        unit_id: editConversionUnitOfMeasurementId,
        custom_unit: editConversionCustomUnitFlag,
        conversion_factor: editConversionFactor,
      });

      // Clear the form fields
      fetchIngredientConversions(selectedIngredient.id)
        .then((data) => setIngredientConversionList(data))
        .catch((error) => console.error(error));
      setSelectedConversionToEdit(null);
      setEditConversionUnitOfMeasurement("");
      setEditConversionUnitOfMeasurementId("");
      setEditConversionCustomUnitFlag(0);
      setEditConversionFactor("");
      fetchRecipeIngredients(id);
    } catch (error) {
      console.error(error);
    }
  };

  // Handle Delete Conversion
  const handleDeleteConversion = async (conversion) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the conversion "${conversion.unit_name}"?`
    );

    if (!confirmed) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/conversions/delete/${conversion.id}`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      // Remove the deleted conversion from the ingredientConversionList state
      fetchIngredientConversions(selectedIngredient.id)
        .then((data) => setIngredientConversionList(data))
        .catch((error) => console.error(error));
      fetchRecipeIngredients(id);
    } catch (error) {
      console.error(error);
    }
  };

  const isUserRecipeOwner = () => {
    if (recipeData && currentUser && recipeData.user_id === currentUser) {
      console.log(`Recipe Data: ${recipeData.user_id}`);
      console.log(`Current user: ${currentUser}`);
      setUserIsOwner(true);
      console.log(userIsOwner);
    }
  };

  // Import Ingredients Functions
  const handleImportIngredientsOpen = () => {
    setShowImportIngredientsModal(true);
    setImportIngredientsText("");
    setParsedIngredients([]);
    setImportIngredientsError("");
    setParseErrors({});
    setSkippedLines([]);
  };

  const handleImportIngredientsClose = () => {
    setShowImportIngredientsModal(false);
    setImportIngredientsText("");
    setParsedIngredients([]);
    setImportIngredientsError("");
    setParseErrors({});
    setSkippedLines([]);
    setIsParsing(false);
    setIsImporting(false);
  };

  // Fuzzy matching function for ingredients
  const fuzzyMatchIngredient = (searchTerm, ingredients) => {
    if (!searchTerm || !ingredients) return null;
    
    console.log(`Fuzzy matching "${searchTerm}" against ${ingredients.length} ingredients`);
    
    // Normalize the search term
    const normalizedSearch = searchTerm
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ') // Remove special characters except spaces
      .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
      .trim();
    
    // Try exact match first
    let exactMatch = ingredients.find(ing => 
      ing.label.toLowerCase() === searchTerm.toLowerCase()
    );
    if (exactMatch) {
      console.log(`Exact match found for "${searchTerm}":`, exactMatch);
      return exactMatch;
    }
    
    // Special cases to prevent bad matches
    const preventMatches = {
      // Prevent compound ingredients from matching their parts
      'egg mayonnaise': ['egg', 'mayonnaise'],
      'whole egg mayonnaise': ['egg', 'mayonnaise', 'whole egg'],
      'egg white': ['egg', 'white'],
      'egg yolk': ['egg', 'yolk'],
      'tomato paste': ['tomato', 'paste'],
      'tomato sauce': ['tomato', 'sauce'],
      'cream cheese': ['cream', 'cheese'],
      'sour cream': ['cream', 'sour'],
      'heavy cream': ['cream', 'heavy'],
      'whipped cream': ['cream', 'whipped'],
      'coconut milk': ['coconut', 'milk'],
      'almond milk': ['almond', 'milk'],
      'peanut butter': ['peanut', 'butter'],
      'apple cider vinegar': ['apple', 'cider', 'vinegar'],
      'balsamic vinegar': ['balsamic', 'vinegar'],
      'chicken breast': ['chicken broth', 'chicken stock'],  // Prevent breast from matching broth
      'chicken broth': ['chicken breast'],  // Prevent broth from matching breast
    };
    
    // Check if this search term should not match certain ingredients
    const searchLower = normalizedSearch.toLowerCase();
    for (const [compound, parts] of Object.entries(preventMatches)) {
      if (searchLower.includes(compound)) {
        // This is a compound ingredient, don't match to its parts
        const shouldExclude = (ingLabel) => {
          const ingLower = ingLabel.toLowerCase();
          // If the ingredient is just one of the parts, exclude it
          return parts.some(part => ingLower === part) && !ingLower.includes(compound);
        };
        
        // Filter out bad matches
        const filteredIngredients = ingredients.filter(ing => !shouldExclude(ing.label));
        
        // Continue with filtered list
        ingredients = filteredIngredients;
      }
    }
    
    // Prepare variations of the search term
    const searchVariations = [];
    
    // Original normalized term
    searchVariations.push(normalizedSearch);
    
    // Remove common descriptors
    const descriptors = ['pure', 'fresh', 'organic', 'raw', 'natural', 'premium', 'quality', 'fine', 'extra', 'super', 'large', 'medium', 'small', 'whole'];
    let withoutDescriptors = normalizedSearch;
    descriptors.forEach(desc => {
      withoutDescriptors = withoutDescriptors.replace(new RegExp(`\\b${desc}\\s+`, 'g'), '');
    });
    if (withoutDescriptors !== normalizedSearch && withoutDescriptors.trim()) {
      searchVariations.push(withoutDescriptors.trim());
    }
    
    // Handle plurals - more sophisticated approach
    if (normalizedSearch.endsWith('s') && normalizedSearch.length > 2) {
      // Remove 's' but check for special cases
      if (normalizedSearch.endsWith('ies')) {
        // berries -> berry
        searchVariations.push(normalizedSearch.slice(0, -3) + 'y');
      } else if (normalizedSearch.endsWith('ves')) {
        // leaves -> leaf, halves -> half
        searchVariations.push(normalizedSearch.slice(0, -3) + 'f');
      } else if (normalizedSearch.endsWith('oes')) {
        // tomatoes -> tomato
        searchVariations.push(normalizedSearch.slice(0, -2));
      } else if (!normalizedSearch.endsWith('ss')) {
        // Don't remove 's' from words like 'grass'
        searchVariations.push(normalizedSearch.slice(0, -1));
      }
    } else {
      searchVariations.push(normalizedSearch + 's'); // Add 's'
    }
    
    // Handle egg/eggs special case for "egg yolk" vs "egg yolks"
    if (normalizedSearch.includes('egg yolks')) {
      searchVariations.push(normalizedSearch.replace('egg yolks', 'egg yolk'));
    } else if (normalizedSearch.includes('egg yolk')) {
      searchVariations.push(normalizedSearch.replace('egg yolk', 'egg yolks'));
    }
    
    // Handle common plural forms
    const pluralMappings = {
      'tomatoes': 'tomato',
      'potatoes': 'potato',
      'leaves': 'leaf',
      'halves': 'half',
      'loaves': 'loaf',
      'knives': 'knife',
      'shelves': 'shelf',
      'berries': 'berry',
      'cherries': 'cherry',
      'yolks': 'yolk',
      'whites': 'white'
    };
    
    Object.entries(pluralMappings).forEach(([plural, singular]) => {
      if (normalizedSearch.includes(plural)) {
        searchVariations.push(normalizedSearch.replace(plural, singular));
      } else if (normalizedSearch.includes(singular)) {
        searchVariations.push(normalizedSearch.replace(singular, plural));
      }
    });
    
    // Score-based matching system
    const scoredMatches = [];
    
    for (const variation of searchVariations) {
      ingredients.forEach(ing => {
        // First normalize the ingredient name, but also keep a version with parentheses content
        const fullIngName = ing.label.toLowerCase();
        const normalizedIngName = ing.label
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        
        // Extract main part before parentheses for better matching
        const mainPartMatch = fullIngName.match(/^([^(]+)(?:\s*\(|$)/);
        const mainPart = mainPartMatch ? mainPartMatch[1].trim().toLowerCase() : fullIngName;
        const normalizedMainPart = mainPart
          .replace(/[^a-z0-9\s]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        
        let score = 0;
        let matchReason = '';
        
        // Exact match (highest score)
        if (normalizedIngName === variation || normalizedMainPart === variation) {
          score = 100;
          matchReason = 'exact match';
        }
        // Check if variation matches the main part exactly (e.g., "heavy cream" matches "Heavy Cream (Heavy Whipping Cream)")
        else if (mainPart === variation || normalizedMainPart === variation) {
          score = 95;
          matchReason = 'exact match to main part';
        }
        // Words match in different order
        else {
          const searchWords = variation.split(' ').filter(w => w.length > 0);
          const ingWords = normalizedIngName.split(' ').filter(w => w.length > 0);
          
          if (searchWords.sort().join(' ') === ingWords.sort().join(' ')) {
            score = 90;
            matchReason = 'words match (different order)';
          }
          // Check against main part words too
          else {
            const mainPartWords = normalizedMainPart.split(' ').filter(w => w.length > 0);
            if (searchWords.sort().join(' ') === mainPartWords.sort().join(' ')) {
              score = 88;
              matchReason = 'words match main part (different order)';
            }
            // All search words found in ingredient
            else {
              // Check for confusing similar words
              const hasConfusingMatch = (searchWord, ingWord) => {
                // Don't match "breast" with "broth" or vice versa
                if ((searchWord === 'breast' && ingWord === 'broth') || 
                    (searchWord === 'broth' && ingWord === 'breast')) {
                  return false;
                }
                // Don't match "breasts" with "broth"
                if ((searchWord === 'breasts' && ingWord === 'broth') || 
                    (searchWord === 'broth' && ingWord === 'breasts')) {
                  return false;
                }
                return ingWord.includes(searchWord) || searchWord.includes(ingWord);
              };
              
              const allSearchWordsFound = searchWords.every(word => 
                ingWords.some(ingWord => hasConfusingMatch(word, ingWord))
              );
              
              // Also check against main part
              const allSearchWordsFoundInMain = searchWords.every(word => 
                mainPartWords.some(ingWord => hasConfusingMatch(word, ingWord))
              );
            
            if (allSearchWordsFound || allSearchWordsFoundInMain) {
              // Prefer main part matches for ingredients with parentheses
              const useMainPart = allSearchWordsFoundInMain && fullIngName.includes('(');
              const compareWords = useMainPart ? mainPartWords : ingWords;
              const extraWords = compareWords.length - searchWords.length;
              
              // Give bonus points if the exact search term appears in the ingredient name
              let exactWordBonus = 0;
              if (fullIngName.includes(variation)) {
                exactWordBonus = 5;
              }
              
              if (extraWords === 0) {
                score = (useMainPart ? 85 : 80) + exactWordBonus;
                matchReason = useMainPart ? 'all words match main part' : 'all words match';
              } else if (extraWords === 1) {
                score = (useMainPart ? 75 : 70) + exactWordBonus;
                matchReason = useMainPart ? 'all words match main part with 1 extra' : 'all words match with 1 extra';
              } else if (extraWords === 2) {
                score = (useMainPart ? 65 : 60) + exactWordBonus;
                matchReason = useMainPart ? 'all words match main part with 2 extra' : 'all words match with 2 extra';
              } else {
                score = 50 + exactWordBonus;
                matchReason = 'all words match with many extra';
              }
            }
            // Partial containment (lower scores)
            else if (normalizedIngName.includes(variation) || variation.includes(normalizedIngName)) {
              // Only if it's a meaningful match
              if (variation.length > 3) {
                // Penalize if the search term is much longer than the ingredient
                if (variation.length > normalizedIngName.length * 1.5) {
                  score = 20;
                  matchReason = 'partial match (search much longer)';
                } else {
                  score = 40;
                  matchReason = 'partial containment';
                }
              }
            }
          }
          }
        }
        
        // Handle specific cases with bonus scoring
        // "granulated sugar" vs "sugar (granulated white)"
        if (score === 0 && variation.includes('sugar') && normalizedIngName.includes('sugar')) {
          const sugarTypes = ['granulated', 'white', 'brown', 'powdered', 'confectioners', 'confectioner', 'caster', 'icing'];
          const hasMatchingType = sugarTypes.some(type => 
            variation.includes(type) && normalizedIngName.includes(type)
          );
          if (hasMatchingType) {
            score = 85;
            matchReason = 'sugar type match';
          }
          // If no type specified in search but ingredient has type, still match
          else if (variation === 'sugar' && sugarTypes.some(type => normalizedIngName.includes(type))) {
            score = 75;
            matchReason = 'generic sugar match';
          }
        }
        
        // "all-purpose flour" vs "all purpose flour" vs "flour, all purpose"
        if (score === 0 && variation.includes('flour') && normalizedIngName.includes('flour')) {
          const flourTypes = ['all purpose', 'allpurpose', 'bread', 'cake', 'self rising', 'selfraising', 'whole wheat', 'wholewheat', 'plain'];
          const hasMatchingType = flourTypes.some(type => {
            const normalizedType = type.replace(/\s+/g, '');
            return (variation.includes(type) || variation.includes(normalizedType)) && 
                   (normalizedIngName.includes(type) || normalizedIngName.includes(normalizedType));
          });
          if (hasMatchingType) {
            score = 85;
            matchReason = 'flour type match';
          }
          // Match "all-purpose flour" with "flour all purpose"
          else if (variation.replace(/-/g, ' ').split(' ').sort().join(' ') === 
              normalizedIngName.split(' ').sort().join(' ')) {
            score = 90;
            matchReason = 'flour words reordered';
          }
        }
        
        // "unsalted butter" vs "butter, unsalted" vs "butter unsalted"
        if (score === 0 && ((variation.includes('butter') && normalizedIngName.includes('butter')) ||
            (variation.includes('margarine') && normalizedIngName.includes('margarine')))) {
          const butterTypes = ['unsalted', 'salted', 'clarified', 'melted', 'softened', 'cold', 'room temperature'];
          const searchTypes = butterTypes.filter(type => variation.includes(type));
          const ingTypes = butterTypes.filter(type => normalizedIngName.includes(type));
          // If both have types and they match
          if (searchTypes.length > 0 && ingTypes.length > 0 && 
              searchTypes.some(type => ingTypes.includes(type))) {
            score = 85;
            matchReason = 'butter type match';
          }
          // If search has no type but ingredient does, still match
          else if (searchTypes.length === 0 && variation.split(' ').includes('butter') && 
              normalizedIngName.split(' ').includes('butter')) {
            score = 75;
            matchReason = 'generic butter match';
          }
        }
        
        // "vanilla extract" vs "pure vanilla extract" - already handled by descriptor removal
        // But let's add specific handling for extracts
        if (score === 0 && variation.includes('extract') && normalizedIngName.includes('extract')) {
          const extractTypes = ['vanilla', 'almond', 'lemon', 'orange', 'peppermint', 'coconut'];
          const hasMatchingType = extractTypes.some(type => 
            variation.includes(type) && normalizedIngName.includes(type)
          );
          if (hasMatchingType) {
            score = 85;
            matchReason = 'extract type match';
          }
        }
        
        // Handle chocolate types - prefer exact type matches
        if (score === 0 && variation.includes('chocolate') && normalizedIngName.includes('chocolate')) {
          const chocolateTypes = ['bittersweet', 'bitter sweet', 'semi sweet', 'semisweet', 'milk', 'dark', 'white', 'unsweetened'];
          
          // Check for exact type match
          const searchChocolateType = chocolateTypes.find(type => variation.includes(type));
          const ingChocolateType = chocolateTypes.find(type => normalizedIngName.includes(type) || normalizedMainPart.includes(type));
          
          if (searchChocolateType && ingChocolateType) {
            if (searchChocolateType === ingChocolateType) {
              score = 88;
              matchReason = 'exact chocolate type match';
            } else if ((searchChocolateType === 'bittersweet' || searchChocolateType === 'bitter sweet') && 
                      (ingChocolateType === 'bittersweet' || ingChocolateType === 'bitter sweet')) {
              // Handle variations of bittersweet
              score = 88;
              matchReason = 'bittersweet chocolate match';
            } else if ((searchChocolateType === 'semisweet' || searchChocolateType === 'semi sweet') && 
                      (ingChocolateType === 'semisweet' || ingChocolateType === 'semi sweet')) {
              // Handle variations of semisweet
              score = 85;
              matchReason = 'semisweet chocolate match';
            } else {
              // Different chocolate types - lower score
              score = 60;
              matchReason = 'different chocolate type';
            }
          } else if (!searchChocolateType && ingChocolateType) {
            // Generic chocolate search
            score = 70;
            matchReason = 'generic chocolate match';
          } else if (searchChocolateType && !ingChocolateType) {
            // Search has type but ingredient doesn't specify
            score = 65;
            matchReason = 'chocolate match (no type in ingredient)';
          }
        }
        
        // Add to scored matches if score is above threshold
        if (score >= 50) {
          // Check if we already have this ingredient in our matches
          const existingMatch = scoredMatches.find(m => m.ingredient.value === ing.value);
          if (!existingMatch || existingMatch.score < score) {
            if (existingMatch) {
              // Replace with better score
              const index = scoredMatches.indexOf(existingMatch);
              scoredMatches[index] = { ingredient: ing, score, matchReason, variation };
            } else {
              scoredMatches.push({ ingredient: ing, score, matchReason, variation });
            }
          }
        }
      });
    }
    
    // Sort by score and return the best match
    if (scoredMatches.length > 0) {
      scoredMatches.sort((a, b) => b.score - a.score);
      const bestMatch = scoredMatches[0];
      
      // Only return matches with reasonable scores
      // Prevent "tomatoes" from matching "tomato paste" (partial match with low score)
      if (bestMatch.score >= 60) {
        console.log(`Fuzzy match found for "${searchTerm}" -> "${bestMatch.ingredient.label}" (score: ${bestMatch.score}, reason: ${bestMatch.matchReason})`);
        return bestMatch.ingredient;
      } else {
        console.log(`Low score match rejected for "${searchTerm}" -> "${bestMatch.ingredient.label}" (score: ${bestMatch.score})`);
      }
    }
    
    console.log(`No suitable match found for "${searchTerm}"`);
    return null;
  };

  const parseIngredientLine = (line) => {
    // Clean up the line - remove Unicode checkboxes and other common prefixes
    line = line.trim();
    // Remove common prefixes like checkboxes, bullets, etc.
    line = line.replace(/^[▢□☐✓✔●•·\-\*]\s*/u, '');
    
    if (!line) return null;

    // Skip lines that are just "Ingredient" or "Ingredients" (case insensitive)
    if (/^ingredients?$/i.test(line)) {
      return null;
    }

    // Skip lines that contain common UI/instruction text
    if (/cook mode|prevent.*screen|going dark|instructions?:/i.test(line)) {
      return null;
    }

    // Skip section headers (lines that don't start with numbers or contain common header words)
    const headerPatterns = [
      /^(ingredients?|pastry|sponge|cake|frosting|glaze|filling|topping|crust|dough|sauce|cream|batter|mixture|marinade|burger[s]?|sandwich[es]?)\s*:?$/i,
      /^for (the|a)/i,
      /:$/,
      /^ingredients?\s*:/i,  // Skip "Ingredients:" or "Ingredient:"
      /^(wet|dry)\s+ingredients?\s*:?$/i,  // Skip "Wet ingredients:" or "Dry ingredients"
      // Skip common recipe section headers (without colons)
      /^(pastry cream|sponge cake|chocolate glaze|butter cream|cream cheese frosting|ganache|caramel sauce|vanilla custard|whipped cream|meringue|pie crust|cookie dough|cake batter|bread dough|pizza dough|peri peri sauce|perinaise)$/i,
      // Skip lines that are likely headers (2-3 words, common baking terms)
      /^([\w\s]{2,25})$/i.test(line) && /\b(cake|cream|glaze|frosting|filling|sauce|dough|batter|crust|topping|coating|icing|ganache|custard|mousse|pudding|syrup|jam|jelly|compote|marinade|burger[s]?)\b/i.test(line) && !/\d/.test(line)
    ];
    
    // Special check for potential headers - lines with 2-3 words that include common recipe components
    // Also allow forward slashes for things like "Sauce/Marinade"
    const potentialHeader = /^([\w\s\/]{2,30})$/i.test(line) && 
                          /\b(cake|cream|glaze|frosting|filling|sauce|dough|batter|crust|topping|coating|icing|ganache|custard|mousse|pudding|syrup|jam|jelly|compote|marinade|burger[s]?)\b/i.test(line) && 
                          !/\d/.test(line); // No numbers in the line
    
    // Check if this looks like a sauce/condiment name (often ending in "aise", "naise", etc.)
    const isSauceName = /\b(aise|naise|mayo|aioli|pesto|salsa|chutney|relish|dressing|vinaigrette|reduction|coulis|jus|gravy|ragu|bolognese)\b/i.test(line);
    
    // Additional contextual check - if a line has no numbers and is not too long, it might be a section
    // This catches things like "Peri Peri Sauce/Marinade", "Perinaise", "Burgers"
    const contextualHeader = /^[\w\s\/\-]{2,30}$/i.test(line) && 
                            !/\d/.test(line) && // No numbers
                            !/(cup|tablespoon|teaspoon|pound|ounce|gram|liter|tbsp|tsp|oz|lb|g|ml|kg)s?\b/i.test(line) && // No units
                            (isSauceName || line.split(/\s+/).length <= 2); // Either a sauce name or very short (1-2 words)
    
    if (potentialHeader || contextualHeader) {
      return {
        isSection: true,
        sectionName: line.trim(),
        original: line
      };
    }
    
    for (const pattern of headerPatterns) {
      if (typeof pattern === 'boolean' ? pattern : pattern.test(line)) {
        return {
          isSection: true,
          sectionName: line.trim(),
          original: line
        };
      }
    }

    // Check if this is a text row (often in brackets or parentheses)
    const textRowPattern = /^\[([^\]]+)\]$|^\(([^)]+)\)$/;
    const textRowMatch = line.match(textRowPattern);
    if (textRowMatch) {
      return {
        isTextRow: true,
        textContent: textRowMatch[1] || textRowMatch[2],
        original: line
      };
    }

    // Unit abbreviation mappings - maps various forms to a canonical form
    const unitMappings = {
      // Volume - US
      'tablespoon': ['tablespoon', 'tablespoons', 'tbsp', 'tbsps', 'tbs', 'tbl', 'tb', 'T'],
      'teaspoon': ['teaspoon', 'teaspoons', 'tsp', 'tsps', 't'],
      'cup': ['cup', 'cups', 'c', 'C'],
      'fluid ounce': ['fluid ounce', 'fluid ounces', 'fl oz', 'fl. oz', 'fl.oz', 'floz'],
      'pint': ['pint', 'pints', 'pt', 'pts'],
      'quart': ['quart', 'quarts', 'qt', 'qts'],
      'gallon': ['gallon', 'gallons', 'gal', 'gals'],
      
      // Volume - Metric
      'milliliter': ['milliliter', 'milliliters', 'millilitre', 'millilitres', 'ml', 'mL'],
      'liter': ['liter', 'liters', 'litre', 'litres', 'l', 'L'],
      
      // Weight - US
      'pound': ['pound', 'pounds', 'lb', 'lbs', 'lb.', 'lbs.', '#'],
      'ounce': ['ounce', 'ounces', 'oz', 'oz.', 'ozs'],
      
      // Weight - Metric
      'gram': ['gram', 'grams', 'gramme', 'grammes', 'g', 'gr', 'gm'],
      'kilogram': ['kilogram', 'kilograms', 'kilogramme', 'kilogrammes', 'kg', 'kgs'],
      'milligram': ['milligram', 'milligrams', 'milligramme', 'milligrammes', 'mg', 'mgs'],
      
      // Other common units
      'package': ['package', 'packages', 'pkg', 'pkgs', 'pack', 'packs'],
      'can': ['can', 'cans'],
      'jar': ['jar', 'jars'],
      'bottle': ['bottle', 'bottles', 'btl', 'btls'],
      'box': ['box', 'boxes'],
      'bag': ['bag', 'bags'],
      'clove': ['clove', 'cloves'],
      'piece': ['piece', 'pieces', 'pc', 'pcs'],
      'slice': ['slice', 'slices'],
      'pinch': ['pinch', 'pinches'],
      'dash': ['dash', 'dashes'],
      'bunch': ['bunch', 'bunches'],
      'sprig': ['sprig', 'sprigs'],
      'stalk': ['stalk', 'stalks'],
      'head': ['head', 'heads'],
      'whole': ['whole'],
      'half': ['half', 'halves'],
      'quarter': ['quarter', 'quarters'],
      'third': ['third', 'thirds'],
      'dozen': ['dozen', 'doz', 'dz'],
      'stick': ['stick', 'sticks'],
      'sheet': ['sheet', 'sheets'],
      'envelope': ['envelope', 'envelopes', 'packet', 'packets'],
      'scoop': ['scoop', 'scoops'],
      'handful': ['handful', 'handfuls'],
      'drop': ['drop', 'drops'],
      
      // Size descriptors often used as units
      'small': ['small', 'sm'],
      'medium': ['medium', 'med'],
      'large': ['large', 'lg'],
      'extra large': ['extra large', 'extra-large', 'xl', 'x-large']
    };

    // Create a reverse mapping for quick lookup
    const unitLookup = {};
    for (const [canonical, variations] of Object.entries(unitMappings)) {
      for (const variation of variations) {
        unitLookup[variation.toLowerCase()] = canonical;
      }
    }

    // Build regex pattern for units - include all variations
    const allUnitVariations = Object.values(unitMappings).flat();
    const unitsPattern = allUnitVariations
      .sort((a, b) => b.length - a.length) // Sort by length descending to match longer units first
      .map(unit => unit.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')) // Escape special regex characters
      .join('|');
    
    // Remove parenthetical measurements first (like "(100g)" or "(4 ounces)")
    let cleanedLine = line.replace(/\s*\([^)]*\)/g, '');
    
    // Special handling for ingredients with post-positioned units
    // e.g., "5 garlic cloves" -> qty: 5, unit: cloves, name: garlic
    const postUnitIngredients = {
      'garlic': ['clove', 'cloves', 'head', 'heads', 'bulb', 'bulbs'],
      'ginger': ['inch', 'inches', 'piece', 'pieces', 'knob', 'knobs'],
      'egg': ['whole', 'large', 'medium', 'small', 'yolk', 'yolks', 'white', 'whites'],
      'chicken': ['breast', 'breasts', 'thigh', 'thighs', 'leg', 'legs', 'wing', 'wings', 'piece', 'pieces', 'drumstick', 'drumsticks'],
      'bell pepper': ['whole', 'large', 'medium', 'small', 'half', 'halves'],
      'red capsicum': ['whole', 'large', 'medium', 'small', 'half', 'halves'],  // Alternative name
      'capsicum': ['whole', 'large', 'medium', 'small', 'half', 'halves'],  // Alternative name
      'tomato': ['whole', 'large', 'medium', 'small', 'slice', 'slices'],
      'tomatoes': ['whole', 'large', 'medium', 'small', 'slice', 'slices'],  // Handle plural
      'onion': ['whole', 'large', 'medium', 'small', 'half', 'halves'],
    };
    
    // Check for post-positioned units pattern
    for (const [ingredient, units] of Object.entries(postUnitIngredients)) {
      const postUnitPattern = new RegExp(
        `^([\\d\\s\\-\\/\\.½⅓⅔¼¾⅛⅜⅝⅞]+(?:and\\s+[\\d\\s\\/½⅓⅔¼¾⅛⅜⅝⅞]+)?)\\s+(${ingredient})\\s+(${units.join('|')})\\b(.*)$`,
        'i'
      );
      const postMatch = cleanedLine.match(postUnitPattern);
      if (postMatch) {
        const qty = postMatch[1] ? postMatch[1].trim() : "1";
        const name = postMatch[2].trim();
        const unit = postMatch[3].trim();
        const restParts = postMatch[4] ? postMatch[4].trim() : "";
        
        // Handle suffix
        const parts = restParts.split(',').map(p => p.trim()).filter(p => p);
        const suffix = parts.join(', ');
        
        return {
          qty: parseQuantity(qty),
          unit: unit,
          name: name,
          suffix: suffix,
          original: line,
          parseWarning: null
        };
      }
    }
    
    // Main parsing regex - captures quantity, unit, and everything else
    // Updated to handle various number formats including fractions and decimals
    const mainPattern = new RegExp(
      `^([\\d\\s\\-\\/\\.½⅓⅔¼¾⅛⅜⅝⅞]+(?:and\\s+[\\d\\s\\/½⅓⅔¼¾⅛⅜⅝⅞]+)?)\\s*(?:(${unitsPattern})\\b)?\\s*(.+)$`,
      'i'
    );
    
    const match = cleanedLine.match(mainPattern);
    
    if (match) {
      const qty = match[1] ? match[1].trim() : "1";
      let unit = match[2] ? match[2].trim() : "";
      let rest = match[3] ? match[3].trim() : cleanedLine;
      
      // Keep the original unit - let the matching logic handle variations
      // This preserves what the user typed (e.g., "teaspoon", "tsp", etc.)
      
      // Split the rest by comma to separate ingredient name from suffix
      const parts = rest.split(',').map(p => p.trim());
      const name = parts[0];
      const suffix = parts.slice(1).join(', ');
      
      return {
        qty: parseQuantity(qty),
        unit: unit,
        name: name,
        suffix: suffix,
        original: line,
        parseWarning: null
      };
    }

    // If no pattern matches, treat the whole line as an ingredient name
    return {
      qty: 1,
      unit: "",
      name: cleanedLine,
      suffix: "",
      original: line,
      parseWarning: "Could not parse quantity/unit - defaulting to 1"
    };
  };

  const parseQuantity = (qtyStr) => {
    // Handle ranges (e.g., "2-3")
    if (qtyStr.includes('-')) {
      const parts = qtyStr.split('-');
      return parseFloat(parts[0]);
    }

    // Handle "and" in mixed numbers (e.g., "1 and 1/2")
    qtyStr = qtyStr.replace(/\s+and\s+/i, ' ');

    // Handle mixed numbers (e.g., "1 1/2")
    const mixedMatch = qtyStr.match(/(\d+)\s+(\d+)\/(\d+)/);
    if (mixedMatch) {
      const whole = parseInt(mixedMatch[1]);
      const numerator = parseInt(mixedMatch[2]);
      const denominator = parseInt(mixedMatch[3]);
      return whole + (numerator / denominator);
    }

    // Handle fractions (e.g., "1/2")
    const fractionMatch = qtyStr.match(/(\d+)\/(\d+)/);
    if (fractionMatch) {
      const numerator = parseInt(fractionMatch[1]);
      const denominator = parseInt(fractionMatch[2]);
      return numerator / denominator;
    }

    // Handle decimals and whole numbers
    return parseFloat(qtyStr) || 1;
  };

  // Helper function to match unit against available units for an ingredient
  const matchUnitWithAvailable = (parsedUnit, availableUnits) => {
    if (!parsedUnit || !availableUnits || availableUnits.length === 0) return null;
    
    const normalizeUnit = (unitName) => {
      return unitName
        .toLowerCase()
        .replace(/\(e?s\)$/, '')    // Remove (s) or (es) at the end
        .replace(/e?s$/, '')        // Remove plural 's' or 'es'
        .trim();
    };
    
    const parsedUnitNormalized = normalizeUnit(parsedUnit);
    const parsedUnitLower = parsedUnit.toLowerCase();
    
    // Try exact match first (case-insensitive)
    let matchedUnit = availableUnits.find(unit => {
      return unit.unit_name.toLowerCase() === parsedUnitLower;
    });
    
    if (matchedUnit) return matchedUnit;
    
    // Try to match patterns like "cloves" to "clove(s)" or "clove" to "clove(s)"
    matchedUnit = availableUnits.find(unit => {
      const dbUnitLower = unit.unit_name.toLowerCase();
      
      // Handle (s) and (es) patterns
      if (dbUnitLower.includes('(s)') || dbUnitLower.includes('(es)')) {
        // Extract base form: "clove(s)" -> "clove", "dash(es)" -> "dash"
        const dbBase = dbUnitLower.replace(/\(e?s\)/, '');
        
        // Check if parsed unit matches any form
        const isMatch = parsedUnitLower === dbBase || // "clove" matches "clove(s)"
                       parsedUnitLower === dbBase + 's' || // "cloves" matches "clove(s)"
                       parsedUnitLower === dbBase + 'es' || // "dashes" matches "dash(es)"
                       parsedUnitLower === dbUnitLower; // exact match with parentheses
        
        if (isMatch) {
          console.log(`Matched "${parsedUnit}" to "${unit.unit_name}" via (s)/(es) pattern`);
        }
        return isMatch;
      }
      
      // Also handle reverse case: database has "cloves" but parsed is "clove"
      if (parsedUnitLower.endsWith('s') || parsedUnitLower.endsWith('es')) {
        const parsedBase = parsedUnitNormalized; // Already normalized, removes s/es
        const isMatch = dbUnitLower === parsedBase || // "clove" in db matches "cloves" parsed
                       dbUnitLower === parsedUnitLower; // exact match
        if (isMatch) {
          console.log(`Matched "${parsedUnit}" to "${unit.unit_name}" via reverse plural pattern`);
        }
        return isMatch;
      }
      
      return false;
    });
    
    if (matchedUnit) return matchedUnit;
    
    // Try normalized match (both sides normalized)
    matchedUnit = availableUnits.find(unit => {
      const dbUnitNormalized = normalizeUnit(unit.unit_name);
      return dbUnitNormalized === parsedUnitNormalized;
    });
    
    if (matchedUnit) return matchedUnit;
    
    // Try direct mappings
    matchedUnit = availableUnits.find(unit => {
      const dbUnitLower = unit.unit_name.toLowerCase();
      const dbUnitUpper = unit.unit_name.toUpperCase();
      const parsedLower = parsedUnit.toLowerCase();
      const parsedUpper = parsedUnit.toUpperCase();
      
      // Common abbreviation mappings - check both the parsed unit and the database unit
      const mappings = [
        { 
          patterns: ['teaspoon', 'teaspoons', 'tsp', 'tsps', 't', 'tspn'],
          matches: ['tsp', 'TSP', 'teaspoon', 'teaspoons', 'Tsp']
        },
        { 
          patterns: ['tablespoon', 'tablespoons', 'tbsp', 'tbsps', 'tbs', 'TBS', 'tbl', 'tb', 'T', 'TBSP'],
          matches: ['TBS', 'tbs', 'tbsp', 'TBSP', 'tablespoon', 'tablespoons', 'Tbs', 'Tablespoon']
        },
        { 
          patterns: ['cup', 'cups', 'c', 'C'],
          matches: ['cup', 'cups', 'c', 'C', 'Cup']
        },
        { 
          patterns: ['pound', 'pounds', 'lb', 'lbs', 'lb.', '#'],
          matches: ['lb', 'lbs', 'pound', 'pounds', 'Lb', 'LB']
        },
        { 
          patterns: ['ounce', 'ounces', 'oz', 'oz.', 'ozs'],
          matches: ['oz', 'ounce', 'ounces', 'Oz', 'OZ']
        },
        { 
          patterns: ['fluid ounce', 'fluid ounces', 'fl oz', 'fl. oz', 'floz', 'fl.oz'],
          matches: ['fl oz', 'fluid ounce', 'fluid ounces', 'fl. oz']
        },
        { 
          patterns: ['gram', 'grams', 'g', 'gr', 'gm'],
          matches: ['g', 'gram', 'grams', 'G']
        },
        { 
          patterns: ['kilogram', 'kilograms', 'kg', 'kgs'],
          matches: ['kg', 'kilogram', 'kilograms', 'Kg', 'KG']
        },
        { 
          patterns: ['milliliter', 'milliliters', 'ml', 'mL'],
          matches: ['ml', 'mL', 'milliliter', 'milliliters', 'ML']
        },
        { 
          patterns: ['liter', 'liters', 'l', 'L'],
          matches: ['l', 'L', 'liter', 'liters']
        },
        { 
          patterns: ['pint', 'pints', 'pt', 'pts'],
          matches: ['pt', 'pint', 'pints', 'Pt', 'PT']
        },
        { 
          patterns: ['quart', 'quarts', 'qt', 'qts'],
          matches: ['qt', 'quart', 'quarts', 'Qt', 'QT']
        },
        { 
          patterns: ['gallon', 'gallons', 'gal', 'gals'],
          matches: ['gal', 'gallon', 'gallons', 'Gal', 'GAL']
        },
        // Add common units with (s) pattern
        { 
          patterns: ['clove', 'cloves'],
          matches: ['clove', 'cloves', 'clove(s)', 'Clove(s)']
        },
        { 
          patterns: ['dash', 'dashes'],
          matches: ['dash', 'dashes', 'dash(es)', 'Dash(es)']
        },
        { 
          patterns: ['apple', 'apples'],
          matches: ['apple', 'apples', 'apple(s)', 'Apple(s)']
        },
        { 
          patterns: ['piece', 'pieces', 'pc', 'pcs'],
          matches: ['piece', 'pieces', 'piece(s)', 'Piece(s)', 'pc', 'pcs']
        }
      ];
      
      // Check if parsed unit matches database unit through mappings
      for (const mapping of mappings) {
        // Check if the parsed unit is in the patterns array
        const parsedIsPattern = mapping.patterns.some(pattern => 
          pattern.toLowerCase() === parsedLower || 
          pattern.toUpperCase() === parsedUpper
        );
        
        // Check if the database unit is in the matches array
        const dbIsMatch = mapping.matches.some(match => 
          match.toLowerCase() === dbUnitLower || 
          match.toUpperCase() === dbUnitUpper ||
          match === unit.unit_name
        );
        
        // If both are in the same mapping group, it's a match
        if (parsedIsPattern && dbIsMatch) {
          return true;
        }
      }
      
      return false;
    });
    
    return matchedUnit;
  };
  
  const handleParseIngredients = async () => {
    setIsParsing(true);
    try {
      const lines = importIngredientsText.split('\n').filter(line => line.trim());
      const parsed = [];
      const errors = [];
      const errorsPerLine = {};
      const skippedLines = [];

      for (let index = 0; index < lines.length; index++) {
      const line = lines[index];
      const ingredient = parseIngredientLine(line);
      if (!ingredient) {
        // Track skipped lines (empty lines, etc.)
        if (line.trim()) {
          skippedLines.push({ lineIndex: index, text: line });
        }
        continue;
      }
      
      if (ingredient) {
        // Check if this is a section header
        if (ingredient.isSection) {
          parsed.push({
            ...ingredient,
            lineIndex: index,
            needsReview: false,
            errors: []
          });
          continue;
        }
        
        const lineErrors = [];
        
        // FIRST: Try to find matching ingredient from database using fuzzy matching
        const matchedIngredient = fuzzyMatchIngredient(ingredient.name, ingredients);
        
        // SECOND: If ingredient found, fetch its available units
        let availableUnits = undefined; // Start as undefined to indicate loading state
        let matchedUnit = null;
        
        if (matchedIngredient) {
          // Fetch available units for this specific ingredient
          try {
            const customIngredientFlag = matchedIngredient.value.includes("user") ? 1 : 0;
            const ingredientId = matchedIngredient.value.split(",")[0];
            
            console.log(`Fetching units for ${ingredient.name} (${matchedIngredient.label}):`, {
              ingredientId,
              customIngredientFlag,
              matchedValue: matchedIngredient.value
            });
            
            const response = await fetch(
              `${process.env.REACT_APP_API_URL}/api/ingredient-all-units/${ingredientId}?custom_ingredient=${customIngredientFlag}`,
              {
                credentials: "include",
              }
            );
            
            if (response.ok) {
              availableUnits = await response.json();
              console.log(`Units fetched for ${ingredient.name}:`, availableUnits);
              
              // Ensure availableUnits is an array
              if (!Array.isArray(availableUnits)) {
                console.error(`Invalid response for units - expected array, got:`, availableUnits);
                availableUnits = [];
              }
              
              // Now match the parsed unit against only the available units for this ingredient
              if (ingredient.unit && availableUnits && availableUnits.length > 0) {
                matchedUnit = matchUnitWithAvailable(ingredient.unit, availableUnits);
                console.log(`Unit matching result for "${ingredient.unit}":`, matchedUnit);
              }
            } else {
              console.error(`Failed to fetch units for ${ingredient.name}:`, response.status, response.statusText);
              availableUnits = []; // Explicitly set to empty array on error
            }
          } catch (error) {
            console.error("Error fetching ingredient units:", error);
            availableUnits = []; // Ensure availableUnits is set even on exception
          }
        } else {
          // If no ingredient matched or no unit specified, set availableUnits to empty array
          availableUnits = [];
        }

        if (!matchedIngredient) {
          lineErrors.push(`Ingredient "${ingredient.name}" not found - please select from dropdown`);
          errors.push(`Line ${index + 1}: Ingredient "${ingredient.name}" not found in database`);
        } else if (ingredient.unit && !matchedUnit) {
          if (availableUnits && availableUnits.length > 0) {
            const availableUnitNames = availableUnits.map(u => u.unit_name).join(', ');
            lineErrors.push(`Unit "${ingredient.unit}" not available for ${ingredient.name}. Available units: ${availableUnitNames}`);
            errors.push(`Line ${index + 1}: Unit "${ingredient.unit}" not available for this ingredient`);
          } else {
            lineErrors.push(`No units available for ${ingredient.name}`);
            errors.push(`Line ${index + 1}: No units configured for this ingredient`);
          }
        }
        
        if (ingredient.parseWarning) {
          lineErrors.push(ingredient.parseWarning);
        }
        
        if (lineErrors.length > 0) {
          errorsPerLine[index] = lineErrors;
        }

        parsed.push({
          ...ingredient,
          lineIndex: index,
          unitId: matchedUnit ? matchedUnit.unit_id : null,
          unitName: matchedUnit ? matchedUnit.unit_name : ingredient.unit,
          ingredientId: matchedIngredient ? matchedIngredient.value : null,
          ingredientFound: !!matchedIngredient,
          availableUnits: availableUnits || [], // Store available units for this ingredient
          needsReview: (!matchedUnit && ingredient.unit !== "") || !matchedIngredient,
          errors: lineErrors
        });
      }
    }

      setParsedIngredients(parsed);
      setImportIngredientsError(errors.join('\n'));
      setParseErrors(errorsPerLine);
      setSkippedLines(skippedLines);
    } catch (error) {
      console.error('Error parsing ingredients:', error);
      setImportIngredientsError('An error occurred while parsing ingredients. Please try again.');
    } finally {
      setIsParsing(false);
    }
  };

  const handleParsedIngredientUpdate = async (index, field, value) => {
    const updated = [...parsedIngredients];
    updated[index][field] = value;
    
    // If updating ingredient selection
    if (field === 'ingredientId') {
      const matchedIngredient = ingredients.find(ing => ing.value === value);
      updated[index].name = matchedIngredient ? matchedIngredient.label : updated[index].name;
      updated[index].ingredientFound = !!matchedIngredient;
      
      // Fetch available units for the new ingredient
      if (matchedIngredient) {
        // Set to undefined first to indicate loading state
        updated[index].availableUnits = undefined;
        setParsedIngredients([...updated]); // Update state to show loading
        
        try {
          const customIngredientFlag = value.includes("user") ? 1 : 0;
          const ingredientId = value.split(",")[0];
          
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/ingredient-all-units/${ingredientId}?custom_ingredient=${customIngredientFlag}`,
            {
              credentials: "include",
            }
          );
          
          if (response.ok) {
            const availableUnits = await response.json();
            updated[index].availableUnits = availableUnits;
            
            // Try to match the original unit with the available units
            if (updated[index].unit && availableUnits.length > 0) {
              const matchedUnit = matchUnitWithAvailable(updated[index].unit, availableUnits);
              if (matchedUnit) {
                updated[index].unitId = matchedUnit.unit_id;
                updated[index].unitName = matchedUnit.unit_name;
              } else {
                // Clear unit selection if not found in available units
                updated[index].unitId = null;
              }
            }
          } else {
            updated[index].availableUnits = []; // Set to empty array on error
          }
        } catch (error) {
          console.error("Error fetching ingredient units:", error);
          updated[index].availableUnits = []; // Set to empty array on error
        }
      } else {
        updated[index].availableUnits = [];
        updated[index].unitId = null;
      }
    }
    
    // If updating unit selection
    if (field === 'unitId') {
      const matchedUnit = updated[index].availableUnits?.find(unit => unit.unit_id === value);
      updated[index].unitName = matchedUnit ? matchedUnit.unit_name : '';
    }
    
    // Recalculate needsReview status and errors
    const newErrors = [];
    if (!updated[index].ingredientFound) {
      newErrors.push(`Ingredient "${updated[index].name}" not found - please select from dropdown`);
    } else if (updated[index].unit && !updated[index].unitId && updated[index].availableUnits !== undefined) {
      // Only show unit errors if we've actually loaded the units (not undefined)
      if (updated[index].availableUnits.length > 0) {
        const availableUnitNames = updated[index].availableUnits.map(u => u.unit_name).join(', ');
        newErrors.push(`Unit "${updated[index].unit}" not available. Choose from: ${availableUnitNames}`);
      } else {
        newErrors.push(`No units available for this ingredient`);
      }
    }
    
    updated[index].errors = newErrors;
    updated[index].needsReview = newErrors.length > 0;
    
    // Update parseErrors state
    const newParseErrors = { ...parseErrors };
    if (newErrors.length > 0) {
      newParseErrors[updated[index].lineIndex] = newErrors;
    } else {
      delete newParseErrors[updated[index].lineIndex];
    }
    setParseErrors(newParseErrors);
    
    setParsedIngredients(updated);
  };

  const handleImportIngredientsSave = async () => {
    setIsImporting(true);
    try {
      // Process both sections and valid ingredients
      for (const item of parsedIngredients) {
        if (item.isSection) {
          // Add section header
          try {
            await fetch(`${process.env.REACT_APP_API_URL}/api/add-ingredient-to-recipe`, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                recipe_id: recipeData.id,
                ingredient_id: null,
                custom_ingredient: 0,
                qty: 0,
                suffix: "",
                is_section_header: 1,
                section_name: item.sectionName,
              }),
            });
          } catch (error) {
            console.error(`Error adding section "${item.sectionName}":`, error);
          }
        } else if (item.isTextRow) {
          // Add text row
          try {
            await fetch(`${process.env.REACT_APP_API_URL}/api/add-ingredient-to-recipe`, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                recipe_id: recipeData.id,
                ingredient_id: null,
                custom_ingredient: 0,
                qty: null,
                suffix: "",
                is_section_header: 0,
                is_text_row: 1,
                section_name: item.textContent,
              }),
            });
          } catch (error) {
            console.error(`Error adding text row "${item.textContent}":`, error);
          }
        } else if (item.ingredientFound) {
          // Add ingredient
          const customIngredientFlag = item.ingredientId.includes("user") ? 1 : 0;
          const ingredientId = item.ingredientId.split(",")[0];

          try {
            const response = await fetch(
              `${process.env.REACT_APP_API_URL}/api/ingredient-all-units/${ingredientId}?custom_ingredient=${customIngredientFlag}`,
              {
                credentials: "include",
              }
            );

            if (response.ok) {
              const unitData = await response.json();
              
              // Find the matching unit or use the default
              let selectedUnit = unitData[0]; // Default unit
              if (item.unitId) {
                const matchedUnit = unitData.find(u => u.unit_id === item.unitId);
                if (matchedUnit) {
                  selectedUnit = matchedUnit;
                }
              }

              // Add the ingredient to the recipe
              await fetch(`${process.env.REACT_APP_API_URL}/api/add-ingredient-to-recipe`, {
                method: "POST",
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  recipe_id: recipeData.id,
                  ingredient_id: ingredientId,
                  custom_ingredient: customIngredientFlag,
                  qty: item.qty * selectedUnit.conversion_factor,
                  suffix: item.suffix,
                  old_id: item.ingredientId,
                  calorie_percent: 100,
                  needs_prep: null,
                }),
              });
            }
          } catch (error) {
            console.error(`Error adding ingredient "${item.name}":`, error);
          }
        }
      }

      // Refresh the recipe ingredients
      fetchRecipeIngredients(id);
      handleImportIngredientsClose();
    } catch (error) {
      console.error('Error importing ingredients:', error);
      alert('An error occurred while importing ingredients. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };

  // Import Instructions Functions
  const handleImportInstructionsOpen = () => {
    setShowImportInstructionsModal(true);
    setImportInstructionsText("");
    setParsedInstructions([]);
  };

  const handleImportInstructionsClose = () => {
    setShowImportInstructionsModal(false);
    setImportInstructionsText("");
    setParsedInstructions([]);
    setOriginalParsedInstructions([]);
    setIsParsingInstructions(false);
    setIsImportingInstructions(false);
  };

  const parseInstructions = () => {
    const text = importInstructionsText.trim();
    if (!text) return;
    
    setIsParsingInstructions(true);
    try {

    let instructions = [];
    
    // First, clean up common non-instruction lines
    const lines = text.split('\n');
    const cleanedLines = lines.filter(line => {
      const trimmed = line.trim().toLowerCase();
      // Skip common headers and UI text
      return trimmed && 
        !trimmed.match(/^instructions?:?$/i) &&
        !trimmed.includes('cook mode') &&
        !trimmed.includes('prevent your screen') &&
        !trimmed.includes('watch the helpful video') &&
        !trimmed.includes('read the recipe');
    });
    
    const cleanedText = cleanedLines.join('\n');
    
    // Try to detect numbered instructions first (1., 2., etc. or 1), 2), etc.)
    const numberedPattern = /^(?:\d+[\.\)])\s*/gm;
    const hasNumbers = numberedPattern.test(cleanedText);
    
    if (hasNumbers) {
      // Split by numbered patterns
      const parts = cleanedText.split(/^(?=\d+[\.\)])/gm).filter(part => part.trim());
      instructions = parts.map(part => part.replace(/^\d+[\.\)]\s*/, '').trim());
    } else {
      // Look for instruction patterns - common cooking verbs at the start of sentences
      const instructionStarters = [
        'make', 'heat', 'whisk', 'add', 'pour', 'mix', 'combine', 'stir', 'cook',
        'bake', 'preheat', 'remove', 'place', 'spread', 'refrigerate', 'chill',
        'cover', 'divide', 'assemble', 'serve', 'slice', 'cut', 'chop', 'melt',
        'boil', 'simmer', 'reduce', 'increase', 'decrease', 'strain', 'sift',
        'grease', 'line', 'prepare', 'set', 'let', 'allow', 'cool', 'warm',
        'transfer', 'brush', 'sprinkle', 'garnish', 'drizzle', 'fold', 'beat',
        'cream', 'knead', 'roll', 'shape', 'press', 'flip', 'turn', 'rotate'
      ];
      
      // Create pattern for instruction starters (including "step name:" format)
      const starterPattern = new RegExp(
        `^((?:[A-Z][\\w\\s]+:\\s*)?(?:${instructionStarters.join('|')}))`,
        'gmi'
      );
      
      // Split text into potential instructions
      const sentences = cleanedText.split(/(?<=[.!?])\s+(?=[A-Z])/);
      
      let currentInstruction = '';
      for (const sentence of sentences) {
        const trimmed = sentence.trim();
        
        // Check if this starts a new instruction
        if (starterPattern.test(trimmed) || trimmed.match(/^[A-Z][^:]+:\s*[A-Z]/)) {
          // If we have a current instruction, save it
          if (currentInstruction) {
            instructions.push(currentInstruction.trim());
          }
          currentInstruction = trimmed;
        } else if (currentInstruction) {
          // Continue the current instruction
          currentInstruction += ' ' + trimmed;
        } else {
          // Start a new instruction if we don't have one
          currentInstruction = trimmed;
        }
      }
      
      // Don't forget the last instruction
      if (currentInstruction) {
        instructions.push(currentInstruction.trim());
      }
      
      // If we still don't have good instructions, fall back to paragraph splitting
      if (instructions.length <= 2) {
        instructions = cleanedText.split(/\n\n+/).filter(inst => inst.trim().length > 20);
        
        // If still too few, try single newlines but be more selective
        if (instructions.length <= 2) {
          instructions = cleanedText
            .split(/\n/)
            .filter(inst => {
              const trimmed = inst.trim();
              // Keep lines that are substantial (more than 50 chars) or start with cooking verbs
              return trimmed.length > 50 || starterPattern.test(trimmed);
            });
        }
      }
    }

    // Clean up instructions
    instructions = instructions.map(inst => {
      // Remove bullet points and list markers
      inst = inst.replace(/^[\-\*\•]\s*/, '');
      // Remove step numbers if any remain
      inst = inst.replace(/^\d+[\.\)]\s*/, '');
      // Trim whitespace
      inst = inst.trim();
      return inst;
    }).filter(inst => inst.length > 10); // Filter out very short instructions

    setParsedInstructions(instructions);
    setOriginalParsedInstructions(instructions);
    } catch (error) {
      console.error('Error parsing instructions:', error);
      alert('An error occurred while parsing instructions. Please try again.');
    } finally {
      setIsParsingInstructions(false);
    }
  };

  const handleImportInstructionsSave = async () => {
    setIsImportingInstructions(true);
    try {
      for (const instruction of parsedInstructions) {
        try {
        await fetch(`${process.env.REACT_APP_API_URL}/api/add-instruction-to-recipe`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            recipe_id: recipeData.id,
            instructionStep: instruction,
          }),
        });
      } catch (error) {
        console.error("Error adding instruction:", error);
      }
    }

      // Refresh the recipe instructions
      fetchRecipeInstructions(id);
      handleImportInstructionsClose();
    } catch (error) {
      console.error('Error importing instructions:', error);
      alert('An error occurred while importing instructions. Please try again.');
    } finally {
      setIsImportingInstructions(false);
    }
  };

  useEffect(() => {
    // Fetch ingredients
    isUserRecipeOwner();
  }, [recipeData, currentUser]);

  useEffect(() => {
    // Fetch ingredients
    fetchIngredients();
    fetchRecipeInstructions(id);
    fetchRecipeIngredients(id);
    fetchUserMetricSetting();
  }, []);

  // Handle clicking outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.custom-dropdown-container')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  if (!isLoggedIn) {
    return (
      <h3>
        Please{" "}
        <a
          className="btn btn-primary"
          href={`/signin?returnUrl=${encodeURIComponent(
            window.location.pathname
          )}`}
        >
          Sign In
        </a>{" "}
        to view
      </h3>
    );
  }

  if (!recipeData) {
    return <div>Only the user that created this recipe can edit it.</div>;
  }

  return (
    <>
      <div className={styles.recipeContainer}>
      <div className={styles.chatbotWidget}>
        {closeChat ? (
          <>
            <div className={styles.chatControls}>
              <Button
                variant="primary"
                onClick={toggleCloseChat}
              >
                Close
              </Button>
              <Button
                variant="danger"
                onClick={toggleClearChat}
              >
                Clear Chat
              </Button>
            </div>
            <FullChatBot
              recipeIngredients={recipeIngredients}
              recipeData={recipeData}
              recipeInstructions={recipeInstructions}
            />
          </>
        ) : (
          <div className={styles.chatbotTab} onClick={toggleCloseChat}>
            <FontAwesomeIcon icon={faComments} /> Chat with Remy
          </div>
        )}
      </div>

      <div className={styles.recipeHeader}>
        <div className={styles.headerContent}>
          <Button 
            className={styles.backButton}
            onClick={handleBackClick}
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </Button>
          
          <div className={styles.headerControls}>
            <div className={styles.metricToggle}>
              <Form.Switch
                type="switch"
                id="metric-switch"
                label="Use Metric"
                checked={isMetric}
                onChange={handleToggle}
              />
            </div>
            
            <Button
              className={styles.deleteButton}
              onClick={() => handleDeleteRecipe(id)}
            >
              <FontAwesomeIcon icon={faTrash} /> Delete Recipe
            </Button>
          </div>
        </div>
      </div>
      
      {/* Recipe Title Section */}
      <div className={styles.recipeTitle}>
        <h1>{recipeData.name}</h1>
        <div className={styles.recipeMeta}>
          {recipeCourse && (
            <div className={styles.metaItem}>
              <FontAwesomeIcon icon={faUtensils} />
              <span>{recipeCourse.label}</span>
            </div>
          )}
          <div className={styles.metaItem}>
            <FontAwesomeIcon icon={faClock} />
            <span>{recipeServings} servings</span>
          </div>
          {recipeTags && (
            <div className={styles.metaItem}>
              <FontAwesomeIcon icon={faTag} />
              <span>{recipeTags}</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className={styles.contentGrid}>
        {/* Left Column - Recipe Details Form */}
        <div className={styles.card}>
          <h3><FontAwesomeIcon icon={faListCheck} /> Details</h3>
          
          <div className={styles.recipeDetailsForm}>
            <div className={styles.formGroup} style={{ gridColumn: '1 / -1' }}>
              <label className={styles.formLabel}>Recipe Name:</label>
              <input
                type="text"
                className={styles.formControl}
                value={recipeName}
                onChange={handleRecipeNameChange}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Course:</label>
              <Select
                options={courseOptions}
                isSearchable
                value={recipeCourse}
                onChange={handleRecipeCourseChange}
                menuPlacement="auto"
                menuPosition="fixed"
                styles={{
                  control: (base) => ({
                    ...base,
                    fontSize: '0.85rem',
                    minHeight: '28px'
                  }),
                  menu: (base) => ({
                    ...base,
                    fontSize: '0.85rem'
                  })
                }}
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Servings:</label>
              <input
                type="number"
                className={styles.formControl}
                min="0"
                value={recipeServings}
                onChange={(e) =>
                  handleRecipeServingsChange(parseFloat(e.target.value))
                }
              />
            </div>
            <div className={styles.formGroup} style={{ gridColumn: '1 / -1' }}>
              <label className={styles.formLabel}>Serving Size:</label>
              <input
                type="text"
                className={styles.formControl}
                value={recipeServingSize}
                onChange={handleRecipeServingSizeChange}
              />
            </div>
            <div className={styles.formGroup} style={{ gridColumn: '1 / -1' }}>
              <label className={styles.formLabel}>Tags:</label>
              <TagInput
                value={recipeTags}
                onChange={setRecipeTags}
                placeholder="Add tags (e.g., vegetarian, quick, gluten-free)"
              />
            </div>
            <div className={styles.formGroup} style={{ gridColumn: '1 / -1' }}>
              <label className={styles.formLabel}>Description:</label>
              <textarea
                className={styles.formControl}
                rows="3"
                value={recipeDescription}
                onChange={handleRecipeDescriptionChange}
              />
            </div>
            
            {/* Detailed Nutrition Info */}
            <div className={styles.infoCard} style={{ gridColumn: '1 / -1' }}>
              <div className={styles.infoLabel}>Detailed Nutrition (per serving)</div>
              <div className={styles.infoValue}>
                Protein: {parseFloat(totalProtein / recipeData.servings).toFixed(1)}g |
                Carbs: {parseFloat(totalCarbs / recipeData.servings).toFixed(1)}g |
                Fat: {parseFloat(totalFat / recipeData.servings).toFixed(1)}g |
                Fiber: {parseFloat(totalFiber / recipeData.servings).toFixed(1)}g |
                Sugar: {parseFloat(totalSugar / recipeData.servings).toFixed(1)}g
              </div>
            </div>
            
            <button
              className="btn btn-primary btn-sm"
              onClick={handleUpdateRecipe}
              style={{ gridColumn: '1 / -1' }}
            >
              Save Updates
            </button>
          </div>
        </div>

        {/* Right Column - Image and Stats */}
        <div>
          {/* Recipe Image */}
          <div className={`${styles.card} ${styles.recipeImageContainer}`}>
            {isLoading ? (
              <div className="chat-bubble mt-2">
                <span className="dot"></span>
                <span className="dot"></span>
                <span className="dot"></span>
              </div>
            ) : (
              <>
                {imageUrl ? (
                  <img
                    className={styles.recipeImage}
                    src={imageUrl}
                    alt={recipeData.name}
                  />
                ) : (
                  <div className={styles.noImage}>
                    <div>
                      <FontAwesomeIcon icon={faImage} />
                      <p>No image available</p>
                    </div>
                  </div>
                )}
              </>
            )}
            <div className={styles.imageControls}>
              <label htmlFor="file-upload" className={styles.uploadButton} style={{ margin: 0, textAlign: 'center' }}>
                <FontAwesomeIcon icon={faUpload} /> Upload
              </label>
              <input
                type="file"
                className={styles.fileInput}
                id="file-upload"
                accept="image/*"
                onChange={handleImageUpload}
              />
              <button
                className={`${styles.uploadButton} ${!hasGold ? 'disabled' : ''}`}
                onClick={handleImageUpGenerate}
                disabled={!hasGold}
                style={{ whiteSpace: 'nowrap' }}
              >
                <FontAwesomeIcon icon={faWandMagicSparkles} /> Generate
              </button>
            </div>
          </div>

          {/* Cost and Nutrition Stats */}
          <div className={styles.statsGrid}>
            <div className={`${styles.statCard} ${styles.costCard}`}>
              <h4><FontAwesomeIcon icon={faDollarSign} /> Cost</h4>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Total:</span>
                <span className={styles.statValue}>
                  ${parseFloat(totalPrice).toFixed(2)}
                </span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Per Serving:</span>
                <span className={styles.statValue}>
                  ${parseFloat(totalPrice / recipeData.servings).toFixed(2)}
                </span>
              </div>
            </div>

            <div className={`${styles.statCard} ${styles.nutritionCard}`}>
              <h4><FontAwesomeIcon icon={faChartPie} /> Per Serving</h4>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>Calories:</span>
                <span className={styles.statValue}>
                  {parseFloat(totalCalories / recipeData.servings).toFixed(0)}
                </span>
              </div>
              <div className={styles.statItem}>
                <span className={styles.statLabel}>P/C/F:</span>
                <span className={styles.statValue}>
                  {parseFloat(totalProtein / recipeData.servings).toFixed(0)}/
                  {parseFloat(totalCarbs / recipeData.servings).toFixed(0)}/
                  {parseFloat(totalFat / recipeData.servings).toFixed(0)}g
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notes Section */}
      <div className={styles.notesSection}>
        <div className={styles.notesButtons}>
          <button
            className={`btn btn-outline-secondary ${styles.noteButton} ${
              publicNotesContent !== null && publicNotesContent !== "<p></p>" 
                ? styles.hasContent 
                : ""
            }`}
            onClick={handlePublicNotesOpenModal}
          >
            <FontAwesomeIcon icon={faNoteSticky} /> Recipe Notes
          </button>
          <button
            className={`btn btn-outline-primary ${styles.noteButton} ${
              notesContent !== null && notesContent !== "<p></p>" 
                ? styles.hasContent 
                : ""
            }`}
            onClick={handlePrivateNotesOpenModal}
          >
            <FontAwesomeIcon icon={faNoteSticky} /> My Notes
          </button>
        </div>
      </div>

      {/* Recipe Content - Ingredients and Instructions */}
      <div className={styles.recipeContent}>
        {/* Ingredients Section */}
        <div className={`${styles.card} ${styles.ingredientsSection}`}>
          <div className={styles.sectionHeader}>
            <FontAwesomeIcon icon={faUtensils} />
            <h2>Ingredients {hasUnsavedIngredientOrder && <span className={styles.unsavedIndicator}>*</span>}</h2>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                className={styles.addButton}
                onClick={handleImportIngredientsOpen}
              >
                <FontAwesomeIcon icon={faUpload} style={{ color: 'white' }} /> Import
              </button>
              <button
                className={styles.addButton}
                onClick={handleAddClickModal}
              >
                <FontAwesomeIcon icon={faPlus} style={{ color: 'white' }} /> Create New
              </button>
              <button
                className={styles.addButton}
                onClick={() => setShowIngredientSectionModal(true)}
              >
                <FontAwesomeIcon icon={faHeading} style={{ color: 'white' }} /> Add Section
              </button>
              <button
                className={styles.addButton}
                onClick={() => setShowTextRowModal(true)}
              >
                <FontAwesomeIcon icon={faFileText} style={{ color: 'white' }} /> Add Text Row
              </button>
              {hasUnsavedIngredientOrder && (
                <>
                  <button
                    className={styles.saveButton}
                    onClick={handleSaveIngredientOrder}
                  >
                    Save Order
                  </button>
                  <button
                    className={styles.cancelButton}
                    onClick={handleCancelIngredientOrder}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Add Ingredient:</label>
            <Select
              options={ingredients}
              isSearchable
              onChange={handleChange}
              menuPlacement="auto"
              menuPosition="fixed"
              styles={{
                control: (base) => ({
                  ...base,
                  fontSize: '0.9rem',
                  minHeight: '32px'
                }),
                menu: (base) => ({
                  ...base,
                  fontSize: '0.9rem'
                })
              }}
            />
            <div className="row mt-2">
              <div className="col-4">
                <label className={styles.formLabel}>Qty:</label>
                <input
                  type="number"
                  className={styles.formControl}
                  placeholder="Qty"
                  min="0"
                  value={newQty}
                  onChange={(e) =>
                    handleNewQtyChange(parseFloat(e.target.value))
                  }
                />
              </div>
              <div className="col-5">
                <label className={styles.formLabel}>Unit:</label>
                <Select
                  options={newIngredientConversionsOptions.map((option) => ({
                    value: option.unit_id,
                    label: option.unit_name,
                    conversion_factor: option.conversion_factor,
                  }))}
                  value={{
                    value: newIngredientConversionsOptionsId,
                    label: newIngredientConversionsOptionsName,
                    conversion_factor: newIngredientConversionFactor,
                  }}
                  onChange={(selectedOption) => {
                    setNewIngredientConversionsOptionsId(selectedOption.value);
                    setNewIngredientConversionsOptionsName(
                      selectedOption.label
                    );
                    setNewIngredientConversionFactor(
                      selectedOption.conversion_factor
                    );
                  }}
                  menuPlacement="auto"
                  menuPosition="fixed"
                  isSearchable={true}
                  isDisabled={false}
                  styles={{
                    control: (base) => ({
                      ...base,
                      fontSize: '0.9rem',
                      minHeight: '32px'
                    }),
                    menu: (base) => ({
                      ...base,
                      fontSize: '0.9rem'
                    })
                  }}
                />
              </div>
              <div className="col-3">
                <label className={styles.formLabel}>
                  Calorie %
                    <OverlayTrigger
                      delay={{ hide: 150, show: 150 }}
                      overlay={(props) => (
                        <Tooltip {...props}>
                          Adjust what % of the ingredient's calories are
                          calculated in the total. Useful for items like
                          marinades, frying oil, breaded dishes, etc., where not
                          all calories are consumed.
                        </Tooltip>
                      )}
                      placement="bottom"
                    >
                      <FontAwesomeIcon
                        className="mx-2"
                        icon={faQuestionCircle}
                      />
                    </OverlayTrigger>
                  </label>
                <input
                  type="number"
                  className={styles.formControl}
                  placeholder="100"
                  min="0"
                  value={newCalorie}
                  onChange={(e) =>
                    handleNewCalorieChange(parseFloat(e.target.value))
                  }
                />
              </div>
            </div>
            <div className="row mt-2">
              <div className="col-sm-12">
                <label className={styles.formLabel}>Suffix (optional):</label>
                <input
                  type="text"
                  className={styles.formControl}
                  placeholder="e.g., 'minced', 'at room temp', etc."
                  value={newSuffix}
                  onChange={handleNewSuffixChange}
                />
              </div>
            </div>
            <button
              className="btn btn-primary mt-2 mb-3"
              onClick={handleAddIngredientToRecipe}
            >
              Add Ingredient To Recipe
            </button>
            <div className={styles.ingredientsList}>

              <div className={`${styles.tableWrapper} table-responsive`}>
                <table className="table">
                  {recipeIngredients.length > 0 && (
                    <thead className="text-center">
                      <tr>
                        <th></th>
                        <th>
                          {" "}
                          <OverlayTrigger
                            delay={{ hide: 150, show: 150 }}
                            overlay={(props) => (
                              <Tooltip {...props}>
                                Click an ingredient to edit the ingredient's
                                settings (calories, conversions, etc)
                              </Tooltip>
                            )}
                            placement="bottom"
                          >
                            <FontAwesomeIcon
                              className=""
                              icon={faQuestionCircle}
                            />
                          </OverlayTrigger>
                        </th>
                        <th className="align-text-bottom">
                          <OverlayTrigger
                            delay={{ hide: 150, show: 150 }}
                            overlay={(props) => (
                              <Tooltip {...props}>
                                Use colors to make it easier to read recipes or
                                align colors with relevant instruction steps.
                              </Tooltip>
                            )}
                            placement="bottom"
                          >
                            <FontAwesomeIcon
                              className=""
                              icon={faQuestionCircle}
                            />
                          </OverlayTrigger>
                        </th>
                        <th className="align-text-bottom">
                          <OverlayTrigger
                            delay={{ hide: 150, show: 150 }}
                            overlay={(props) => (
                              <Tooltip {...props}>
                                Don't like how a unit is being displayed? Set it
                                yourself using the below dropdown and it will
                                automatically be converted and saved.
                              </Tooltip>
                            )}
                            placement="bottom"
                          >
                            <FontAwesomeIcon
                              className=""
                              icon={faQuestionCircle}
                            />
                          </OverlayTrigger>
                        </th>
                        <th><OverlayTrigger
                      delay={{ hide: 150, show: 150 }}
                      overlay={(props) => (
                        <Tooltip {...props}>
                          Mark ingredients that need to be prepped (chopped, diced, etc.) before cooking
                        </Tooltip>
                      )}
                      placement="bottom"
                    >
                      <span>Needs Prep <FontAwesomeIcon
                        className=""
                        icon={faQuestionCircle}
                      /></span>
                    </OverlayTrigger></th>
                        <th><OverlayTrigger
                      delay={{ hide: 150, show: 150 }}
                      overlay={(props) => (
                        <Tooltip {...props}>
                          A black "Edit" button means the calorie % has been edited and is not set to 100%.
                        </Tooltip>
                      )}
                      placement="bottom"
                    >
                      <FontAwesomeIcon
                        className="mx-2"
                        icon={faQuestionCircle}
                      />
                    </OverlayTrigger></th>
                        <th></th>
                      </tr>
                    </thead>
                  )}
                  <tbody>
                    {recipeIngredients.map((ingredient, index) => {
                      // Check if this is a section header
                      if (ingredient.is_section_header) {
                        const isDragging = draggedIngredientIndex === index;
                        const isDragOver = dragOverIngredientIndex === index;
                        return (
                          <tr 
                            key={ingredient.id} 
                            className={`${styles.sectionHeaderRow} ${isDragging ? styles.dragging : ''} ${isDragOver ? styles.dragOver : ''}`}
                            draggable={true}
                            onDragStart={(e) => handleIngredientDragStart(e, ingredient, index)}
                            onDragOver={(e) => handleIngredientDragOver(e, index)}
                            onDragEnd={handleIngredientDragEnd}
                            onDrop={(e) => handleIngredientDrop(e, index)}
                          >
                            <td className="text-center">
                              <div className={styles.reorderControls} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span className={styles.dragHandle}>⋮⋮</span>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  <button
                                    className={styles.reorderButton}
                                    onClick={() =>
                                      handleUpArrowClick(
                                        index,
                                        ingredient.id,
                                        ingredient.sort_order
                                      )
                                    }
                                    title="Move up"
                                    disabled={index === 0}
                                    style={{ padding: '2px 6px', margin: '0' }}
                                  >
                                    <FontAwesomeIcon icon={faArrowUp} />
                                  </button>
                                  <button
                                    className={styles.reorderButton}
                                    onClick={() =>
                                      handleDownArrowClick(
                                        index,
                                        ingredient.id,
                                        ingredient.sort_order
                                      )
                                    }
                                    title="Move down"
                                    disabled={index === recipeIngredients.length - 1}
                                    style={{ padding: '2px 6px', margin: '0' }}
                                  >
                                    <FontAwesomeIcon icon={faArrowDown} />
                                  </button>
                                </div>
                              </div>
                            </td>
                            <td style={{ backgroundColor: ingredient.color_tag, fontWeight: 'bold', fontSize: '1.1em', padding: '0.75rem 1rem', borderLeft: '4px solid #6c757d', minWidth: '250px' }}>
                              {ingredient.section_name || "Section Name"}
                            </td>
                            <td className="align-middle">
                              <div className="btn-group">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-secondary dropdown-toggle-2"
                                  data-bs-toggle="dropdown"
                                  aria-haspopup="true"
                                  aria-expanded="false"
                                >
                                  Color
                                </button>
                                  <div className="dropdown-menu">
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "");
                                      }}
                                    >
                                      <span className="color-swatch"></span>
                                      <span>None</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#98F5E1");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-1"></span>
                                      <span>Aqua Blue</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#FBF8CC");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-2"></span>
                                      <span>Pale Yellow</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#FDE4CF");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-3"></span>
                                      <span>Peach</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#90DBF4");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-4"></span>
                                      <span>Sky Blue</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#F1C0E8");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-5"></span>
                                      <span>Pink Lavender</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#8EECF5");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-6"></span>
                                      <span>Baby Blue</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#CFBAF0");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-7"></span>
                                      <span>Mauve</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#B9FBC0");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-8"></span>
                                      <span>Light Green</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#FFCFD2");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-9"></span>
                                      <span>Light Pink</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#A3C4F3");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-10"></span>
                                      <span>Powder Blue</span>
                                    </button>
                                  </div>
                              </div>
                            </td>
                            <td className="align-middle">
                              {/* Empty cell for Set Unit column */}
                            </td>
                            <td className="text-center align-middle">
                              {/* Empty cell for Prep checkbox column */}
                            </td>
                            <td className="align-middle">
                              <div className={styles.actionButtons}>
                                <button
                                  className={styles.editBtn}
                                  onClick={() => handleEditIngredient(ingredient)}
                                >
                                  <FontAwesomeIcon icon={faEdit} /> Edit
                                </button>
                                <button
                                  className={styles.deleteBtn}
                                  onClick={() => handleDeleteIngredient(ingredient)}
                                >
                                  <FontAwesomeIcon icon={faTrash} /> Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      }
                      
                      // Check if this is a text row
                      if (ingredient.is_text_row) {
                        const isDragging = draggedIngredientIndex === index;
                        const isDragOver = dragOverIngredientIndex === index;
                        return (
                          <tr 
                            key={ingredient.id} 
                            className={`${isDragging ? styles.dragging : ''} ${isDragOver ? styles.dragOver : ''}`}
                            draggable={true}
                            onDragStart={(e) => handleIngredientDragStart(e, ingredient, index)}
                            onDragOver={(e) => handleIngredientDragOver(e, index)}
                            onDragEnd={handleIngredientDragEnd}
                            onDrop={(e) => handleIngredientDrop(e, index)}
                          >
                            <td className="text-center">
                              <div className={styles.reorderControls} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span className={styles.dragHandle}>⋮⋮</span>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                  <button
                                    className={styles.reorderButton}
                                    onClick={() =>
                                      handleUpArrowClick(
                                        index,
                                        ingredient.id,
                                        ingredient.sort_order
                                      )
                                    }
                                    title="Move up"
                                    disabled={index === 0}
                                    style={{ padding: '2px 6px', margin: '0' }}
                                  >
                                    <FontAwesomeIcon icon={faArrowUp} />
                                  </button>
                                  <button
                                    className={styles.reorderButton}
                                    onClick={() =>
                                      handleDownArrowClick(
                                        index,
                                        ingredient.id,
                                        ingredient.sort_order
                                      )
                                    }
                                    title="Move down"
                                    disabled={index === recipeIngredients.length - 1}
                                    style={{ padding: '2px 6px', margin: '0' }}
                                  >
                                    <FontAwesomeIcon icon={faArrowDown} />
                                  </button>
                                </div>
                              </div>
                            </td>
                            <td style={{ 
                              backgroundColor: ingredient.color_tag,
                              wordWrap: 'break-word',
                              whiteSpace: 'normal',
                              minWidth: '250px',
                              maxWidth: '400px'
                            }}>
                              <div style={{ padding: '0.5rem' }}>
                                {ingredient.section_name || ""}
                              </div>
                            </td>
                            <td className="align-middle">
                              <div className="btn-group">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-secondary dropdown-toggle-2"
                                  data-bs-toggle="dropdown"
                                  aria-haspopup="true"
                                  aria-expanded="false"
                                >
                                  Color
                                </button>
                                  <div className="dropdown-menu">
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "");
                                      }}
                                    >
                                      <span className="color-swatch"></span>
                                      <span>None</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#98F5E1");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-1"></span>
                                      <span>Aqua Blue</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#FFC6FF");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-2"></span>
                                      <span>Light Purple</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#BDB2FF");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-3"></span>
                                      <span>Lavender</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#A0C4FF");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-4"></span>
                                      <span>Light Blue</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#B9FBC0");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-5"></span>
                                      <span>Mint Green</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#FBFCC0");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-6"></span>
                                      <span>Light Yellow</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#FFD6A5");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-7"></span>
                                      <span>Peach</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#FFADAD");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-8"></span>
                                      <span>Light Red</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#FFCFD2");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-9"></span>
                                      <span>Light Pink</span>
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={() => {
                                        updateIngredientColor(ingredient.id, "#A3C4F3");
                                        setOpenDropdown(null);
                                      }}
                                    >
                                      <span className="color-swatch swatch-10"></span>
                                      <span>Powder Blue</span>
                                    </button>
                                  </div>
                              </div>
                            </td>
                            <td className="align-middle">
                              {/* Empty cell for Set Unit column */}
                            </td>
                            <td className="text-center align-middle">
                              {/* Empty cell for Prep checkbox column */}
                            </td>
                            <td className="align-middle">
                              <div className={styles.actionButtons}>
                                <button
                                  className={styles.editBtn}
                                  onClick={() => handleEditIngredient(ingredient)}
                                >
                                  <FontAwesomeIcon icon={faEdit} /> Edit
                                </button>
                                <button
                                  className={styles.deleteBtn}
                                  onClick={() => handleDeleteIngredient(ingredient)}
                                >
                                  <FontAwesomeIcon icon={faTrash} /> Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      }
                      
                      // Regular ingredient row
                      const isDragging = draggedIngredientIndex === index;
                      const isDragOver = dragOverIngredientIndex === index;
                      return (
                      <tr 
                        key={ingredient.id}
                        className={`${isDragging ? styles.dragging : ''} ${isDragOver ? styles.dragOver : ''}`}
                        draggable={true}
                        onDragStart={(e) => handleIngredientDragStart(e, ingredient, index)}
                        onDragOver={(e) => handleIngredientDragOver(e, index)}
                        onDragEnd={handleIngredientDragEnd}
                        onDrop={(e) => handleIngredientDrop(e, index)}
                      >
                        <td className="text-center">
                          <div className={styles.reorderControls} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span className={styles.dragHandle}>⋮⋮</span>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <button
                                className={styles.reorderButton}
                                onClick={() =>
                                  handleUpArrowClick(
                                    index,
                                    ingredient.id,
                                    ingredient.sort_order
                                  )
                                }
                                title="Move up"
                                disabled={index === 0}
                                style={{ padding: '2px 6px', margin: '0' }}
                              >
                                <FontAwesomeIcon icon={faArrowUp} />
                              </button>
                              <button
                                className={styles.reorderButton}
                                onClick={() =>
                                  handleDownArrowClick(
                                    index,
                                    ingredient.id,
                                    ingredient.sort_order
                                  )
                                }
                                title="Move down"
                                disabled={index === recipeIngredients.length - 1}
                                style={{ padding: '2px 6px', margin: '0' }}
                              >
                                <FontAwesomeIcon icon={faArrowDown} />
                              </button>
                            </div>
                          </div>
                        </td>
                        <td
                          className="align-middle"
                          style={{ backgroundColor: ingredient.color_tag, minWidth: '250px' }}
                        >
                          <b>
                            {(() => {
                              const result = parseFloat(ingredient.qty);
                              let displayValue;

                              if (isMetric && !Number.isInteger(result)) {
                                displayValue = result.toFixed(2);
                              } else if (!isMetric) {
                                if (
                                  (result % 1).toFixed(2) >= 0.31 &&
                                  (result % 1).toFixed(2) <= 0.35
                                ) {
                                  displayValue = new Fraction(
                                    Math.round(3 * result)
                                  )
                                    .div(3)
                                    .toFraction(true);
                                } else if (
                                  (result % 1).toFixed(2) >= 0.65 &&
                                  (result % 1).toFixed(2) <= 0.68
                                ) {
                                  displayValue = new Fraction(
                                    Math.round(3 * result)
                                  )
                                    .div(3)
                                    .toFraction(true);
                                } else {
                                  displayValue = new Fraction(
                                    Math.round(8 * result)
                                  )
                                    .div(8)
                                    .toFraction(true);
                                }
                              } else {
                                displayValue = result;
                              }

                              return <b>{displayValue}</b>;
                            })()}
                          </b>{" "}
                          {ingredient.unit_name}
                          <button
                            className="EditIngredientButton"
                            onClick={() => handleSelectIngredient(ingredient)}
                          >
                            {ingredient.name}
                          </button>{" "}
                          {ingredient.suffix}{" "}
                        </td>

                        <td className="align-middle">
                          <div className="btn-group">
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary dropdown-toggle-2"
                              data-bs-toggle="dropdown"
                              aria-haspopup="true"
                              aria-expanded="false"
                            >
                              Color
                            </button>
                              <div 
                                className="dropdown-menu" 
                                style={{ 
                                  maxHeight: '300px', 
                                  overflowY: 'auto',
                                  minWidth: '150px',
                                  margin: 0
                                }}
                              >
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => {
                                  updateIngredientColor(ingredient.id, "");
                                  setOpenDropdown(null);
                                }}
                              >
                                <span className="color-swatch"></span>
                                <span>None</span>
                              </button>

                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => {
                                  updateIngredientColor(
                                    ingredient.id,
                                    "#98F5E1"
                                  );
                                  setOpenDropdown(null);
                                }}
                              >
                                <span className="color-swatch swatch-1"></span>
                                <span>Aqua Blue</span>
                              </button>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => {
                                  updateIngredientColor(
                                    ingredient.id,
                                    "#FBF8CC"
                                  );
                                  setOpenDropdown(null);
                                }}
                              >
                                <span className="color-swatch swatch-2"></span>
                                <span>Pale Yellow</span>
                              </button>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => {
                                  updateIngredientColor(
                                    ingredient.id,
                                    "#FDE4CF"
                                  );
                                  setOpenDropdown(null);
                                }}
                              >
                                <span className="color-swatch swatch-3"></span>
                                <span>Peach</span>
                              </button>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => {
                                  updateIngredientColor(
                                    ingredient.id,
                                    "#90DBF4"
                                  );
                                  setOpenDropdown(null);
                                }}
                              >
                                <span className="color-swatch swatch-4"></span>
                                <span>Sky Blue</span>
                              </button>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => {
                                  updateIngredientColor(
                                    ingredient.id,
                                    "#F1C0E8"
                                  );
                                  setOpenDropdown(null);
                                }}
                              >
                                <span className="color-swatch swatch-5"></span>
                                <span>Pink Lavender</span>
                              </button>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => {
                                  updateIngredientColor(
                                    ingredient.id,
                                    "#8EECF5"
                                  );
                                  setOpenDropdown(null);
                                }}
                              >
                                <span className="color-swatch swatch-6"></span>
                                <span>Baby Blue</span>
                              </button>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => {
                                  updateIngredientColor(
                                    ingredient.id,
                                    "#CFBAF0"
                                  );
                                  setOpenDropdown(null);
                                }}
                              >
                                <span className="color-swatch swatch-7"></span>
                                <span>Mauve</span>
                              </button>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => {
                                  updateIngredientColor(
                                    ingredient.id,
                                    "#B9FBC0"
                                  );
                                  setOpenDropdown(null);
                                }}
                              >
                                <span className="color-swatch swatch-8"></span>
                                <span>Light Green</span>
                              </button>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => {
                                  updateIngredientColor(
                                    ingredient.id,
                                    "#FFCFD2"
                                  );
                                  setOpenDropdown(null);
                                }}
                              >
                                <span className="color-swatch swatch-9"></span>
                                <span>Light Pink</span>
                              </button>
                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => {
                                  updateIngredientColor(
                                    ingredient.id,
                                    "#A3C4F3"
                                  );
                                  setOpenDropdown(null);
                                }}
                              >
                                <span className="color-swatch swatch-10"></span>
                                <span>Powder Blue</span>
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="align-middle">
                          <div className="btn-group">
                            <button
                              type="button"
                              className={`btn btn-sm ${
                                parseInt(ingredient.override) !== 0
                                  ? "btn-success"
                                  : "btn-secondary"
                              } dropdown-toggle-2`}
                              data-bs-toggle="dropdown"
                              aria-haspopup="true"
                              aria-expanded="false"
                            >
                              Set Unit
                            </button>
                              <div 
                                className="dropdown-menu" 
                                style={{ 
                                  maxHeight: '200px', 
                                  overflowY: 'auto',
                                  minWidth: '150px',
                                  margin: 0
                                }}
                              >
                              <button
                                type="button"
                                className="dropdown-item"
                                key={index}
                                onClick={() => {
                                  handleSetUnitOptionDefault(ingredient.id);
                                  setOpenDropdown(null);
                                }}
                              >
                                <span>Default</span>
                              </button>
                              {ingredient.conversion_options.map(
                                (option, index) => (
                                  <button
                                    type="button"
                                    className="dropdown-item"
                                    key={index}
                                    onClick={() => {
                                      handleSetUnitOption(
                                        ingredient.id,
                                        option.id
                                      );
                                      setOpenDropdown(null);
                                    }}
                                  >
                                    <span>{option.unit_name}</span>
                                  </button>
                                )
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="text-center align-middle">
                          {(() => {
                            const needsPrep = ingredient.needs_prep === 1 || (ingredient.needs_prep === null && ingredient.default_needs_prep === 1);
                            const currentPrepType = ingredient.prep_type || ingredient.default_prep_type || 'chop';
                            
                            if (!needsPrep) {
                              // Show just a checkbox when prep is disabled
                              return (
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={false}
                                  onChange={() => handlePrepFlagToggle(ingredient)}
                                />
                              );
                            }
                            
                            // Show dropdown when prep is enabled
                            return (
                              <div className={styles.prepDropdownContainer}>
                                <button
                                  type="button"
                                  className={`${styles.prepDropdownButton} ${openPrepDropdown === ingredient.id ? styles.active : ''}`}
                                  onClick={() => {
                                    console.log('Prep dropdown button clicked for:', ingredient.id);
                                    setOpenPrepDropdown(openPrepDropdown === ingredient.id ? null : ingredient.id);
                                  }}
                                  ref={el => prepDropdownRefs.current[ingredient.id] = el}
                                  style={{
                                    whiteSpace: 'normal',
                                    wordWrap: 'break-word',
                                    minWidth: '80px'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    className="form-check-input me-2"
                                    checked={true}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handlePrepFlagToggle(ingredient);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <span className={styles.prepTypeLabel}>
                                    {currentPrepType.charAt(0).toUpperCase() + currentPrepType.slice(1)}
                                  </span>
                                  <FontAwesomeIcon icon={faChevronDown} className="ms-1" />
                                </button>
                                <DropdownPortal
                                  targetRef={{ current: prepDropdownRefs.current[ingredient.id] }}
                                  isOpen={openPrepDropdown === ingredient.id}
                                >
                                  <div 
                                    className={`dropdown-menu show ${styles.prepDropdownMenu}`}
                                    onClick={(e) => {
                                      console.log('Dropdown menu clicked');
                                      e.stopPropagation();
                                    }}
                                  >
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('Chop clicked for ingredient:', ingredient.id);
                                        handlePrepTypeChange(ingredient, 'chop');
                                        setOpenPrepDropdown(null);
                                      }}
                                    >
                                      Chop
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('Mix clicked for ingredient:', ingredient.id);
                                        handlePrepTypeChange(ingredient, 'mix');
                                        setOpenPrepDropdown(null);
                                      }}
                                    >
                                      Mix
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('Measure clicked for ingredient:', ingredient.id);
                                        handlePrepTypeChange(ingredient, 'measure');
                                        setOpenPrepDropdown(null);
                                      }}
                                    >
                                      Measure
                                    </button>
                                    <button
                                      type="button"
                                      className="dropdown-item"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('Other clicked for ingredient:', ingredient.id);
                                        handlePrepTypeChange(ingredient, 'other');
                                        setOpenPrepDropdown(null);
                                      }}
                                    >
                                      Other
                                    </button>
                                  </div>
                                </DropdownPortal>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="align-middle">
                          <div className={styles.actionButtons}>
                            <button
                              className={`${styles.editBtn} ${ingredient.ingredient_calorie_percent !== 100 ? styles.editBtnModified : ''}`}
                              onClick={() => handleEditIngredient(ingredient)}
                            >
                              <FontAwesomeIcon icon={faEdit} /> Edit
                            </button>
                            <button
                              className={styles.deleteBtn}
                              onClick={() => handleDeleteIngredient(ingredient)}
                            >
                              <FontAwesomeIcon icon={faTrash} /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
              <div>
                <Modal
                  show={selectedRecipeIngredient !== null}
                  onHide={() => setSelectedRecipeIngredient(null)}
                  centered
                >
                  <Modal.Header closeButton>
                    <Modal.Title>
                      {selectedRecipeIngredient?.is_section_header ? 'Edit Section Header' : 
                       selectedRecipeIngredient?.is_text_row ? 'Edit Text Row' : 
                       'Edit Recipe Ingredient'}
                    </Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <div>
                      {selectedRecipeIngredient?.is_section_header ? (
                        // Section header edit form
                        <div>
                          <label className="mr-2">Section Name:</label>
                          <input
                            type="text"
                            className="form-control"
                            value={editRecipeIngredientName}
                            onChange={(e) => setEditRecipeIngredientName(e.target.value)}
                            placeholder="Enter section name"
                          />
                        </div>
                      ) : selectedRecipeIngredient?.is_text_row ? (
                        // Text row edit form
                        <div>
                          <label className="mr-2">Text Content:</label>
                          <input
                            type="text"
                            className="form-control"
                            value={editRecipeIngredientName}
                            onChange={(e) => setEditRecipeIngredientName(e.target.value)}
                            placeholder="Enter text (e.g., 3 TBS of Special Sauce)"
                          />
                        </div>
                      ) : (
                        // Regular ingredient edit form
                        <div>
                      <label className="mr-2">Ingredient:</label>
                      <Select
                        options={ingredients}
                        value={{
                          value: selectedRecipeIngredientId,
                          label: editRecipeIngredientName,
                        }}
                        onChange={handleEditChange}
                        isSearchable={true}
                        isDisabled={false}
                        onLoad={handleEditChange}
                      />
                      <div className="row mt-2">
                        <div className="col-sm-4">
                          <label className="mr-2">Qty:</label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Qty"
                            min="0"
                            value={editIngredientQty}
                            onChange={(e) =>
                              handleEditQtyChange(parseFloat(e.target.value))
                            }
                          />
                        </div>
                        <div className="col-sm-1">
                          <label className="mr-2"> </label>
                          <p className="text-center fw-bold fs-5">X</p>
                        </div>
                        <div className="col-sm-7">
                          <label className="mr-2">Unit:</label>
                          <Select
                            options={editIngredientConversionsOptions.map(
                              (option) => ({
                                value: option.unit_id,
                                label: option.unit_name,
                                conversion_factor: option.conversion_factor,
                              })
                            )}
                            value={{
                              value: editIngredientConversionsOptionsId,
                              label: editIngredientConversionsOptionsName,
                              conversion_factor: editIngredientConversionFactor,
                            }}
                            onChange={(selectedOption) => {
                              setEditIngredientConversionsOptionsId(
                                selectedOption.value
                              );
                              setEditIngredientConversionsOptionsName(
                                selectedOption.label
                              );
                              setEditIngredientConversionFactor(
                                selectedOption.conversion_factor
                              );
                            }}
                            isSearchable={true}
                            isDisabled={false}
                          />{" "}
                        </div>
                        <div className="row">
                          <div className="col-sm-8">
                            <label className="mr-2">Unit Suffix:</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Enter suffix (eg. 'At room temp', 'minced', 'etc')"
                              value={editIngredientSuffix}
                              onChange={handleEditSuffixChange}
                            />
                          </div>
                          <div className="col-sm-4">
                            <label className="mr-2">
                              Calorie %
                              <OverlayTrigger
                                delay={{ hide: 150, show: 150 }}
                                overlay={(props) => (
                                  <Tooltip {...props}>
                                    Adjust what % of the ingredient's calories
                                    are calculated in the total. Useful for
                                    items like marinades, frying oil, breaded
                                    dishes, etc., where not all calories are
                                    consumed.
                                  </Tooltip>
                                )}
                                placement="bottom"
                              >
                                <FontAwesomeIcon
                                  className="mx-2"
                                  icon={faQuestionCircle}
                                />
                              </OverlayTrigger>
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              placeholder="100%"
                              min="0"
                              value={editIngredientCalorie}
                              onChange={(e) =>
                                handleEditCalorieChange(
                                  parseFloat(e.target.value)
                                )
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                      )}
                    </div>
                  </Modal.Body>
                  <Modal.Footer>
                    <Button
                      variant="primary"
                      onClick={
                        selectedRecipeIngredient?.is_section_header ? handleUpdateSectionHeader :
                        selectedRecipeIngredient?.is_text_row ? handleUpdateTextRow :
                        handleUpdateRecipeIngredient
                      }
                    >
                      Save Changes
                    </Button>
                  </Modal.Footer>
                </Modal>
              </div>
            </div>
          </div>
        </div>
        
        {/* Instructions Section */}
        <div className={`${styles.card} ${styles.instructionsSection}`}>
          <div className={styles.sectionHeader}>
            <FontAwesomeIcon icon={faListCheck} />
            <h2>Instructions {hasUnsavedInstructionOrder && <span className={styles.unsavedIndicator}>*</span>}</h2>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button
                className={styles.addButton}
                onClick={handleImportInstructionsOpen}
              >
                <FontAwesomeIcon icon={faUpload} style={{ color: 'white' }} /> Import
              </button>
              <button
                className={styles.addButton}
                onClick={() => setShowInstructionSectionModal(true)}
              >
                <FontAwesomeIcon icon={faHeading} style={{ color: 'white' }} /> Add Section
              </button>
              {hasUnsavedInstructionOrder && (
                <>
                  <button
                    className={styles.saveButton}
                    onClick={handleSaveInstructionOrder}
                  >
                    Save Order
                  </button>
                  <button
                    className={styles.cancelButton}
                    onClick={handleCancelInstructionOrder}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Add Instruction Step:</label>
            <textarea
              className={styles.formControl}
              value={addInstruction}
              onChange={handleInstructionChange}
              rows="4"
            />
            <button
              className="btn btn-primary mt-2"
              onClick={handleAddInstructionToRecipe}
            >
              Add Instruction Step
            </button>
          </div>
          <div className={styles.instructionsList}>
            <div className="table-responsive" style={{ overflow: "visible" }}>
              <table className="table">
                <tbody>
                  {recipeInstructions.map((instruction, index) => {
                    // Check if this is a section header
                    if (instruction.is_section_header) {
                      const isDragging = draggedInstructionIndex === index;
                      const isDragOver = dragOverInstructionIndex === index;
                      return (
                        <tr 
                          key={instruction.id} 
                          className={`${styles.sectionHeaderRow} ${isDragging ? styles.dragging : ''} ${isDragOver ? styles.dragOver : ''}`}
                          draggable={true}
                          onDragStart={(e) => handleInstructionDragStart(e, instruction, index)}
                          onDragOver={(e) => handleInstructionDragOver(e, index)}
                          onDragEnd={handleInstructionDragEnd}
                          onDrop={(e) => handleInstructionDrop(e, index)}
                        >
                          <td className="text-center">
                            <div className={styles.reorderControls} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span className={styles.dragHandle}>⋮⋮</span>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <button
                                  className={styles.reorderButton}
                                  onClick={() =>
                                    handleInstructionUpArrowClick(
                                      index,
                                      instruction.id,
                                      instruction.sort_order
                                    )
                                  }
                                  title="Move up"
                                  disabled={index === 0}
                                  style={{ padding: '2px 6px', margin: '0' }}
                                >
                                  <FontAwesomeIcon icon={faArrowUp} />
                                </button>
                                <button
                                  className={styles.reorderButton}
                                  onClick={() =>
                                    handleInstructionDownArrowClick(
                                      index,
                                      instruction.id,
                                      instruction.sort_order
                                    )
                                  }
                                  title="Move down"
                                  disabled={index === recipeInstructions.length - 1}
                                  style={{ padding: '2px 6px', margin: '0' }}
                                >
                                  <FontAwesomeIcon icon={faArrowDown} />
                                </button>
                              </div>
                            </div>
                          </td>
                          <td style={{ backgroundColor: instruction.color_tag, fontWeight: 'bold', fontSize: '1.1em', padding: '0.75rem 1rem', borderLeft: '4px solid #6c757d' }}>
                            {instruction.section_name || "Section Name"}
                          </td>
                          <td className="align-middle">
                            <div className="btn-group">
                              <button
                                type="button"
                                className="btn btn-sm btn-secondary dropdown-toggle-2"
                                data-bs-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                              >
                                Color
                              </button>
                              <div className="dropdown-menu">
                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={() =>
                                    updateInstructionColor(instruction.id, "")
                                  }
                                >
                                  <span className="color-swatch"></span>
                                  <span>None</span>
                                </button>
                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={() =>
                                    updateInstructionColor(
                                      instruction.id,
                                      "#98F5E1"
                                    )
                                  }
                                >
                                  <span className="color-swatch swatch-1"></span>
                                  <span>Aqua Blue</span>
                                </button>
                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={() =>
                                    updateInstructionColor(
                                      instruction.id,
                                      "#FBF8CC"
                                    )
                                  }
                                >
                                  <span className="color-swatch swatch-2"></span>
                                  <span>Pale Yellow</span>
                                </button>
                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={() =>
                                    updateInstructionColor(
                                      instruction.id,
                                      "#FDE4CF"
                                    )
                                  }
                                >
                                  <span className="color-swatch swatch-3"></span>
                                  <span>Peach</span>
                                </button>
                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={() =>
                                    updateInstructionColor(
                                      instruction.id,
                                      "#90DBF4"
                                    )
                                  }
                                >
                                  <span className="color-swatch swatch-4"></span>
                                  <span>Sky Blue</span>
                                </button>
                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={() =>
                                    updateInstructionColor(
                                      instruction.id,
                                      "#F1C0E8"
                                    )
                                  }
                                >
                                  <span className="color-swatch swatch-5"></span>
                                  <span>Pink Lavender</span>
                                </button>
                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={() =>
                                    updateInstructionColor(
                                      instruction.id,
                                      "#8EECF5"
                                    )
                                  }
                                >
                                  <span className="color-swatch swatch-6"></span>
                                  <span>Baby Blue</span>
                                </button>
                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={() =>
                                    updateInstructionColor(
                                      instruction.id,
                                      "#CFBAF0"
                                    )
                                  }
                                >
                                  <span className="color-swatch swatch-7"></span>
                                  <span>Mauve</span>
                                </button>
                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={() =>
                                    updateInstructionColor(
                                      instruction.id,
                                      "#B9FBC0"
                                    )
                                  }
                                >
                                  <span className="color-swatch swatch-8"></span>
                                  <span>Light Green</span>
                                </button>
                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={() =>
                                    updateInstructionColor(
                                      instruction.id,
                                      "#FFCFD2"
                                    )
                                  }
                                >
                                  <span className="color-swatch swatch-9"></span>
                                  <span>Light Pink</span>
                                </button>
                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={() =>
                                    updateInstructionColor(
                                      instruction.id,
                                      "#A3C4F3"
                                    )
                                  }
                                >
                                  <span className="color-swatch swatch-10"></span>
                                  <span>Powder Blue</span>
                                </button>
                              </div>
                            </div>
                          </td>
                          <td className="align-middle">
                            <div className={styles.actionButtons}>
                              <button
                                className={styles.editBtn}
                                onClick={() => handleEditInstruction(instruction)}
                              >
                                <FontAwesomeIcon icon={faEdit} /> Edit
                              </button>
                              <button
                                className={styles.deleteBtn}
                                onClick={() => handleDeleteInstruction(instruction)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    
                    // Regular instruction row
                    const isDragging = draggedInstructionIndex === index;
                    const isDragOver = dragOverInstructionIndex === index;
                    return (
                    <tr 
                      key={instruction.id}
                      className={`${isDragging ? styles.dragging : ''} ${isDragOver ? styles.dragOver : ''}`}
                      draggable={true}
                      onDragStart={(e) => handleInstructionDragStart(e, instruction, index)}
                      onDragOver={(e) => handleInstructionDragOver(e, index)}
                      onDragEnd={handleInstructionDragEnd}
                      onDrop={(e) => handleInstructionDrop(e, index)}
                    >
                      <td className="text-center">
                        <div className={styles.reorderControls} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span className={styles.dragHandle}>⋮⋮</span>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <button
                              className={styles.reorderButton}
                              onClick={() =>
                                handleInstructionUpArrowClick(
                                  index,
                                  instruction.id,
                                  instruction.sort_order
                                )
                              }
                              title="Move up"
                              disabled={index === 0}
                              style={{ padding: '2px 6px', margin: '0' }}
                            >
                              <FontAwesomeIcon icon={faArrowUp} />
                            </button>
                            <button
                              className={styles.reorderButton}
                              onClick={() =>
                                handleInstructionDownArrowClick(
                                  index,
                                  instruction.id,
                                  instruction.sort_order
                                )
                              }
                              title="Move down"
                              disabled={index === recipeInstructions.length - 1}
                              style={{ padding: '2px 6px', margin: '0' }}
                            >
                              <FontAwesomeIcon icon={faArrowDown} />
                            </button>
                          </div>
                        </div>
                      </td>
                      <td
                        className="align-middle"
                        style={{ backgroundColor: instruction.color_tag }}
                      >
                        {instruction.instruction}{" "}
                      </td>
                      <td className="align-middle">
                        <div className="btn-group">
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary dropdown-toggle-2"
                            data-bs-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                          >
                            Color
                          </button>
                          <div className="dropdown-menu">
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() =>
                                updateInstructionColor(instruction.id, "")
                              }
                            >
                              <span className="color-swatch"></span>
                              <span>None</span>
                            </button>

                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() =>
                                updateInstructionColor(
                                  instruction.id,
                                  "#98F5E1"
                                )
                              }
                            >
                              <span className="color-swatch swatch-1"></span>
                              <span>Aqua Blue</span>
                            </button>
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() =>
                                updateInstructionColor(
                                  instruction.id,
                                  "#FBF8CC"
                                )
                              }
                            >
                              <span className="color-swatch swatch-2"></span>
                              <span>Pale Yellow</span>
                            </button>
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() =>
                                updateInstructionColor(
                                  instruction.id,
                                  "#FDE4CF"
                                )
                              }
                            >
                              <span className="color-swatch swatch-3"></span>
                              <span>Peach</span>
                            </button>
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() =>
                                updateInstructionColor(
                                  instruction.id,
                                  "#90DBF4"
                                )
                              }
                            >
                              <span className="color-swatch swatch-4"></span>
                              <span>Sky Blue</span>
                            </button>
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() =>
                                updateInstructionColor(
                                  instruction.id,
                                  "#F1C0E8"
                                )
                              }
                            >
                              <span className="color-swatch swatch-5"></span>
                              <span>Pink Lavender</span>
                            </button>
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() =>
                                updateInstructionColor(
                                  instruction.id,
                                  "#8EECF5"
                                )
                              }
                            >
                              <span className="color-swatch swatch-6"></span>
                              <span>Baby Blue</span>
                            </button>
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() =>
                                updateInstructionColor(
                                  instruction.id,
                                  "#CFBAF0"
                                )
                              }
                            >
                              <span className="color-swatch swatch-7"></span>
                              <span>Mauve</span>
                            </button>
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() =>
                                updateInstructionColor(
                                  instruction.id,
                                  "#B9FBC0"
                                )
                              }
                            >
                              <span className="color-swatch swatch-8"></span>
                              <span>Light Green</span>
                            </button>
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() =>
                                updateInstructionColor(
                                  instruction.id,
                                  "#FFCFD2"
                                )
                              }
                            >
                              <span className="color-swatch swatch-9"></span>
                              <span>Light Pink</span>
                            </button>
                            <button
                              type="button"
                              className="dropdown-item"
                              onClick={() =>
                                updateInstructionColor(
                                  instruction.id,
                                  "#A3C4F3"
                                )
                              }
                            >
                              <span className="color-swatch swatch-10"></span>
                              <span>Powder Blue</span>
                            </button>
                          </div>
                        </div>
                      </td>
                      <td className="align-middle">
                        <div className={styles.actionButtons}>
                          <button
                            className={styles.editBtn}
                            onClick={() => handleEditInstruction(instruction)}
                          >
                            <FontAwesomeIcon icon={faEdit} /> Edit
                          </button>
                          <button
                            className={styles.deleteBtn}
                            onClick={() => handleDeleteInstruction(instruction)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
      
    <Modal
              show={selectedInstruction !== null}
              onHide={() => setSelectedInstruction(null)}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>
                  {selectedInstruction?.is_section_header ? 'Edit Section Header' : 'Edit Instruction'}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div>
                  {selectedInstruction?.is_section_header ? (
                    <>
                      <label className="mr-2">Section Name:</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editInstructionStep}
                        onChange={handleEditInstructionChange}
                        placeholder="Enter section name"
                      />
                    </>
                  ) : (
                    <textarea
                      className="form-control mb-2"
                      value={editInstructionStep}
                      onChange={handleEditInstructionChange}
                      rows="6"
                    />
                  )}
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="primary"
                  onClick={selectedInstruction?.is_section_header ? handleUpdateInstructionSectionHeader : handleUpdateRecipeInstruction}
                >
                  Save Changes
                </Button>
              </Modal.Footer>
            </Modal>
            {/* -------------------- */}
            {/* Add Ingredient Modal */}
            {/* -------------------- */}
            <Modal show={showNewModal} onHide={handleAddCloseModal} size="lg" className="ingredient-modal">
              <Modal.Header closeButton>
                <Modal.Title>Add New Ingredient</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Tabs 
                  defaultActiveKey="basic" 
                  className="mb-3"
                  style={{ 
                    '--bs-nav-tabs-link-active-color': '#212529',
                    '--bs-nav-tabs-link-active-bg': '#fff',
                    '--bs-nav-link-color': '#495057',
                    '--bs-nav-link-hover-color': '#212529'
                  }}
                >
                  <Tab eventKey="basic" title="Basic Information">
                    <div className="section-header mb-3">
                      <p className="text-muted small mb-0">Required fields for ingredient identification</p>
                    </div>
                    <Alert variant="info" className="mb-3">
                      <i className="fas fa-info-circle me-2"></i>
                      <strong>Important:</strong> Once an ingredient is saved, its base unit cannot be changed. Choose carefully!
                    </Alert>
                    <div className="basic-info-section">
                      <div className="mb-3">
                        <label htmlFor="add-ingredient-name" className="form-label">
                          Ingredient Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter new ingredient name"
                          value={newIngredientName}
                          onChange={handleNewIngredientNameChange}
                          maxLength={100}
                        />
                        <Form.Text className="text-muted">
                          {newIngredientName.length}/100 characters
                        </Form.Text>
                      </div>
                      <div className="row">
                        <div className="mb-3 col-md-6">
                          <label htmlFor="add-base-unit" className="form-label">
                            Base Unit of Measurement <span className="text-danger">*</span>
                            <OverlayTrigger
                              placement="right"
                              overlay={
                                <Tooltip>
                                  The primary unit for storing this ingredient. This cannot be changed after creation.
                                </Tooltip>
                              }
                            >
                              <FontAwesomeIcon icon={faQuestionCircle} className="ms-1 text-muted" />
                            </OverlayTrigger>
                          </label>
                          <div className="d-flex gap-2">
                            <div className="flex-grow-1">
                              <Select
                                options={unitOptions}
                                value={unitOptions.find(
                                  (unit) => unit.unit_id === newUnitOfMeasurement
                                )}
                                onChange={(selectedOption) => {
                                  if (selectedOption.unit_id.startsWith("D")) {
                                    const unitId = parseInt(selectedOption.unit_id.substring(1));
                                    setNewUnitOfMeasurement(unitId);
                                    SetNewUnitOfMeasurementLabel(selectedOption.unit_name);
                                    setNewCustomUnitFlag(0);
                                    // Set pricing unit to match base unit by default
                                    setNewPricingUnit(unitId);
                                    setNewPricingUnitLabel(selectedOption.unit_name);
                                  } else {
                                    setNewUnitOfMeasurement(selectedOption.unit_id);
                                    SetNewUnitOfMeasurementLabel(selectedOption.unit_name);
                                    setNewCustomUnitFlag(1);
                                    // Set pricing unit to match base unit by default
                                    setNewPricingUnit(selectedOption.unit_id);
                                    setNewPricingUnitLabel(selectedOption.unit_name);
                                  }
                                }}
                                getOptionLabel={(option) => option.unit_name}
                                getOptionValue={(option) => option.unit_id}
                                isSearchable={true}
                                placeholder="Select a unit..."
                              />
                            </div>
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={() => setShowQuickUnitModal(true)}
                              style={{ height: '38px' }}
                            >
                              <FontAwesomeIcon icon={faPlus} className="me-1" /> New
                            </Button>
                          </div>
                          <Form.Text className="text-warning">
                            <i className="fas fa-exclamation-triangle"></i> Cannot be changed after saving
                          </Form.Text>
                        </div>

                        <div className="mb-3 col-md-6">
                          <label htmlFor="add-shopping-aisle" className="form-label">
                            Shopping Aisle
                            <OverlayTrigger
                              placement="right"
                              overlay={
                                <Tooltip>
                                  Where to find this ingredient in the store. Helps organize your shopping list.
                                </Tooltip>
                              }
                            >
                              <FontAwesomeIcon icon={faQuestionCircle} className="ms-1 text-muted" />
                            </OverlayTrigger>
                          </label>
                          <Select
                            options={shoppingOptions}
                            value={shoppingOptions.find(
                              (aisle) => aisle.id === newShoppingAisle
                            )}
                            onChange={(selectedOption) => {
                              setNewShoppingAisle(selectedOption.id);
                            }}
                            getOptionLabel={(option) => option.aisle_name}
                            getOptionValue={(option) => option.id}
                            isSearchable={true}
                            placeholder="Select an aisle..."
                          />
                        </div>
                      </div>
                      <div className="form-check mt-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="add-default-needs-prep"
                          checked={newDefaultNeedsPrep}
                          onChange={(e) => setNewDefaultNeedsPrep(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="add-default-needs-prep">
                          Typically needs to be prepped (chopped, diced, etc.)
                          <OverlayTrigger
                            placement="right"
                            overlay={
                              <Tooltip>
                                Check this if the ingredient typically requires preparation before use in recipes.
                              </Tooltip>
                            }
                          >
                            <FontAwesomeIcon icon={faQuestionCircle} className="ms-2 text-muted" />
                          </OverlayTrigger>
                        </label>
                      </div>
                    </div>
                  </Tab>
                  
                  <Tab eventKey="nutrition" title="Nutrition">
                    <div className="section-header mb-3">
                      <p className="text-muted small mb-0">Nutritional information per base unit</p>
                    </div>
                    <div className="nutrition-grid">
                      <div className="row g-3">
                        <div className="col-md-4">
                          <div className="p-3 bg-light rounded">
                            <label htmlFor="add-new-protein" className="form-label small mb-1">
                              <i className="fas fa-drumstick-bite me-1 text-primary"></i> Protein
                            </label>
                            <div className="input-group">
                              <input
                                type="number"
                                className="form-control"
                                id="add-new-protein"
                                min="0"
                                step="0.1"
                                value={newProtein}
                                onChange={(e) => handleNewProteinChange(parseFloat(e.target.value))}
                              />
                              <span className="input-group-text">g</span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="p-3 bg-light rounded">
                            <label htmlFor="add-new-carbs" className="form-label small mb-1">
                              <i className="fas fa-bread-slice me-1 text-warning"></i> Carbs
                            </label>
                            <div className="input-group">
                              <input
                                type="number"
                                className="form-control"
                                id="add-new-carbs"
                                min="0"
                                step="0.1"
                                value={newCarbs}
                                onChange={(e) => handleNewCarbsChange(parseFloat(e.target.value))}
                              />
                              <span className="input-group-text">g</span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="p-3 bg-light rounded">
                            <label htmlFor="add-new-fat" className="form-label small mb-1">
                              <i className="fas fa-cheese me-1 text-success"></i> Fat
                            </label>
                            <div className="input-group">
                              <input
                                type="number"
                                className="form-control"
                                id="add-new-fat"
                                min="0"
                                step="0.1"
                                value={newFat}
                                onChange={(e) => handleNewFatChange(parseFloat(e.target.value))}
                              />
                              <span className="input-group-text">g</span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="p-3 bg-light rounded">
                            <label htmlFor="add-new-fiber" className="form-label small mb-1">
                              <i className="fas fa-seedling me-1 text-info"></i> Fiber
                            </label>
                            <div className="input-group">
                              <input
                                type="number"
                                className="form-control"
                                id="add-new-fiber"
                                min="0"
                                step="0.1"
                                value={newFiber}
                                onChange={(e) => handleNewFiberChange(parseFloat(e.target.value))}
                              />
                              <span className="input-group-text">g</span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="p-3 bg-light rounded">
                            <label htmlFor="add-new-sugar" className="form-label small mb-1">
                              <i className="fas fa-candy-cane me-1 text-danger"></i> Sugar
                            </label>
                            <div className="input-group">
                              <input
                                type="number"
                                className="form-control"
                                id="add-new-sugar"
                                min="0"
                                step="0.1"
                                value={newSugar}
                                onChange={(e) => handleNewSugarChange(parseFloat(e.target.value))}
                              />
                              <span className="input-group-text">g</span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="p-3 bg-light rounded">
                            <label className="form-label small mb-1">
                              <i className="fas fa-fire me-1 text-secondary"></i> Calories
                            </label>
                            <div className="form-control-plaintext">
                              <strong>{calculateCalories(newProtein, newCarbs, newFat).toFixed(1)}</strong> cal
                            </div>
                            <small className="text-muted">Auto-calculated</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Tab>
                  
                  <Tab eventKey="pricing" title="Pricing">
                    <div className="section-header mb-3">
                      <p className="text-muted small mb-0">Set pricing information for accurate cost calculations</p>
                    </div>
                    <div className="price-calculator p-3 bg-light rounded">
                      <h5 className="mb-3">Price Calculator</h5>
                      <div className="row g-3">
                        <div className="col-12">
                          <label className="form-label small mb-1">
                            Pricing Unit
                            <OverlayTrigger
                              placement="right"
                              overlay={
                                <Tooltip>
                                  Choose which unit you want to use for entering the store price. The system will automatically convert to the base unit.
                                </Tooltip>
                              }
                            >
                              <FontAwesomeIcon icon={faQuestionCircle} className="ms-1 text-muted" />
                            </OverlayTrigger>
                          </label>
                          <Select
                            options={unitOptions.filter(
                              (unit) => {
                                // If base unit is selected, include it and any units that have conversions
                                if (newUnitOfMeasurement) {
                                  // Always include the base unit
                                  if ((unit.unit_id.startsWith("D") && parseInt(unit.unit_id.substring(1)) === newUnitOfMeasurement) ||
                                      (unit.unit_id === newUnitOfMeasurement)) {
                                    return true;
                                  }
                                  // Include units that have conversions for this ingredient
                                  // For new ingredients, we won't have conversions yet, so only show base unit
                                  return false;
                                }
                                return false;
                              }
                            )}
                            value={unitOptions.find(
                              (unit) => {
                                if (newPricingUnit) {
                                  return (unit.unit_id.startsWith("D") && parseInt(unit.unit_id.substring(1)) === newPricingUnit) ||
                                         unit.unit_id === newPricingUnit;
                                }
                                return false;
                              }
                            )}
                            onChange={(selectedOption) => {
                              if (selectedOption.unit_id.startsWith("D")) {
                                setNewPricingUnit(parseInt(selectedOption.unit_id.substring(1)));
                                setNewPricingUnitLabel(selectedOption.unit_name);
                              } else {
                                setNewPricingUnit(selectedOption.unit_id);
                                setNewPricingUnitLabel(selectedOption.unit_name);
                              }
                            }}
                            getOptionLabel={(option) => option.unit_name}
                            getOptionValue={(option) => option.unit_id}
                            isSearchable={true}
                            placeholder="Select pricing unit..."
                          />
                        </div>
                        <div className="col-6">
                          <label htmlFor="add-store-amount" className="form-label small mb-1">
                            Package Size
                          </label>
                          <div className="input-group">
                            <input
                              type="number"
                              className="form-control"
                              placeholder="e.g., 12"
                              min="0"
                              step="0.01"
                              value={newStoreAmount}
                              onChange={handleNewStoreBaseAmountChange}
                            />
                            <span className="input-group-text">{newPricingUnitLabel || newUnitOfMeasurementLabel}</span>
                          </div>
                        </div>
                        <div className="col-6">
                          <label htmlFor="add-store-price" className="form-label small mb-1">
                            Store Price
                          </label>
                          <div className="input-group">
                            <span className="input-group-text">$</span>
                            <input
                              type="number"
                              className="form-control"
                              placeholder="4.99"
                              min="0"
                              step="0.01"
                              value={newPrice}
                              onChange={handleNewStorePriceAmountChange}
                            />
                          </div>
                        </div>
                      </div>
                      {newStoreAmount > 0 && newPrice > 0 && (
                        <div className="mt-3 p-3 bg-white rounded border">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>Price per {newPricingUnitLabel || newUnitOfMeasurementLabel}:</strong>
                              <div className="text-muted small">Store price ÷ Package size</div>
                            </div>
                            <div className="text-end">
                              <h5 className="mb-0 text-primary">${(newPrice / newStoreAmount).toFixed(2)}</h5>
                            </div>
                          </div>
                          {newPricingUnit !== newUnitOfMeasurement && (
                            <div className="mt-2 pt-2 border-top">
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <strong>Price per {newUnitOfMeasurementLabel}:</strong>
                                  <div className="text-muted small">Converted to base unit</div>
                                </div>
                                <div className="text-end">
                                  <h5 className="mb-0 text-success">${newCalculatedPrice.toFixed(2)}</h5>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Tab>
                </Tabs>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleAddCloseModal}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleAddIngredient}>
                  <i className="fas fa-save me-2"></i>
                  Save Ingredient
                </Button>
              </Modal.Footer>
            </Modal>

            {/* QuickUnitCreator Modal */}
            <QuickUnitCreator
              show={showQuickUnitModal}
              onHide={() => setShowQuickUnitModal(false)}
              onUnitCreated={(newUnit) => {
                // Refresh units and select the new unit
                fetch(`${process.env.REACT_APP_API_URL}/units`, {
                  credentials: "include",
                })
                  .then((response) => response.json())
                  .then((data) => setUnitOptions(data))
                  .catch((error) => console.error("Error retrieving units:", error));
                
                // Set the new unit as selected for base unit
                if (newUnit.custom_unit_flag === 1) {
                  setNewUnitOfMeasurement(newUnit.unit_id);
                  SetNewUnitOfMeasurementLabel(newUnit.unit_name);
                  setNewCustomUnitFlag(1);
                  // Also set as pricing unit
                  setNewPricingUnit(newUnit.unit_id);
                  setNewPricingUnitLabel(newUnit.unit_name);
                } else {
                  setNewUnitOfMeasurement(newUnit.unit_id);
                  SetNewUnitOfMeasurementLabel(newUnit.unit_name);
                  setNewCustomUnitFlag(0);
                  // Also set as pricing unit
                  setNewPricingUnit(newUnit.unit_id);
                  setNewPricingUnitLabel(newUnit.unit_name);
                }
              }}
              defaultType="metric"
            />

            {/* --------------------- */}
            {/* Edit Ingredient Modal */}
            {/* --------------------- */}
            <Modal
              show={selectedIngredient !== null}
              onHide={() => setSelectedIngredient(null)}
              size="lg"
              className="ingredient-modal"
            >
              <Modal.Header closeButton>
                <Modal.Title>
                  Edit Ingredient
                  {selectedIngredient && !selectedIngredient.id.includes("user") && (
                    <span className="badge bg-info ms-2">System Ingredient</span>
                  )}
                  {selectedIngredient && selectedIngredient.id.includes("user") && (
                    <span className="badge bg-success ms-2">Custom Ingredient</span>
                  )}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Tabs 
                  defaultActiveKey="basic" 
                  className="mb-3"
                  style={{ 
                    '--bs-nav-tabs-link-active-color': '#212529',
                    '--bs-nav-tabs-link-active-bg': '#fff',
                    '--bs-nav-link-color': '#495057',
                    '--bs-nav-link-hover-color': '#212529'
                  }}
                >
                  <Tab eventKey="basic" title="Basic Information">
                    <div className="section-header mb-3">
                      <p className="text-muted small mb-0">Edit ingredient details and settings</p>
                    </div>
                    {selectedIngredient && !selectedIngredient.id.includes("user") && (
                      <Alert variant="info" className="mb-3">
                        <i className="fas fa-info-circle me-2"></i>
                        System ingredients have limited editing capabilities. You can customize pricing and shopping information.
                      </Alert>
                    )}
                    <div className="basic-info-section">
                      <div className="mb-3">
                        <label htmlFor="edit-ingredient-name" className="form-label">
                          Ingredient Name
                          {selectedIngredient && !selectedIngredient.id.includes("user") && (
                            <FontAwesomeIcon icon={faLock} className="ms-2 text-muted" />
                          )}
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={editIngredientName}
                          onChange={handleEditIngredientNameChange}
                          disabled={
                            !selectedIngredient ||
                            !selectedIngredient.id.includes("user")
                          }
                          maxLength={100}
                        />
                        {selectedIngredient && selectedIngredient.id.includes("user") && (
                          <Form.Text className="text-muted">
                            {editIngredientName.length}/100 characters
                          </Form.Text>
                        )}
                        {selectedIngredient && !selectedIngredient.id.includes("user") && (
                          <Form.Text className="text-muted">
                            <i className="fas fa-lock"></i> System ingredients cannot be renamed
                          </Form.Text>
                        )}
                      </div>
                      <div className="row">
                        <div className="mb-3 col-md-6">
                          <label htmlFor="edit-base-unit" className="form-label">
                            Base Unit of Measurement
                            <FontAwesomeIcon icon={faLock} className="ms-2 text-muted" />
                            <OverlayTrigger
                              placement="right"
                              overlay={
                                <Tooltip>
                                  The base unit cannot be changed after an ingredient is created. This ensures recipe consistency.
                                </Tooltip>
                              }
                            >
                              <FontAwesomeIcon icon={faQuestionCircle} className="ms-1 text-muted" />
                            </OverlayTrigger>
                          </label>
                          <Select
                            options={unitOptions}
                            value={{
                              unit_id: editUnitOfMeasurementId,
                              unit_name: editUnitOfMeasurement,
                            }}
                            onChange={(selectedOption) => {
                              if (selectedOption.unit_id.startsWith("D")) {
                                setEditUnitOfMeasurementId(
                                  parseInt(selectedOption.unit_id.substring(1))
                                );
                                setEditUnitOfMeasurement(selectedOption.unit_name);
                                setEditCustomUnitFlag(0);
                              } else {
                                setEditUnitOfMeasurementId(selectedOption.unit_id);
                                setEditUnitOfMeasurement(selectedOption.unit_name);
                                setEditCustomUnitFlag(1);
                              }
                            }}
                            getOptionLabel={(option) => option.unit_name}
                            getOptionValue={(option) => option.unit_id}
                            isSearchable={true}
                            isDisabled={true}
                          />
                          <Form.Text className="text-warning">
                            <i className="fas fa-lock"></i> Base unit is permanent and cannot be changed
                          </Form.Text>
                        </div>
                        <div className="mb-3 col-md-6">
                          <label htmlFor="edit-shopping-aisle" className="form-label">
                            Shopping Aisle
                          </label>
                          <Select
                            options={shoppingOptions}
                            value={{
                              aisle_name: editShoppingAisle,
                              id: editShoppingAisleId,
                            }}
                            onChange={(selectedOption) => {
                              setEditShoppingAisleId(selectedOption.id);
                              setEditShoppingAisle(selectedOption.aisle_name);
                            }}
                            getOptionLabel={(option) => option.aisle_name}
                            getOptionValue={(option) => option.id}
                            isSearchable={true}
                          />
                        </div>
                      </div>
                      <div className="form-check mt-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="edit-default-needs-prep"
                          checked={editDefaultNeedsPrep}
                          onChange={(e) => setEditDefaultNeedsPrep(e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor="edit-default-needs-prep">
                          Typically needs to be prepped (chopped, diced, etc.)
                          <OverlayTrigger
                            placement="right"
                            overlay={
                              <Tooltip>
                                Check this if the ingredient typically requires preparation before use in recipes.
                              </Tooltip>
                            }
                          >
                            <FontAwesomeIcon icon={faQuestionCircle} className="ms-2 text-muted" />
                          </OverlayTrigger>
                        </label>
                      </div>
                    </div>
                  </Tab>
                  
                  <Tab eventKey="nutrition" title="Nutrition">
                    <div className="section-header mb-3">
                      <p className="text-muted small mb-0">Nutritional information per base unit</p>
                    </div>
                    {selectedIngredient && !selectedIngredient.id.includes("user") && (
                      <Alert variant="info" className="mb-3">
                        <i className="fas fa-info-circle me-2"></i>
                        Nutrition values are read-only for system ingredients.
                      </Alert>
                    )}
                    <div className="nutrition-grid">
                      <div className="row g-3">
                        <div className="col-md-4">
                          <div className="p-3 bg-light rounded">
                            <label htmlFor="edit-new-protein" className="form-label small mb-1">
                              <i className="fas fa-drumstick-bite me-1 text-primary"></i> Protein
                              {selectedIngredient && !selectedIngredient.id.includes("user") && (
                                <FontAwesomeIcon icon={faLock} className="ms-2 text-muted" />
                              )}
                            </label>
                            <div className="input-group">
                              <input
                                type="number"
                                className="form-control"
                                id="edit-new-protein"
                                min="0"
                                step="0.1"
                                value={editProtein !== null ? editProtein : ""}
                                onChange={(e) => handleEditProteinChange(parseFloat(e.target.value))}
                                disabled={
                                  !selectedIngredient ||
                                  !selectedIngredient.id.includes("user")
                                }
                              />
                              <span className="input-group-text">g</span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="p-3 bg-light rounded">
                            <label htmlFor="edit-new-carbs" className="form-label small mb-1">
                              <i className="fas fa-bread-slice me-1 text-warning"></i> Carbs
                              {selectedIngredient && !selectedIngredient.id.includes("user") && (
                                <FontAwesomeIcon icon={faLock} className="ms-2 text-muted" />
                              )}
                            </label>
                            <div className="input-group">
                              <input
                                type="number"
                                className="form-control"
                                id="edit-new-carbs"
                                min="0"
                                step="0.1"
                                value={editCarbs !== null ? editCarbs : ""}
                                onChange={(e) => handleEditCarbsChange(parseFloat(e.target.value))}
                                disabled={
                                  !selectedIngredient ||
                                  !selectedIngredient.id.includes("user")
                                }
                              />
                              <span className="input-group-text">g</span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="p-3 bg-light rounded">
                            <label htmlFor="edit-new-fat" className="form-label small mb-1">
                              <i className="fas fa-cheese me-1 text-success"></i> Fat
                              {selectedIngredient && !selectedIngredient.id.includes("user") && (
                                <FontAwesomeIcon icon={faLock} className="ms-2 text-muted" />
                              )}
                            </label>
                            <div className="input-group">
                              <input
                                type="number"
                                className="form-control"
                                id="edit-new-fat"
                                min="0"
                                step="0.1"
                                value={editFat !== null ? editFat : ""}
                                onChange={(e) => handleEditFatChange(parseFloat(e.target.value))}
                                disabled={
                                  !selectedIngredient ||
                                  !selectedIngredient.id.includes("user")
                                }
                              />
                              <span className="input-group-text">g</span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="p-3 bg-light rounded">
                            <label htmlFor="edit-new-fiber" className="form-label small mb-1">
                              <i className="fas fa-seedling me-1 text-info"></i> Fiber
                              {selectedIngredient && !selectedIngredient.id.includes("user") && (
                                <FontAwesomeIcon icon={faLock} className="ms-2 text-muted" />
                              )}
                            </label>
                            <div className="input-group">
                              <input
                                type="number"
                                className="form-control"
                                id="edit-new-fiber"
                                min="0"
                                step="0.1"
                                value={editFiber !== null ? editFiber : ""}
                                onChange={(e) => handleEditFiberChange(parseFloat(e.target.value))}
                                disabled={
                                  !selectedIngredient ||
                                  !selectedIngredient.id.includes("user")
                                }
                              />
                              <span className="input-group-text">g</span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="p-3 bg-light rounded">
                            <label htmlFor="edit-new-sugar" className="form-label small mb-1">
                              <i className="fas fa-candy-cane me-1 text-danger"></i> Sugar
                              {selectedIngredient && !selectedIngredient.id.includes("user") && (
                                <FontAwesomeIcon icon={faLock} className="ms-2 text-muted" />
                              )}
                            </label>
                            <div className="input-group">
                              <input
                                type="number"
                                className="form-control"
                                id="edit-new-sugar"
                                min="0"
                                step="0.1"
                                value={editSugar !== null ? editSugar : ""}
                                onChange={(e) => handleEditSugarChange(parseFloat(e.target.value))}
                                disabled={
                                  !selectedIngredient ||
                                  !selectedIngredient.id.includes("user")
                                }
                              />
                              <span className="input-group-text">g</span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="p-3 bg-light rounded">
                            <label className="form-label small mb-1">
                              <i className="fas fa-fire me-1 text-secondary"></i> Calories
                            </label>
                            <div className="form-control-plaintext">
                              <strong>{calculateCalories(editProtein, editCarbs, editFat).toFixed(1)}</strong> cal
                            </div>
                            <small className="text-muted">Auto-calculated</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Tab>
                  
                  <Tab eventKey="pricing" title="Pricing">
                    <div className="section-header mb-3">
                      <p className="text-muted small mb-0">Update your store pricing to calculate unit cost</p>
                    </div>
                    <div className="price-calculator p-3 bg-light rounded">
                      <h5 className="mb-3">Price Calculator</h5>
                      <div className="row g-3">
                        <div className="col-6">
                          <label htmlFor="edit-store-amount" className="form-label small mb-1">
                            Package Size
                          </label>
                          <div className="input-group">
                            <input
                              type="number"
                              className="form-control"
                              placeholder="e.g., 12"
                              min="0"
                              step="0.01"
                              value={editStoreAmount !== null ? editStoreAmount : ""}
                              onChange={handleEditStoreBaseAmountChange}
                            />
                            <span className="input-group-text">{editUnitOfMeasurement}</span>
                          </div>
                        </div>
                        <div className="col-6">
                          <label htmlFor="edit-store-price" className="form-label small mb-1">
                            Store Price
                          </label>
                          <div className="input-group">
                            <span className="input-group-text">$</span>
                            <input
                              type="number"
                              className="form-control"
                              placeholder="4.99"
                              min="0"
                              step="0.01"
                              value={editPrice !== null ? editPrice : ""}
                              onChange={handleEditStorePriceAmountChange}
                            />
                          </div>
                        </div>
                      </div>
                      {editStoreAmount > 0 && editPrice > 0 && (
                        <div className="mt-3 p-3 bg-white rounded border">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>Price per {editUnitOfMeasurement}:</strong>
                              <div className="text-muted small">Store price ÷ Package size</div>
                            </div>
                            <div className="text-end">
                              <h5 className="mb-0 text-primary">${editCalculatedPrice.toFixed(2)}</h5>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </Tab>
                  
                  <Tab eventKey="conversions" title="Unit Conversions">
                    <div className="section-header mb-3">
                      <p className="text-muted small mb-0">Manage unit conversions for this ingredient</p>
                    </div>
                    <div className="conversion-section">
                      <div className="add-conversion-area p-3 bg-light rounded mb-4">
                        <h5>Add New Conversion</h5>
                        <div className="row align-items-center mb-3">
                          <div className="col-auto">
                            <Form.Switch
                              type="switch"
                              id="custom-switch"
                              label={"Inverse Mode"}
                              checked={inverseConversion}
                              onChange={handleToggleConversion}
                            />
                          </div>
                          <div className="col-auto">
                            <OverlayTrigger
                              delay={{ hide: 150, show: 150 }}
                              overlay={(props) => (
                                <Tooltip {...props}>
                                  Toggle to switch the conversion direction for easier input
                                </Tooltip>
                              )}
                              placement="bottom"
                            >
                              <FontAwesomeIcon icon={faQuestionCircle} />
                            </OverlayTrigger>
                          </div>
                        </div>
                        <div className="row align-items-end">
                          <div className="col-md-4">
                            <label className="form-label">
                              Convert to:
                            </label>
                            <Select
                              options={unitOptions}
                              value={unitOptions.find(
                                (unit) => unit.unit_id === newUnitOfMeasurement
                              )}
                              onChange={(selectedOption) => {
                                if (selectedOption.unit_id.startsWith("D")) {
                                  setNewUnitOfMeasurement(
                                    parseInt(selectedOption.unit_id.substring(1))
                                  );
                                  setNewCustomUnitFlag(0);
                                  setSelectedUnitOption(selectedOption.unit_name);
                                } else {
                                  setNewUnitOfMeasurement(selectedOption.unit_id);
                                  setNewCustomUnitFlag(1);
                                  setSelectedUnitOption(selectedOption.unit_name);
                                }
                              }}
                              getOptionLabel={(option) => option.unit_name}
                              getOptionValue={(option) => option.unit_id}
                              isSearchable={true}
                              placeholder="Select unit..."
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">
                              {inverseConversion ? (
                                <>How many <b>{selectedUnitOption}</b> in 1 <b>{editUnitOfMeasurement}</b>?</>
                              ) : (
                                <>How many <b>{editUnitOfMeasurement}</b> in 1 <b>{selectedUnitOption}</b>?</>
                              )}
                            </label>
                            <input
                              type="number"
                              className="form-control"
                              min="0"
                              step="0.001"
                              placeholder="Enter amount"
                              value={addConversionInput}
                              onChange={(e) =>
                                handleAddConversionInput(parseFloat(e.target.value))
                              }
                            />
                          </div>
                          <div className="col-md-2">
                            <Button variant="primary" onClick={handleAddUnitConversion} className="w-100">
                              Add
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      <h5>Existing Conversions</h5>
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>Unit Conversion</th>
                              <th>Conversion Factor</th>
                              <th className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ingredientConversionList.length === 0 ? (
                              <tr>
                                <td colSpan="3" className="text-center text-muted">
                                  No conversions added yet
                                </td>
                              </tr>
                            ) : (
                              ingredientConversionList.map((conversion) => (
                                <tr key={conversion.id}>
                                  <td>{conversion.unit_name}</td>
                                  <td>{conversion.conversion_factor}</td>
                                  <td className="text-end">
                                    <button
                                      className="btn btn-sm btn-primary me-2"
                                      onClick={() => handleEditConversion(conversion)}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      className="btn btn-sm btn-danger"
                                      onClick={() => handleDeleteConversion(conversion)}
                                    >
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Tab>
                </Tabs>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setSelectedIngredient(null)}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSaveIngredient}>
                  <i className="fas fa-save me-2"></i>
                  Save Changes
                </Button>
              </Modal.Footer>
            </Modal>

            {/* --------------------- */}
            {/* Edit Conversion Modal */}
            {/* --------------------- */}
            <Modal
              show={selectedConversionToEdit !== null}
              onHide={() => setSelectedConversionToEdit(null)}
              centered
              size="lg"
            >
              <Modal.Header closeButton>
                <Modal.Title>Edit Conversion</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <div className="row">
                  <div
                    className="col"
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Form.Switch
                      type="switch"
                      id="custom-switch"
                      label={"Inverse Conversion"}
                      checked={inverseConversion}
                      onChange={handleToggleConversion}
                    />{" "}
                    <OverlayTrigger
                      delay={{ hide: 150, show: 150 }}
                      overlay={(props) => (
                        <Tooltip {...props}>
                          Toggle this to change the conversion statement below
                          to make inputs easier. Eg, swithes "How many
                          Serving(s) in 1x Cup(s)" to "How many Cup(s) in 1x
                          Serving(s)".
                        </Tooltip>
                      )}
                      placement="bottom"
                    >
                      <FontAwesomeIcon
                        className="mx-2"
                        icon={faQuestionCircle}
                      />
                    </OverlayTrigger>{" "}
                  </div>
                </div>
                <div className="row">
                  <div className="mb-3 col">
                    <label htmlFor="add-base-unit" className="form-label">
                      Edit unit to convert to:
                    </label>
                    <Select
                      options={unitOptions}
                      value={{
                        unit_id: editConversionUnitOfMeasurementId,
                        unit_name: editConversionUnitOfMeasurement,
                      }}
                      onChange={(selectedOption) => {
                        if (selectedOption.unit_id.startsWith("D")) {
                          setEditConversionUnitOfMeasurementId(
                            parseInt(selectedOption.unit_id.substring(1))
                          );
                          setEditConversionUnitOfMeasurement(
                            selectedOption.unit_name
                          );
                          setEditConversionCustomUnitFlag(0);
                        } else {
                          setEditConversionUnitOfMeasurementId(
                            selectedOption.unit_id
                          );
                          setEditConversionUnitOfMeasurement(
                            selectedOption.unit_name
                          );
                          setEditConversionCustomUnitFlag(1);
                        }
                      }}
                      getOptionLabel={(option) => option.unit_name}
                      getOptionValue={(option) => option.unit_id}
                      isSearchable={true}
                    />
                  </div>

                  <div className="mb-3 col">
                    {inverseConversion ? (
                      <label
                        htmlFor="add-conversion-input"
                        className="form-label"
                      >
                        {/* Content when inverseConversion is true */}
                        How many <b>{editConversionUnitOfMeasurement}</b> in 1x{" "}
                        <b>{editUnitOfMeasurement}</b>
                      </label>
                    ) : (
                      <label
                        htmlFor="add-conversion-input"
                        className="form-label"
                      >
                        {/* Content when inverseConversion is false */}
                        How many <b>{editUnitOfMeasurement}</b> in 1x{" "}
                        <b>{editConversionUnitOfMeasurement}</b>
                      </label>
                    )}
                    <input
                      type="number"
                      className="form-control"
                      id="add-conversion-input"
                      min="0"
                      value={editConversionFactor}
                      onChange={(e) =>
                        handleEditConversionInput(parseFloat(e.target.value))
                      }
                    />
                  </div>
                </div>

                <div className="mb-3 col-sm-2 d-flex align-items-end">
                  <Button
                    variant="primary"
                    onClick={handleUpdateUnitConversion}
                  >
                    Update
                  </Button>
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => setSelectedConversionToEdit(null)}
                >
                  Cancel
                </Button>
              </Modal.Footer>
            </Modal>
            {/* -------------------- */}
            {/* Edit Private Notes*/}
            {/* -------------------- */}
            <Modal
              show={showPrivateNotesModal}
              onHide={handlePrivateNotesCloseModal}
              size="lg"
            >
              <Modal.Header closeButton>
                <Modal.Title>Edit Private Notes</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Tiptap
                  initialContent={notesContent}
                  onUpdateContent={updateNotesContent}
                  editingStatus={userIsOwner}
                />
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={handlePrivateNotesCloseModal}
                >
                  Cancel
                </Button>
                <Button variant="primary" onClick={handlePrivatelSaveNotes}>
                  Save Changes
                </Button>
              </Modal.Footer>
            </Modal>
            {/* -------------------- */}
            {/* Edit Public Notes*/}
            {/* -------------------- */}
            <Modal
              show={showPublicNotesModal}
              onHide={handlePublicNotesCloseModal}
              size="lg"
            >
              <Modal.Header closeButton>
                <Modal.Title>Edit Public Notes</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Tiptap
                  initialContent={publicNotesContent}
                  onUpdateContent={updatePublicNotesContent}
                  editingStatus={userIsOwner}
                />
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={handlePublicNotesCloseModal}
                >
                  Cancel
                </Button>
                <Button variant="primary" onClick={handlePublicSaveNotes}>
                  Save Changes
                </Button>
              </Modal.Footer>
            </Modal>
            
            {/* -------------------- */}
            {/* Import Ingredients Modal */}
            {/* -------------------- */}
            <Modal
              show={showImportIngredientsModal}
              onHide={handleImportIngredientsClose}
              size="xl"
              className={importStyles.importModal}
            >
              <Modal.Header closeButton>
                <Modal.Title>Import Ingredients</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Alert variant="info" className="py-2">
                  <small>
                    <FontAwesomeIcon icon={faQuestionCircle} className="me-1" />
                    Paste ingredients one per line. Examples: "2 cups flour", "1/2 tsp salt", "3 eggs, beaten"
                  </small>
                </Alert>
                
                <div className="mb-3">
                  <label className="form-label small mb-1">Paste Ingredients:</label>
                  <textarea
                    className="form-control"
                    rows="6"
                    value={importIngredientsText}
                    onChange={(e) => setImportIngredientsText(e.target.value)}
                    placeholder="2 cups all-purpose flour&#10;1 teaspoon baking soda&#10;1/2 teaspoon salt&#10;1 cup butter, softened&#10;3/4 cup sugar"
                  />
                </div>
                
                <button
                  className={`btn btn-primary mb-3 ${importStyles.parseButton}`}
                  onClick={handleParseIngredients}
                  disabled={!importIngredientsText.trim() || isParsing}
                >
                  {isParsing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Parsing...
                    </>
                  ) : (
                    'Parse Ingredients'
                  )}
                </button>
                
                {parsedIngredients.length > 0 && (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">Preview ({parsedIngredients.filter(ing => !ing.isSection && !ing.isTextRow).length} ingredients, {parsedIngredients.filter(ing => ing.isSection).length} sections, {parsedIngredients.filter(ing => ing.isTextRow).length} text rows):</h6>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                          const newSection = {
                            isSection: true,
                            sectionName: 'New Section',
                            original: '[Added Section]',
                            lineIndex: -1,
                            needsReview: false,
                            errors: []
                          };
                          setParsedIngredients([newSection, ...parsedIngredients]);
                        }}
                      >
                        <FontAwesomeIcon icon={faPlus} className="me-1" />
                        Add Section at Top
                      </button>
                    </div>
                    <div className={importStyles.parseResultsContainer}>
                      <table className={`table table-sm mb-0 ${importStyles.parseResultsTable} ${importStyles.compactTable}`}>
                        <thead>
                          <tr>
                            <th style={{ width: '70px' }}>Qty</th>
                            <th style={{ width: '140px' }}>Unit</th>
                            <th style={{ width: '30%' }}>Ingredient</th>
                            <th style={{ width: '140px' }}>Suffix</th>
                            <th style={{ width: '70px' }}>Status</th>
                            <th style={{ width: '20%' }}>Original</th>
                            <th style={{ width: '110px' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parsedIngredients.map((ing, idx) => (
                            <React.Fragment key={idx}>
                              {ing.isSection ? (
                                <tr className={importStyles.sectionHeaderRow} style={{ backgroundColor: '#f8f9fa' }}>
                                  <td colSpan="6" style={{ fontWeight: 'bold', fontSize: '1.1em', padding: '0.75rem 1rem', borderLeft: '4px solid #6c757d' }}>
                                    <div className="d-flex align-items-center justify-content-between">
                                      <div className="d-flex align-items-center flex-grow-1">
                                        <FontAwesomeIcon icon={faHeading} className="me-2 text-muted" />
                                        <input
                                          type="text"
                                          className="form-control form-control-sm fw-bold"
                                          style={{ backgroundColor: 'transparent', border: '1px solid #ced4da', maxWidth: '400px' }}
                                          value={ing.sectionName}
                                          onChange={(e) => {
                                            const updated = [...parsedIngredients];
                                            updated[idx].sectionName = e.target.value;
                                            setParsedIngredients(updated);
                                          }}
                                          placeholder="Section name..."
                                        />
                                      </div>
                                      <span className={`${importStyles.statusBadge}`} style={{ backgroundColor: '#6c757d', color: 'white' }}>
                                        Section
                                      </span>
                                    </div>
                                  </td>
                                  <td style={{ backgroundColor: '#f8f9fa' }}>
                                    <div className="btn-group btn-group-sm">
                                      <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => {
                                          const updated = [...parsedIngredients];
                                          const parsed = parseIngredientLine(ing.sectionName);
                                          if (parsed && !parsed.isSection) {
                                            updated[idx] = {
                                              ...parsed,
                                              lineIndex: ing.lineIndex,
                                              unitId: null,
                                              unitName: parsed.unit,
                                              ingredientId: null,
                                              ingredientFound: false,
                                              availableUnits: [],
                                              needsReview: true,
                                              errors: [`Ingredient "${parsed.name}" not found - please select from dropdown`]
                                            };
                                          } else {
                                            // If parsing fails, convert to a simple ingredient
                                            updated[idx] = {
                                              qty: 1,
                                              unit: "",
                                              name: ing.sectionName,
                                              suffix: "",
                                              original: ing.original,
                                              lineIndex: ing.lineIndex,
                                              unitId: null,
                                              unitName: "",
                                              ingredientId: null,
                                              ingredientFound: false,
                                              availableUnits: [],
                                              needsReview: true,
                                              errors: [`Ingredient "${ing.sectionName}" not found - please select from dropdown`],
                                              parseWarning: null
                                            };
                                          }
                                          setParsedIngredients(updated);
                                        }}
                                        title="Convert to ingredient"
                                      >
                                        <FontAwesomeIcon icon={faUtensils} />
                                      </button>
                                      <button
                                        className="btn btn-outline-primary"
                                        onClick={() => {
                                          const newSection = {
                                            isSection: true,
                                            sectionName: 'New Section',
                                            original: '[Added Section]',
                                            lineIndex: -1,
                                            needsReview: false,
                                            errors: []
                                          };
                                          const updated = [...parsedIngredients];
                                          updated.splice(idx + 1, 0, newSection);
                                          setParsedIngredients(updated);
                                        }}
                                        title="Add section below"
                                      >
                                        <FontAwesomeIcon icon={faPlus} />
                                      </button>
                                      <button
                                        className="btn btn-outline-danger"
                                        onClick={() => {
                                          const updated = parsedIngredients.filter((_, i) => i !== idx);
                                          setParsedIngredients(updated);
                                        }}
                                        title="Delete section"
                                      >
                                        <FontAwesomeIcon icon={faTrash} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ) : ing.isTextRow ? (
                                <tr className={importStyles.textRow} style={{ backgroundColor: '#f0f8ff' }}>
                                  <td colSpan="6" style={{ padding: '0.5rem 1rem' }}>
                                    <div className="d-flex align-items-center justify-content-between">
                                      <div className="d-flex align-items-center flex-grow-1">
                                        <FontAwesomeIcon icon={faFileText} className="me-2 text-info" />
                                        <input
                                          type="text"
                                          className="form-control form-control-sm"
                                          value={ing.textContent}
                                          onChange={(e) => {
                                            const updated = [...parsedIngredients];
                                            updated[idx] = { ...updated[idx], textContent: e.target.value };
                                            setParsedIngredients(updated);
                                          }}
                                          placeholder="Enter text content..."
                                          style={{ background: 'transparent', border: '1px solid #ced4da' }}
                                        />
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <div className="btn-group btn-group-sm">
                                      <button
                                        className="btn btn-outline-danger"
                                        onClick={() => {
                                          const updated = parsedIngredients.filter((_, i) => i !== idx);
                                          setParsedIngredients(updated);
                                        }}
                                        title="Delete text row"
                                      >
                                        <FontAwesomeIcon icon={faTrash} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                <tr className={importStyles.ingredientRow}>
                                  <td>
                                    <input
                                      type="number"
                                      className={`form-control form-control-sm ${ing.errors && ing.errors.some(e => e.includes('quantity')) ? importStyles.inputError : ''}`}
                                      value={ing.qty}
                                      onChange={(e) => handleParsedIngredientUpdate(idx, 'qty', e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                                      min="0"
                                      step="0.1"
                                    />
                                  </td>
                                  <td>
                                    <div className={ing.unit && !ing.unitId ? importStyles.selectError : ''}>
                                      <Select
                                        options={ing.availableUnits || []}
                                        value={ing.availableUnits && ing.unitId ? ing.availableUnits.find(u => u.unit_id === ing.unitId) : null}
                                        onChange={(selectedOption) => 
                                          handleParsedIngredientUpdate(idx, 'unitId', selectedOption ? selectedOption.unit_id : null)
                                        }
                                        getOptionLabel={(option) => option.unit_name}
                                        getOptionValue={(option) => option.unit_id}
                                        isClearable
                                        isSearchable
                                        menuPlacement="auto"
                                        menuPosition="fixed"
                                        placeholder={
                                          !ing.ingredientFound ? "Select ingredient first" :
                                          ing.availableUnits === undefined ? "Loading units..." :
                                          ing.availableUnits.length > 0 ? "Select unit..." :
                                          "No units configured"
                                        }
                                        isDisabled={!ing.ingredientFound || ing.availableUnits === undefined || ing.availableUnits.length === 0}
                                        menuPortalTarget={null}
                                        styles={{
                                          menu: (base) => ({ ...base, zIndex: 9999 })
                                        }}
                                      />
                                    </div>
                                    {ing.unit && !ing.unitId && ing.availableUnits !== undefined && (
                                      <small className={importStyles.inlineError}>
                                        {ing.availableUnits.length > 0 
                                          ? `Unit "${ing.unit}" not available`
                                          : "No units configured"
                                        }
                                      </small>
                                    )}
                                  </td>
                                  <td>
                                    <div className={!ing.ingredientFound ? importStyles.selectError : ''}>
                                      <Select
                                        options={ingredients}
                                        value={ingredients.find(i => i.value === ing.ingredientId) || null}
                                        onChange={(selectedOption) => 
                                          handleParsedIngredientUpdate(idx, 'ingredientId', selectedOption ? selectedOption.value : null)
                                        }
                                        isSearchable
                                        placeholder="Select ingredient..."
                                        menuPlacement="auto"
                                        menuPosition="fixed"
                                        menuPortalTarget={null}
                                        styles={{
                                          menu: (base) => ({ ...base, zIndex: 9999 })
                                        }}
                                      />
                                    </div>
                                    {!ing.ingredientFound && (
                                      <small className={importStyles.inlineError}>
                                        Not found: "{ing.name}"
                                      </small>
                                    )}
                                  </td>
                                  <td>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={ing.suffix}
                                      onChange={(e) => handleParsedIngredientUpdate(idx, 'suffix', e.target.value)}
                                      placeholder="optional"
                                    />
                                  </td>
                                  <td className={importStyles.statusCell}>
                                    <span className={`${importStyles.statusBadge} ${ing.ingredientFound ? importStyles.success : importStyles.error}`}>
                                      {ing.ingredientFound ? '✓' : '✗'}
                                    </span>
                                  </td>
                                  <td>
                                    <span className={importStyles.originalText} title={ing.original}>
                                      {ing.original}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="btn-group btn-group-sm">
                                      <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => {
                                          const updated = [...parsedIngredients];
                                          updated[idx] = {
                                            isSection: true,
                                            sectionName: ing.name || 'New Section',
                                            original: ing.original,
                                            lineIndex: ing.lineIndex,
                                            needsReview: false,
                                            errors: []
                                          };
                                          setParsedIngredients(updated);
                                        }}
                                        title="Convert to section"
                                      >
                                        <FontAwesomeIcon icon={faHeading} />
                                      </button>
                                      <button
                                        className="btn btn-outline-primary"
                                        onClick={() => {
                                          const newSection = {
                                            isSection: true,
                                            sectionName: 'New Section',
                                            original: '[Added Section]',
                                            lineIndex: -1,
                                            needsReview: false,
                                            errors: []
                                          };
                                          const updated = [...parsedIngredients];
                                          updated.splice(idx + 1, 0, newSection);
                                          setParsedIngredients(updated);
                                        }}
                                        title="Add section below"
                                      >
                                        <FontAwesomeIcon icon={faPlus} />
                                      </button>
                                      <button
                                        className="btn btn-outline-danger"
                                        onClick={() => {
                                          const updated = parsedIngredients.filter((_, i) => i !== idx);
                                          setParsedIngredients(updated);
                                        }}
                                        title="Delete ingredient"
                                      >
                                        <FontAwesomeIcon icon={faTrash} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className={importStyles.legend}>
                      <div className={importStyles.legendItem}>
                        <span className={`${importStyles.statusBadge} ${importStyles.success}`}>✓</span>
                        <span>Ready to import</span>
                      </div>
                      <div className={importStyles.legendItem}>
                        <span className={`${importStyles.statusBadge} ${importStyles.error}`}>✗</span>
                        <span>Needs attention</span>
                      </div>
                      <div className={importStyles.legendItem}>
                        <span className={`${importStyles.statusBadge}`} style={{ backgroundColor: '#6c757d', color: 'white' }}>Section</span>
                        <span>Recipe section header</span>
                      </div>
                    </div>
                    <div className={`${importStyles.legend} mt-2`}>
                      <div className={importStyles.legendItem}>
                        <FontAwesomeIcon icon={faHeading} className="text-muted me-1" />
                        <span>Convert to section</span>
                      </div>
                      <div className={importStyles.legendItem}>
                        <FontAwesomeIcon icon={faUtensils} className="text-muted me-1" />
                        <span>Convert to ingredient</span>
                      </div>
                      <div className={importStyles.legendItem}>
                        <FontAwesomeIcon icon={faTrash} className="text-danger me-1" />
                        <span>Delete item</span>
                      </div>
                      <div className={importStyles.legendItem}>
                        <FontAwesomeIcon icon={faPlus} className="text-primary me-1" />
                        <span>Add section below</span>
                      </div>
                    </div>
                    
                    {skippedLines.length > 0 && (
                      <div className="mt-3">
                        <Alert variant="info" className="py-2">
                          <div className="d-flex align-items-center mb-2">
                            <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
                            <strong>Skipped {skippedLines.length} line{skippedLines.length !== 1 ? 's' : ''} (detected as header{skippedLines.length !== 1 ? 's' : ''} or non-ingredient text)</strong>
                          </div>
                          <details>
                            <summary style={{ cursor: 'pointer', userSelect: 'none' }}>Click to view skipped lines</summary>
                            <ul className="mb-0 mt-2" style={{ fontSize: '0.9em' }}>
                              {skippedLines.map((skip, idx) => (
                                <li key={idx}>Line {skip.lineIndex + 1}: "{skip.text}"</li>
                              ))}
                            </ul>
                          </details>
                        </Alert>
                      </div>
                    )}
                    
                    {parsedIngredients.filter(ing => !ing.isSection && (!ing.ingredientFound || (ing.unit && !ing.unitId && ing.availableUnits !== undefined))).length > 0 && (
                      <Alert variant="danger" className="mt-3 mb-0">
                        <div className="d-flex align-items-center">
                          <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
                          <div>
                            <strong>{parsedIngredients.filter(ing => !ing.isSection && (!ing.ingredientFound || (ing.unit && !ing.unitId && ing.availableUnits !== undefined))).length} ingredient{parsedIngredients.filter(ing => !ing.isSection && (!ing.ingredientFound || (ing.unit && !ing.unitId && ing.availableUnits !== undefined))).length !== 1 ? 's need' : ' needs'} attention</strong>
                            {' - '}
                            {parsedIngredients.filter(ing => !ing.isSection && !ing.ingredientFound).length > 0 && (
                              <span>{parsedIngredients.filter(ing => !ing.isSection && !ing.ingredientFound).length} missing ingredient{parsedIngredients.filter(ing => !ing.isSection && !ing.ingredientFound).length !== 1 ? 's' : ''}</span>
                            )}
                            {parsedIngredients.filter(ing => !ing.isSection && !ing.ingredientFound).length > 0 && parsedIngredients.filter(ing => !ing.isSection && ing.unit && !ing.unitId && ing.availableUnits !== undefined).length > 0 && ', '}
                            {parsedIngredients.filter(ing => !ing.isSection && ing.unit && !ing.unitId && ing.availableUnits !== undefined).length > 0 && (
                              <span>{parsedIngredients.filter(ing => !ing.isSection && ing.unit && !ing.unitId && ing.availableUnits !== undefined).length} unknown unit{parsedIngredients.filter(ing => !ing.isSection && ing.unit && !ing.unitId && ing.availableUnits !== undefined).length !== 1 ? 's' : ''}</span>
                            )}
                          </div>
                        </div>
                      </Alert>
                    )}
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <div className="d-flex justify-content-between align-items-center w-100">
                  <div className="text-muted small">
                    {parsedIngredients.length > 0 && (
                      <>
                        <strong>{parsedIngredients.filter(ing => !ing.isSection && ing.ingredientFound).length}</strong> of{' '}
                        <strong>{parsedIngredients.filter(ing => !ing.isSection).length}</strong> ingredients ready to import
                        {parsedIngredients.filter(ing => ing.isSection).length > 0 && (
                          <span className="text-success ms-2">
                            (+ {parsedIngredients.filter(ing => ing.isSection).length} section{parsedIngredients.filter(ing => ing.isSection).length !== 1 ? 's' : ''})
                          </span>
                        )}
                        {parsedIngredients.filter(ing => !ing.isSection && !ing.ingredientFound).length > 0 && (
                          <span className="text-danger ms-2">
                            ({parsedIngredients.filter(ing => !ing.isSection && !ing.ingredientFound).length} will be skipped)
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <div>
                    <Button variant="secondary" onClick={handleImportIngredientsClose} className="me-2">
                      Cancel
                    </Button>
                    <Button 
                      variant="primary" 
                      onClick={handleImportIngredientsSave}
                      disabled={(parsedIngredients.filter(ing => !ing.isSection && ing.ingredientFound).length === 0 && parsedIngredients.filter(ing => ing.isSection).length === 0) || isImporting}
                    >
                      {isImporting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Importing...
                        </>
                      ) : (
                        (() => {
                          const ingredientCount = parsedIngredients.filter(ing => !ing.isSection && ing.ingredientFound).length;
                          const sectionCount = parsedIngredients.filter(ing => ing.isSection).length;
                          const items = [];
                          
                          if (ingredientCount > 0) {
                            items.push(`${ingredientCount} Ingredient${ingredientCount !== 1 ? 's' : ''}`);
                          }
                          if (sectionCount > 0) {
                            items.push(`${sectionCount} Section${sectionCount !== 1 ? 's' : ''}`);
                          }
                          
                          return `Import ${items.join(' & ')}`;
                        })()
                      )}
                    </Button>
                  </div>
                </div>
              </Modal.Footer>
            </Modal>
            
            {/* -------------------- */}
            {/* Import Instructions Modal */}
            {/* -------------------- */}
            <Modal
              show={showImportInstructionsModal}
              onHide={handleImportInstructionsClose}
              size="xl"
              className={importStyles.importModal}
            >
              <Modal.Header closeButton>
                <Modal.Title>Import Instructions</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Alert variant="info">
                  <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
                  Paste cooking instructions from any recipe. They will be automatically split into steps.
                  <br />
                  <small className="text-muted">
                    Numbered steps (1., 2., etc.) will be detected automatically, or each line/paragraph will become a step.
                  </small>
                </Alert>
                
                <div className="mb-3">
                  <label className="form-label">Paste Instructions Here:</label>
                  <textarea
                    className="form-control"
                    rows="10"
                    value={importInstructionsText}
                    onChange={(e) => setImportInstructionsText(e.target.value)}
                    placeholder="1. Preheat oven to 350°F.&#10;2. Mix dry ingredients in a bowl.&#10;3. In another bowl, cream butter and sugar..."
                  />
                </div>
                
                <button
                  className="btn btn-primary mb-3"
                  onClick={parseInstructions}
                  disabled={!importInstructionsText.trim() || isParsingInstructions}
                >
                  {isParsingInstructions ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Parsing...
                    </>
                  ) : (
                    'Parse Instructions'
                  )}
                </button>
                
                {parsedInstructions.length > 0 && (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0">Preview ({parsedInstructions.length} steps):</h6>
                      {JSON.stringify(parsedInstructions) !== JSON.stringify(originalParsedInstructions) && (
                        <button
                          className="btn btn-sm btn-outline-warning"
                          onClick={() => setParsedInstructions([...originalParsedInstructions])}
                        >
                          Reset to Original
                        </button>
                      )}
                    </div>
                    <Alert variant="info" className="py-2 mb-2">
                      <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
                      <small>
                        <strong>Tips:</strong> Edit text directly in the boxes • Click "Merge" to combine with next step • 
                        To split a step: Type "[SPLIT]" where you want to break it, then click "Split"
                      </small>
                    </Alert>
                    <div className="border rounded p-2" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {parsedInstructions.map((instruction, idx) => (
                        <div key={idx} className="mb-3 p-2 border-bottom">
                          <div className="d-flex align-items-start mb-2">
                            <span className="fw-bold me-2" style={{ minWidth: '30px' }}>{idx + 1}.</span>
                            <div className="flex-grow-1">
                              <textarea
                                className="form-control form-control-sm"
                                value={instruction}
                                onChange={(e) => {
                                  const updated = [...parsedInstructions];
                                  updated[idx] = e.target.value;
                                  setParsedInstructions(updated);
                                }}
                                rows={Math.ceil(instruction.length / 80) || 2}
                                style={{ resize: 'vertical' }}
                              />
                            </div>
                            <div className="ms-2 d-flex flex-column gap-1">
                              {instruction.includes('[SPLIT]') && (
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => {
                                    const updated = [...parsedInstructions];
                                    const parts = instruction.split('[SPLIT]');
                                    if (parts.length >= 2) {
                                      // Replace current instruction with first part
                                      updated[idx] = parts[0].trim();
                                      // Insert remaining parts after current instruction
                                      for (let i = 1; i < parts.length; i++) {
                                        const newPart = parts[i].trim();
                                        if (newPart) {
                                          updated.splice(idx + i, 0, newPart);
                                        }
                                      }
                                      setParsedInstructions(updated);
                                    }
                                  }}
                                  title="Split at [SPLIT] markers"
                                >
                                  Split
                                </button>
                              )}
                              {idx < parsedInstructions.length - 1 && (
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => {
                                    const updated = [...parsedInstructions];
                                    // Merge with next instruction
                                    updated[idx] = updated[idx] + ' ' + updated[idx + 1];
                                    updated.splice(idx + 1, 1);
                                    setParsedInstructions(updated);
                                  }}
                                  title="Merge with next step"
                                >
                                  <FontAwesomeIcon icon={faArrowDown} /> Merge
                                </button>
                              )}
                              {parsedInstructions.length > 1 && (
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => {
                                    const updated = parsedInstructions.filter((_, i) => i !== idx);
                                    setParsedInstructions(updated);
                                  }}
                                  title="Remove this step"
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="text-center mt-3">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => {
                            const updated = [...parsedInstructions, ''];
                            setParsedInstructions(updated);
                          }}
                        >
                          <FontAwesomeIcon icon={faPlus} /> Add New Step
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleImportInstructionsClose}>
                  Cancel
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleImportInstructionsSave}
                  disabled={parsedInstructions.length === 0 || isImportingInstructions}
                >
                  {isImportingInstructions ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Importing...
                    </>
                  ) : (
                    `Import ${parsedInstructions.length} Steps`
                  )}
                </Button>
              </Modal.Footer>
            </Modal>
            
            {/* -------------------- */}
            {/* Ingredient Section Header Modal */}
            {/* -------------------- */}
            <Modal
              show={showIngredientSectionModal}
              onHide={() => {
                setShowIngredientSectionModal(false);
                setNewSectionName("");
              }}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Add Ingredient Section Header</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form>
                  <Form.Group>
                    <Form.Label>Section Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={newSectionName}
                      onChange={(e) => setNewSectionName(e.target.value)}
                      placeholder="e.g., For the Cake, Icing, Glaze, etc."
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddIngredientSectionHeader();
                        }
                      }}
                    />
                    <Form.Text className="text-muted">
                      This section header will appear as a visual divider in your ingredient list.
                    </Form.Text>
                  </Form.Group>
                </Form>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowIngredientSectionModal(false);
                    setNewSectionName("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleAddIngredientSectionHeader}
                  disabled={!newSectionName.trim()}
                >
                  Add Section
                </Button>
              </Modal.Footer>
            </Modal>

            {/* -------------------- */}
            {/* Instruction Section Header Modal */}
            {/* -------------------- */}
            <Modal
              show={showInstructionSectionModal}
              onHide={() => {
                setShowInstructionSectionModal(false);
                setNewSectionName("");
              }}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Add Instruction Section Header</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form>
                  <Form.Group>
                    <Form.Label>Section Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={newSectionName}
                      onChange={(e) => setNewSectionName(e.target.value)}
                      placeholder="e.g., Preparation, Making the Cake, Assembly, etc."
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddInstructionSectionHeader();
                        }
                      }}
                    />
                    <Form.Text className="text-muted">
                      This section header will appear as a visual divider in your instruction list.
                    </Form.Text>
                  </Form.Group>
                </Form>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowInstructionSectionModal(false);
                    setNewSectionName("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleAddInstructionSectionHeader}
                  disabled={!newSectionName.trim()}
                >
                  Add Section
                </Button>
              </Modal.Footer>
            </Modal>

            {/* -------------------- */}
            {/* Text Row Modal */}
            {/* -------------------- */}
            <Modal
              show={showTextRowModal}
              onHide={() => {
                setShowTextRowModal(false);
                setNewTextRowContent("");
              }}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Add Text Row</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form>
                  <Form.Group>
                    <Form.Label>Text Content</Form.Label>
                    <Form.Control
                      type="text"
                      value={newTextRowContent}
                      onChange={(e) => setNewTextRowContent(e.target.value)}
                      placeholder="e.g., 3 TBS of Special Sauce (from above)"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTextRow();
                        }
                      }}
                    />
                    <Form.Text className="text-muted">
                      Text rows are numbered with ingredients but contain only text. Use them to reference components made earlier in the recipe.
                    </Form.Text>
                  </Form.Group>
                </Form>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowTextRowModal(false);
                    setNewTextRowContent("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleAddTextRow}
                  disabled={!newTextRowContent.trim()}
                >
                  Add Text Row
                </Button>
              </Modal.Footer>
            </Modal>
    </>
  );
}

export default EditRecipe;
